import { createApi, retry } from '@reduxjs/toolkit/query/react'
import type stripe from '@stripe/stripe-js'
import { add, differenceInSeconds, format, parseISO } from 'date-fns'
import type { PlacecardConfig } from '@/components/Placecards/types'
import config from '@/config'
import type { ServiceCursorPaginatedResponseType } from '@/connect-types/backend/service'
import { type CreateManualBookingPayload } from '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/upsert_booking_form/zod'
import { type CreateManualPaymentPayload } from '@/pages/[org_id]/venues/[serial]/bookings/day-planner/side-nav/manual_payment_form/zod'
import type { Stat } from '@/pages/[org_id]/venues/[serial]/bookings/reporting/stats/types'
import { buildUrl } from '@/utils/common'
import { type OrganisationUsersAccess } from '../auth/auth.types'
import { axiosBaseQuery } from '../helpers'
import {
  type ActivityLog,
  type ActivityLogs,
  type Area,
  type AutomationConfig,
  type Availability,
  type AvailabilityConfig,
  type BDAScorePayload,
  type Booking,
  type WaitlistEntry,
  type WaitlistEtaSuggestion,
  type WaitlistReport,
  type WaitlistSeatOptions,
  type ChatThread,
  type ChatMessage,
  type BookingAndDuration,
  type BookingAutomationStat,
  type BookingType,
  type BookingDayStats,
  type CloseOut,
  type CollisionCheck,
  type Combination,
  type CoverLimitDaysType,
  type CoversFlow,
  type CreateModifier,
  type DailyAiReport,
  type DailyNote,
  type DailyStatsNew,
  type Enquiry,
  type EnquiryStatus,
  type EnquirySubStatus,
  type EnquiryType,
  type OAuthUserType,
  type EnquiriesResponse,
  type EnquiryCountsResponse,
  type EnquiryFilters,
  type EnquiryReportsResponse,
  type HowAreWeDoingResponse,
  type FloorPlan,
  type GenerateMoreCombination,
  type GoogleConfig,
  type Internal_Booking_Note,
  type InvitationStatus,
  type Item,
  type ItemCreation,
  type ManualPayment,
  type Menu,
  type MenuCategoryUpsert,
  type MenuImportCommitPayload,
  type MenuImportCommitResponse,
  type MenuImportCreateResponse,
  type MenuImportJob,
  type MenuImportSourceType,
  type MenuItem,
  type MenuItemUpsert,
  type MenuTable,
  type Modifier,
  type ModifierCategory,
  type ModifierCategoryCreate,
  type MorpheusVenueWithinABookingContext,
  type OauthUserConfig,
  type ReportingResponse,
  type SpecialDate,
  type SwapManual,
  type Table,
  type TableAssignment,
  type TimeFrame,
  type TimeSlotCapacity,
  type UserRanking,
  type ZionConfigWithBranding,
  type ZionDynamicConfig,
  type ZionEvePromptReply,
  type ZionVenueConfig,
  type OptimisationReplayJob,
  type AutoResourcePayload,
  type AISettings,
  type AllergenSelection,
  type DietarySelection,
  type SpecialOccasionSelection,
  type ConfigOptionItem,
  type PaginatedResponse,
  type Report,
  type ReportsResponse,
  type CreateReportRequest,
  type ComparisonsResponse,
  type VenuesResponse,
  type BookingUpgrade,
  type CreateBookingUpgradePayload,
  type UpdateBookingUpgradePayload,
  type BookingUpgradesResponse,
  type BookingUpgradeParams,
  type SquareCatalogItem,
  type SquareCatalogItemsResponse,
  type SquareCatalogItemsParams,
  type Deposit,
  type DepositsResponse,
  type DepositStatus,
  type CreateRefundPayload,
  type CreateRefundResponse,
  type RefundsResponse,
  type RefundState,
  type RefundTargetType,
  type ChargesQuery,
  type ChargesResponse,
  type Invoice,
  type CreateInvoicePayload,
  type SquareInvoicingCapability,
  type BookingReportResponse,
  type BookingReportStatus,
  type BookingsOverviewResponse,
  type BookingArea,
  type BookingTable,
  type CampaignBookingReportResponse,
  type CampaignBookingSummaryResponse,
  type NeoReportingResponse,
  type NeoVenueOrderEntry,
  type CreateWalletBrandingPayload,
  type UpdateWalletBrandingPayload,
  type WalletBrandingListResponse,
  type WalletBrandingResponse,
  type WalletPassListResponse,
  type WalletPassProvider,
  type WalletPassStatsResponse,
  type PricePreviewBody,
  type PricePreviewResponse,
  type PriceOverrideBody,
  type PriceOverrideResponse,
  type MoveSegmentBody,
  type MoveSegmentPreview,
  type NightSaveAdditions,
  type NightStatusWarning,
  type SegmentStatusAction,
  type SegmentView,
  type StaffAddOnOffer,
  type PriceBasis,
  type BookingPaymentMode,
  type PartyTier,
  type ActivitiesReport,
  type StaffPackageNight,
} from './types'
import type {
  EnquiryQuestion,
  EnquiryQuestionInput,
  EnquirySubjects,
} from './types'
import type {
  BookingEvent,
  EventsListResponse,
  CreateEventPayload,
  UpdateEventPayload,
} from './types/events'
import type {
  CancelTicketsPayload,
  CancelTicketsResult,
  CancellationQuote,
  CreateTierResult,
  DeleteTierResult,
  DoorList,
  ScanResult,
  TicketOrderDetail,
  TicketOrderStatus,
  TicketOrdersResponse,
  TicketTier,
  TicketTierPayload,
} from './types/tickets'
import {
  type CollisionCheckSchemaPayload,
  type TimeSlotCapacityPayload,
  type UpdateOauthUserConfigPayload,
} from './validation'
import type {
  EnquiryOrder,
  EnquiryOrdersResponse,
  EnquiryOrderResponse,
  CreateEnquiryOrderRequest,
  UpdateEnquiryOrderRequest,
  EnquiryOrderParticipant,
  EnquiryOrderParticipantsResponse,
  EnquiryOrderParticipantResponse,
  CreateEnquiryOrderParticipantRequest,
  UpdateEnquiryOrderParticipantRequest,
  EnquiryOrderItem,
  EnquiryOrderItemsResponse,
  EnquiryOrderItemResponse,
  CreateEnquiryOrderItemRequest,
  UpdateEnquiryOrderItemRequest,
} from './enquiry-orders.types'
import type {
  AssignOwnerBody,
  Board,
  BoardColumnPage,
  EnquiryCalendar,
  CreateTaskBody,
  DismissTaskBody,
  EnquiryFile,
  EnquiryLostReason,
  EnquiryLostReasonInput,
  EnquiryStage,
  EnquiryStageInput,
  MarkLostBody,
  MoveToStageBody,
  ReassignTaskBody,
  ReopenBody,
  SetDetailsBody,
  SetSummaryBody,
  WorkflowRunRow,
  EnquiryNoteRow,
  CreateNoteBody,
  UpdateNoteBody,
  RunSheetRow,
  RunSheetBody,
  MoneyLedger,
  MoneyBucket,
  BatchResult,
  BatchEnquiryVerb,
  WaiveReplyBody,
  SnoozeTaskBody,
  SubscribeBody,
  Task,
  TaskStatus,
  TaskVerbResult,
  UnsubscribeBody,
  UpdateTaskBody,
  WorkQueue,
  WorkspaceNotification,
  EnquiryTag,
  EnquiryTagRow,
  EnquiryTagInput,
  EnquiryPriority,
  EnquiryView,
  EnquiryViewInput,
  EnquiryReport,
} from './enquiry-workspace.types'

// Define possible enquiry status values for type safety

/** A booking or enquiry row returned by the customer timeline endpoint. */
export interface VoiceBookingOrder {
  neo_order_id: string
  /** integer pence, gross */
  amount_pence: number
  state: string | null
  closed_at: string | null
}

export interface VoiceBookingRow {
  id: string
  start_time: string
  party_size: number
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  status: string | null
  spend_pence: number
  spend_source: 'square' | 'estimate'
  secured_pence: number
  orders: VoiceBookingOrder[]
}

export interface VoiceFigures {
  bookings: number
  covers: number
  /** bookings the agent took that have a Square order attached */
  bookings_with_orders: number
  /** integer pence: gross order total across those bookings, Square only */
  spend_pence: number
  /** integer pence: covers x a flat per-head figure, where there is no till */
  estimated_spend_pence: number
  /** integer pence: deposits and cancellation charges actually paid */
  secured_pence: number
}

export interface CustomerTimelineItem {
  id: string
  reference: string | null
  start_time: string
  status: string
  party_size: number
  serial: string
}

/** One wallet platform's history for a guest (zion customers/wallet). */
export interface CustomerWalletPlatform {
  added: boolean
  passes: number
  first_added_at: string | null
  last_added_at: string | null
  /** At least one of their cards is still in the wallet. */
  still_added: boolean
}

/** Has a guest ever added one of the org's wallet cards, and on which
 *  platform. Covers booking and ticket passes; issued-but-never-added passes
 *  don't count. */
export interface CustomerWalletSummary {
  has_added: boolean
  latest_provider: 'apple' | 'google' | null
  apple: CustomerWalletPlatform
  google: CustomerWalletPlatform
}

/**
 * Place cards (PLACECARDS_ARD.md §8). These mirror the interfaces the zion
 * controller exports (`routes/org_id/placecards/pre-orders/controller.ts`), with
 * `start_time` as a string because JSON has no Date.
 */

/**
 * `CANCELLED` is deliberately absent: the controller excludes it from every
 * status group, including "all", so it can never arrive here.
 */
export type PlacecardOrderStatus =
  | 'DRAFT'
  | 'FINALIZING'
  | 'CHECKOUT'
  | 'CONFIRMED'

export type PlacecardStatusFilter = 'all' | 'complete' | 'incomplete'

export interface PlacecardChoice {
  /**
   * Null when the menu item behind the line has gone. Kept in the list rather
   * than dropped so the dish count stays honest.
   */
  name: string | null
  modifiers: string[]
}

export interface PlacecardParticipant {
  id: string
  /** Null is a blank card, a valid output rather than a row to filter out. */
  name: string | null
  /** False for a name-only participant: no profile, no email, no invite. */
  linked: boolean
  /** Raw enum values, so the sheet can still filter and colour by them. */
  requirements: string[]
  choices: PlacecardChoice[]
}

export interface PlacecardOrder {
  order_id: string
  /** The container's reference, which staff and the guest both quote. */
  reference: string | null
  status: PlacecardOrderStatus
  container: 'booking' | 'enquiry'
  container_id: string
  /** ISO 8601 instant. */
  start_time: string
  /** `party_size`, falling back to the participant count when it is unset (0). */
  covers: number
  /** True when neither a party size nor a participant exists, so no cards can be made. */
  covers_unknown: boolean
  lead_name: string | null
  /** Empty for an enquiry: tables are only assigned once it becomes a booking. */
  tables: string[]
  comment: string | null
  participants: PlacecardParticipant[]
  /**
   * Social bookings (zion ARD A8 3.12): on a night, the activity whose units `tables` lists, every
   * activity staff can choose instead, and why this one. Null and [] on a single booking or an enquiry.
   */
  activity?: PlacecardActivity | null
  activities?: PlacecardActivity[]
  activity_source?: 'pre_order_menu' | 'last' | 'staff' | null
}

/** A night's activity as the place cards offer it. */
export interface PlacecardActivity {
  segment_id: string
  position: number
  name: string
  /** ISO 8601. */
  start_time: string
  tables: string[]
  has_pre_order_menu: boolean
}

export interface PlacecardOrdersResponse {
  orders: PlacecardOrder[]
  /**
   * Counted over the whole filtered range rather than a page, because selection
   * spans the range: this feeds PRD §14's 500-card warning.
   */
  totals: {
    orders: number
    cards: number
  }
}

export interface PlacecardOrderFilters {
  org_id: string
  /** Cards are printed one venue at a time: stock, presets and artwork are per serial. */
  serial: string
  from?: string
  to?: string
  status?: PlacecardStatusFilter
  /** Social bookings (zion ARD A8 3.12): staff's choice of a night's activity, "<order_id>:<segment_id>". */
  activity?: string[]
}

export interface PlacecardPreset {
  id: string
  name: string
  /**
   * Always false from the API: the built-in presets are code constants on the
   * client (ARD §10), never rows, so anything stored is venue-owned.
   */
  system: boolean
  config: PlacecardConfig
  created_at: string
  updated_at: string
}

export interface PlacecardPresetPayload {
  name: string
  config: PlacecardConfig
}

/**
 * The single place a place cards query string is built. The CSV streams a file so
 * it is fetched outside RTK Query, and giving it a second parameter list is
 * exactly how the file and the sheet come to disagree about who is on them.
 */
const placecardSearchParams = (
  filters: PlacecardOrderFilters
): URLSearchParams => {
  const searchParams = new URLSearchParams()
  searchParams.set('serial', filters.serial)
  if (filters.from) searchParams.set('from', filters.from)
  if (filters.to) searchParams.set('to', filters.to)
  if (filters.status) searchParams.set('status', filters.status)
  filters.activity?.forEach((choice) => {
    searchParams.append('activity', choice)
  })
  return searchParams
}

/**
 * Absolute URL for the place cards CSV, which is requested directly (with an
 * Authorization header) rather than through RTK Query because it streams an
 * attachment. Same filters as `getPlacecardOrders`, by construction.
 */
export function placecardExportUrl(
  args: PlacecardOrderFilters & {
    /** Omitted means the whole filtered range, which is what "Select all" sends. */
    order_ids?: string[]
  }
): string {
  const searchParams = placecardSearchParams(args)
  args.order_ids?.forEach((order_id) => {
    searchParams.append('order_id', order_id)
  })
  return `${config.url.zion}/${args.org_id}/placecards/export?${searchParams.toString()}`
}

const service = 'zion'

/**
 * Enquiries workspace: what a verb on an enquiry touches. The file header
 * itself is written by `onQueryStarted` from the verb's response, so it is
 * listed here only for the legacy detail and list readers that still fetch
 * the enquiry the old way.
 */
const enquiryVerbTags = (id: string) =>
  [
    { type: 'enquiry-file', id },
    { type: 'enquiry-board', id: 'LIST' },
    { type: 'work-queue', id: 'LIST' },
    { type: 'work-queue', id: 'COUNTS' },
    { type: 'enquiries', id },
    { type: 'enquiries', id: 'LIST' },
    { type: 'enquiries', id: 'COUNTS' },
    { type: 'enquiry-activities', id },
    { type: 'tasks', id: `ENQUIRY:${id}` },
    { type: 'tasks', id: 'LIST' },
  ] as const

/** A task verb touches the task lists, the today view and, when bound, the file. */
const taskVerbTags = (enquiry_id?: string | null) =>
  [
    { type: 'tasks', id: 'LIST' },
    { type: 'work-queue', id: 'LIST' },
    { type: 'work-queue', id: 'COUNTS' },
    ...(enquiry_id
      ? ([
          { type: 'tasks', id: `ENQUIRY:${enquiry_id}` },
          { type: 'enquiry-file', id: enquiry_id },
          { type: 'enquiry-activities', id: enquiry_id },
          { type: 'enquiry-board', id: 'LIST' },
        ] as const)
      : []),
  ] as const

const zionApi = createApi({
  reducerPath: service,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  keepUnusedDataFor: 60 * 60 * 12,
  tagTypes: [
    'booking-config',
    'special-dates',
    'booking-types',
    'party-tiers',
    'enquiry-questions',
    'enquiry-subjects',
    'events',
    'areas',
    'booking-areas',
    'booking-tables',
    'combinations',
    'availability',
    'bookings',
    'daily-stats',
    'daily-insights',
    'notes',
    'close-outs',
    'tables',
    'cover-limits',
    'activity',
    'open-close',
    'google-config',
    'reporting',
    'note',
    'bda-score',
    'automations',
    'cover-counts',
    'manual-payments',
    'floorplans',
    'floorplan',
    'daily-notes',
    'oauth-config',
    'daily-notes-count',
    'staff-assign-tables',
    'leader_board',
    'menus',
    'menu',
    'items',
    'modifiers',
    'modifier-categories',
    'tags',
    'menu-items',
    'menu-categories',
    'menu-tables',
    'booking-upgrades',
    'daily-ai-report',
    'daily-stats-new',
    'generate-combination',
    'enquiries',
    'BookingDayStats',
    'time-slot-capacities',
    'config-allergens',
    'allergen-options',
    'config-dietary',
    'dietary-options',
    'config-special-occasions',
    'special-occasion-options',
    'square-catalog-items',
    'deposits',
    'refunds',
    'charges',
    'enquiry-invoices',
    'booking-invoices',
    'square-invoicing-capability',
    'booking-reports',
    'neo_reporting',
    'enquiry-orders',
    'enquiry-activities',
    'booking-orders',
    'queue-stats',
    'onboarding-progress',
    'user-onboarding-progress',
    'waitlist',
    'placecard-presets',
    'menu-imports',
    'wallet-branding',
    'ticket-tiers',
    'ticket-orders',
    'ticket-door',
    // Enquiries workspace (zion ENQUIRIES_WORKSPACE_ARD.md D4)
    'enquiry-stages',
    'enquiry-lost-reasons',
    'enquiry-tags',
    'enquiry-views',
    'enquiry-report',
    'enquiry-board',
    'enquiry-file',
    'tasks',
    'work-queue',
    'workspace-notifications',
    'workflow-runs',
    'enquiry-notes',
    'run-sheet',
    'enquiry-money',
  ],

  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: config.url.zion,
      service,
    }),
    {
      maxRetries: 2,
    }
  ),

  endpoints: (build) => ({
    assignEnquiryOwner: build.mutation<
      {
        message: string
        assignment: {
          id: string
          enquiry_id: string
          oauth_user_id: string
          type: 'owner'
          created_at: string
          oauth_user: {
            oauth_id: string
            oauth_email: string
            oauth_user_name: string
          }
        }
      },
      {
        org_id: string
        id: string
        oauth_id: string
        oauth_email?: string
        oauth_user_name?: string
      }
    >({
      query: ({ org_id, id, oauth_id, oauth_email, oauth_user_name }) => ({
        url: `/${org_id}/enquiries/${id}/assign-owner`,
        method: 'PUT',
        data: {
          oauth_id,
          ...(oauth_email && { oauth_email }),
          ...(oauth_user_name && { oauth_user_name }),
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'enquiries', id: arg.id },
        { type: 'enquiries', id: 'LIST' },
      ],
    }),

    updateEnquiry: build.mutation<
      {
        message: string
        enquiry: Enquiry
      },
      {
        org_id: string
        id: string
        first_name?: string
        last_name?: string
        email?: string
        phone?: string
        message?: string
        party_size?: number
        start_time?: string
        type?: string
        booking_type_id?: string | null
        /** Subject event; one of booking_type_id / event_id (ENQUIRIES_CUSTOM_QUESTIONS_ARD.md §3.6). */
        event_id?: string | null
        /** Changed custom answers only; `value: null` clears one. */
        answers?: { question_id?: string | null; id?: string; label?: string; value: string | string[] | boolean | null }[]
        budget?: number | null
        is_flexible_date?: boolean
        allergen_ids?: string[]
        dietary_ids?: string[]
        special_occasion_ids?: string[]
        internal_notes?: string
        serial?: string
        owner_user_id?: string | null
        inbox_thread_id?: string
        subscriber_user_ids?: string[]
      }
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'enquiries', id: arg.id },
        { type: 'enquiries', id: 'LIST' },
        { type: 'enquiries', id: 'COUNTS' },
      ],
    }),

    updateEnquiryStatus: build.mutation<
      {
        message: string
        enquiry: Enquiry
      },
      {
        org_id: string
        id: string
        status:
          | 'pending'
          | 'in_progress'
          | 'tentative'
          | 'completed'
          | 'cancelled'
        sub_status?: 'triage' | 'qualified' | 'negotiation' | 'closed'
        reason_for_cancel?: string
        internal_notes?: string
      }
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/status`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'enquiries', id: arg.id },
        { type: 'enquiries', id: 'LIST' },
        { type: 'enquiries', id: 'COUNTS' },
      ],
    }),

    createEnquiry: build.mutation<
      {
        message: string
        enquiry: Enquiry
      },
      {
        org_id: string
        // Required fields
        first_name: string
        last_name: string
        email: string
        phone?: string
        message: string
        party_size: number
        start_time: string
        type:
          | 'general'
          | 'large_booking'
          | 'private_event'
          | 'corporate'
          | 'special_occasion'
        // Optional fields
        serial?: string
        booking_type_id?: string | null
        /** Subject event; one of booking_type_id / event_id. */
        event_id?: string | null
        /** The venue's custom questions, answered. */
        answers?: { question_id?: string | null; id?: string; label?: string; value: string | string[] | boolean | null }[]
        budget?: number | null
        is_flexible_date?: boolean
        internal_notes?: string
        allergen_ids?: string[]
        dietary_ids?: string[]
        special_occasion_ids?: string[]
        // OAuth users
        owner?: {
          oauth_id: string
          oauth_email: string
          oauth_user_name: string
        }
        subscribers?: {
          oauth_id: string
          oauth_email: string
          oauth_user_name: string
        }[]
      }
    >({
      query: ({ org_id, ...data }) => ({
        url: `/${org_id}/enquiries`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'enquiries', id: 'LIST' }],
    }),

    manageEnquirySubscribers: build.mutation<
      {
        message: string
        assignments?: {
          id: string
          enquiry_id: string
          oauth_user_id: string
          type: 'subscriber'
          created_at: string
          oauth_user: {
            oauth_id: string
            oauth_email: string
            oauth_user_name: string
          }
        }[]
      },
      {
        org_id: string
        id: string
        action: 'add' | 'remove'
        oauth_users?: {
          oauth_id: string
          oauth_email?: string
          oauth_user_name?: string
        }[]
        oauth_ids?: string[]
      }
    >({
      query: ({ org_id, id, action, oauth_users, oauth_ids }) => ({
        url: `/${org_id}/enquiries/${id}/manage-subscribers`,
        method: 'PUT',
        data:
          action === 'add' ? { action, oauth_users } : { action, oauth_ids },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'enquiries', id: arg.id },
        { type: 'enquiries', id: 'LIST' },
      ],
    }),

    getEnquiry: build.query<
      Enquiry,
      {
        org_id: string
        id: string
        /** Accepted for call-site convenience; not used in the request URL. */
        serial?: string
      }
    >({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/enquiries/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error) => [
        { type: 'enquiries', id: result?.id },
        { type: 'enquiries', id: `STATUS:${result?.status}` },
      ],
    }),

    getEnquiryByThread: build.query<
      Enquiry,
      {
        org_id: string
        thread_id: string
      }
    >({
      query: ({ org_id, thread_id }) => ({
        url: `/${org_id}/enquiries/by-thread/${thread_id}`,
        method: 'GET',
      }),
      providesTags: (result) => [{ type: 'enquiries', id: result?.id }],
    }),

    // A guest's bookings + enquiries across the org (by email), for the inbox
    // details panel to surface past/upcoming activity on any thread.
    getCustomerTimelineByEmail: build.query<
      {
        bookings: CustomerTimelineItem[]
        enquiries: CustomerTimelineItem[]
      },
      { org_id: string; email: string }
    >({
      query: ({ org_id, email }) => ({
        url: `/${org_id}/customers/timeline?email=${encodeURIComponent(email)}`,
        method: 'GET',
      }),
    }),

    // Whether a guest has ever added one of the org's wallet cards to their
    // phone, and on which platform, so a profile can tell iPhone from Android
    // without a Wi-Fi session.
    getCustomerWalletByEmail: build.query<
      CustomerWalletSummary,
      { org_id: string; email: string }
    >({
      query: ({ org_id, email }) => ({
        url: `/${org_id}/customers/wallet?email=${encodeURIComponent(email)}`,
        method: 'GET',
      }),
      transformResponse: (res: { data: CustomerWalletSummary }) => res.data,
    }),

    getEnquiryActivities: build.query<
      {
        activities: {
          id: string
          enquiry_id: string
          action_type: string
          description: string
          created_at: string
          updated_at: string
          org_id: string
          serial: string
          metadata?: any
          oauth_user?: {
            oauth_id: string
            oauth_user_name: string
            oauth_email: string
          }
          user_profile?: any
        }[]
        pagination: {
          page: number
          limit: number
          total: number
          totalPages: number
          hasNext: boolean
          hasPrev: boolean
        }
      },
      {
        org_id: string
        id: string
        query?: {
          page?: number
          limit?: number
          action_type?: string
          start_date?: string
          end_date?: string
          oauth_user_id?: string
        }
      }
    >({
      query: ({ org_id, id, query = {} }) => ({
        url: buildUrl(`/${org_id}/enquiries/${id}/activities`, query),
        method: 'GET',
      }),
      providesTags: (result, error, arg) => [
        { type: 'enquiry-activities', id: arg.id },
      ],
    }),

    getEnquiryCounts: build.query<
      EnquiryCountsResponse,
      {
        org_id: string
        serial?: string
        filters?: EnquiryFilters
      }
    >({
      query: ({ org_id, serial, filters = {} }) => {
        // Build query parameters - handle arrays properly
        const params: Record<string, any> = { ...filters }

        // Add serial as a filter if provided
        if (serial) {
          params['serial[]'] = [serial]
        }

        // Convert arrays to proper query string format
        if (filters.serial?.length) {
          params['serial[]'] = filters.serial
          delete params.serial
        }
        if (filters.status?.length) {
          params['status[]'] = filters.status
          delete params.status
        }
        if (filters.sub_status?.length) {
          params['sub_status[]'] = filters.sub_status
          delete params.sub_status
        }
        if (filters.type?.length) {
          params['type[]'] = filters.type
          delete params.type
        }
        if (filters.booking_type_id?.length) {
          params['booking_type_id[]'] = filters.booking_type_id
          delete params.booking_type_id
        }
        if (filters.event_id?.length) {
          params['event_id[]'] = filters.event_id
          delete params.event_id
        }

        return {
          url: buildUrl(`/${org_id}/enquiries/counts`, params),
          method: 'GET',
        }
      },
      providesTags: [{ type: 'enquiries', id: 'COUNTS' }],
    }),

    getEnquiryReports: build.query<
      EnquiryReportsResponse,
      {
        org_id: string
        serial?: string
        filters?: EnquiryFilters
        bucket?: 'day' | 'week' | 'month'
        timezone?: string
      }
    >({
      query: ({ org_id, serial, filters = {}, bucket, timezone }) => {
        const params: Record<string, any> = { ...filters }

        if (serial) {
          params['serial[]'] = [serial]
        }

        if (filters.serial?.length) {
          params['serial[]'] = filters.serial
          delete params.serial
        }
        if (filters.status?.length) {
          params['status[]'] = filters.status
          delete params.status
        }
        if (filters.sub_status?.length) {
          params['sub_status[]'] = filters.sub_status
          delete params.sub_status
        }
        if (filters.type?.length) {
          params['type[]'] = filters.type
          delete params.type
        }
        if (filters.booking_type_id?.length) {
          params['booking_type_id[]'] = filters.booking_type_id
          delete params.booking_type_id
        }
        if (filters.event_id?.length) {
          params['event_id[]'] = filters.event_id
          delete params.event_id
        }

        if (bucket) params.bucket = bucket
        if (timezone) params.timezone = timezone

        return {
          url: buildUrl(`/${org_id}/enquiries/reports`, params),
          method: 'GET',
        }
      },
      providesTags: [{ type: 'enquiries', id: 'REPORTS' }],
    }),

    getEnquiries: build.query<
      EnquiriesResponse,
      {
        org_id: string
        serial?: string
        query?: {
          // Pagination
          page?: number
          limit?: number
          // Venue filter
          serial?: string[]
          // Search
          search?: string
          // Date filters
          start_date_from?: string
          start_date_to?: string
          created_date_from?: string
          created_date_to?: string
          // Status filters
          status?: EnquiryStatus[]
          sub_status?: EnquirySubStatus[]
          type?: EnquiryType[]
          // Relationship filters
          booking_type_id?: string[]
          /** Filter by subject event (ENQUIRIES_CUSTOM_QUESTIONS_ARD.md §3.6). */
          event_id?: string[]
          user_profile_id?: string
          oauth_user_id?: string
          oauth_user_type?: OAuthUserType
          // Boolean filters
          has_booking?: boolean
          has_payment_links?: boolean
          has_orders?: boolean
          is_flexible_date?: boolean
          all_payments_fulfilled?: boolean
          all_orders_fulfilled?: boolean
          // Work queue (zion R29, R30). Only ever send `true`: zion coerces
          // the string 'false' to true like its other boolean query flags.
          needs_reply?: boolean
          include_needs_reply?: boolean
          // Numeric range filters
          budget_min?: number
          budget_max?: number
          party_size_min?: number
          party_size_max?: number
          // Sorting
          sort_by?:
            | 'created_at'
            | 'start_time'
            | 'party_size'
            | 'budget'
            | 'first_name'
            | 'last_name'
            | 'status'
            | 'serial'
            | 'waiting' // longest-waiting guest first (zion R31)
          sort_order?: 'asc' | 'desc'
        }
      }
    >({
      query: ({ org_id, serial, query = { page: 1, limit: 20 } }) => {
        // Build query parameters - handle arrays properly
        const params: Record<string, any> = { ...query }

        // Add serial as a filter if provided (from the top-level parameter)
        if (serial && !query.serial) {
          params['serial[]'] = [serial]
        }

        // Convert arrays to proper query string format
        if (query.serial?.length) {
          params['serial[]'] = query.serial
          delete params.serial
        }
        if (query.status?.length) {
          params['status[]'] = query.status
          delete params.status
        }
        if (query.sub_status?.length) {
          params['sub_status[]'] = query.sub_status
          delete params.sub_status
        }
        if (query.type?.length) {
          params['type[]'] = query.type
          delete params.type
        }
        if (query.booking_type_id?.length) {
          params['booking_type_id[]'] = query.booking_type_id
          delete params.booking_type_id
        }

        return {
          url: buildUrl(`/${org_id}/enquiries`, params),
          method: 'GET',
        }
      },
      providesTags: [{ type: 'enquiries', id: 'LIST' }],
    }),

    // -----------------------------------------------------------------------
    // Enquiries workspace (zion ENQUIRIES_WORKSPACE_ARD.md D4; rules in
    // ENQUIRIES_WORKSPACE_RULES.md). Verbs are POSTs that return the refreshed
    // file header, so each one writes that header straight into the
    // `getEnquiryFile` cache and invalidates the derived reads (board, work
    // queue, legacy list and counts). Refusals arrive as 422 with
    // `{ error, rule }` in the body: see components/EnquiriesWorkspace/refusal.ts.
    // -----------------------------------------------------------------------

    getEnquiryStages: build.query<{ stages: EnquiryStage[] }, { org_id: string }>({
      query: ({ org_id }) => ({ url: `/${org_id}/enquiries/stages`, method: 'GET' }),
      providesTags: [{ type: 'enquiry-stages', id: 'LIST' }],
    }),

    putEnquiryStages: build.mutation<
      { stages: EnquiryStage[] },
      { org_id: string; stages: EnquiryStageInput[] }
    >({
      query: ({ org_id, stages }) => ({
        url: `/${org_id}/enquiries/stages`,
        method: 'PUT',
        data: { stages },
      }),
      invalidatesTags: [
        { type: 'enquiry-stages', id: 'LIST' },
        { type: 'enquiry-board', id: 'LIST' },
      ],
    }),

    getEnquiryLostReasons: build.query<{ reasons: EnquiryLostReason[] }, { org_id: string }>({
      query: ({ org_id }) => ({ url: `/${org_id}/enquiries/lost-reasons`, method: 'GET' }),
      providesTags: [{ type: 'enquiry-lost-reasons', id: 'LIST' }],
    }),

    putEnquiryLostReasons: build.mutation<
      { reasons: EnquiryLostReason[] },
      { org_id: string; reasons: EnquiryLostReasonInput[] }
    >({
      query: ({ org_id, reasons }) => ({
        url: `/${org_id}/enquiries/lost-reasons`,
        method: 'PUT',
        data: { reasons },
      }),
      invalidatesTags: [{ type: 'enquiry-lost-reasons', id: 'LIST' }],
    }),

    // Tags, priority and views (zion D11; rules G1-G12) ---------------------

    getEnquiryTags: build.query<{ tags: EnquiryTagRow[] }, { org_id: string }>({
      query: ({ org_id }) => ({ url: `/${org_id}/enquiries/tags`, method: 'GET' }),
      providesTags: [{ type: 'enquiry-tags', id: 'LIST' }],
    }),
    putEnquiryTags: build.mutation<{ tags: EnquiryTagRow[] }, { org_id: string; tags: EnquiryTagInput[] }>({
      query: ({ org_id, tags }) => ({ url: `/${org_id}/enquiries/tags`, method: 'PUT', data: { tags } }),
      invalidatesTags: [{ type: 'enquiry-tags', id: 'LIST' }, { type: 'enquiry-board', id: 'LIST' }],
    }),
    mergeEnquiryTags: build.mutation<{ moved: number; tags: EnquiryTagRow[] }, { org_id: string; from: string; into: string }>({
      query: ({ org_id, from, into }) => ({ url: `/${org_id}/enquiries/tags/${from}/merge`, method: 'POST', data: { into } }),
      invalidatesTags: [{ type: 'enquiry-tags', id: 'LIST' }, { type: 'enquiry-board', id: 'LIST' }, 'enquiry-file'],
    }),
    tagEnquiry: build.mutation<{ file: EnquiryFile }, { org_id: string; id: string; tag_id?: string; name?: string }>({
      query: ({ org_id, id, ...data }) => ({ url: `/${org_id}/enquiries/${id}/tag`, method: 'POST', data }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => [...enquiryVerbTags(arg.id), { type: 'enquiry-tags', id: 'LIST' }],
    }),
    untagEnquiry: build.mutation<{ file: EnquiryFile }, { org_id: string; id: string; tag_id: string }>({
      query: ({ org_id, id, ...data }) => ({ url: `/${org_id}/enquiries/${id}/untag`, method: 'POST', data }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => [...enquiryVerbTags(arg.id), { type: 'enquiry-tags', id: 'LIST' }],
    }),
    setEnquiryPriority: build.mutation<{ file: EnquiryFile }, { org_id: string; id: string; priority: EnquiryPriority }>({
      query: ({ org_id, id, ...data }) => ({ url: `/${org_id}/enquiries/${id}/set-priority`, method: 'POST', data }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),
    /** What enquiries have brought in (zion RP1-RP8); any filter or view is a report. */
    getEnquiryReport: build.query<
      EnquiryReport,
      { org_id: string; serial?: string[]; days?: number; from?: string; to?: string; axis?: 'enquiry_date' | 'event_date'; filter?: string; view_id?: string }
    >({
      query: ({ org_id, ...params }) => ({ url: buildUrl(`/${org_id}/enquiries/reporting`, params), method: 'GET' }),
      providesTags: [{ type: 'enquiry-report', id: 'LIST' }],
    }),
    getEnquiryViews: build.query<{ views: EnquiryView[] }, { org_id: string }>({
      query: ({ org_id }) => ({ url: `/${org_id}/enquiries/views`, method: 'GET' }),
      providesTags: [{ type: 'enquiry-views', id: 'LIST' }],
    }),
    createEnquiryView: build.mutation<{ view: EnquiryView }, { org_id: string } & EnquiryViewInput>({
      query: ({ org_id, ...data }) => ({ url: `/${org_id}/enquiries/views`, method: 'POST', data }),
      invalidatesTags: [{ type: 'enquiry-views', id: 'LIST' }],
    }),
    updateEnquiryView: build.mutation<{ view: EnquiryView }, { org_id: string; id: string } & Partial<EnquiryViewInput>>({
      query: ({ org_id, id, ...data }) => ({ url: `/${org_id}/enquiries/views/${id}`, method: 'PUT', data }),
      invalidatesTags: [{ type: 'enquiry-views', id: 'LIST' }],
    }),
    deleteEnquiryView: build.mutation<void, { org_id: string; id: string }>({
      query: ({ org_id, id }) => ({ url: `/${org_id}/enquiries/views/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'enquiry-views', id: 'LIST' }],
    }),

    getEnquiryBoard: build.query<
      Board,
      {
        org_id: string
        serial?: string[]
        owner?: string
        q?: string
        from?: string
        to?: string
        limit_per_stage?: number
        /** zion G5: an `EnquiryFilter` as JSON */
        filter?: string
      }
    >({
      query: ({ org_id, ...params }) => ({
        url: buildUrl(`/${org_id}/enquiries/board`, params),
        method: 'GET',
      }),
      providesTags: [{ type: 'enquiry-board', id: 'LIST' }],
    }),

    /** One page of one column, for the board's infinite scroll (zion S16a order). */
    getEnquiryBoardColumn: build.query<
      BoardColumnPage,
      {
        org_id: string
        stage_id: string
        serial?: string[]
        owner?: string
        q?: string
        offset?: number
        limit?: number
        /** zion G5: an `EnquiryFilter` as JSON */
        filter?: string
      }
    >({
      query: ({ org_id, stage_id, ...params }) => ({
        url: buildUrl(`/${org_id}/enquiries/board/${stage_id}/cards`, params),
        method: 'GET',
      }),
      providesTags: [{ type: 'enquiry-board', id: 'LIST' }],
    }),

    /** Enquiries by event date for the month view. */
    getEnquiryCalendar: build.query<
      EnquiryCalendar,
      { org_id: string; serial?: string[]; from: string; to: string; filter?: string }
    >({
      query: ({ org_id, ...params }) => ({
        url: buildUrl(`/${org_id}/enquiries/calendar`, params),
        method: 'GET',
      }),
      providesTags: [{ type: 'enquiry-board', id: 'LIST' }],
    }),

    getEnquiryFile: build.query<{ file: EnquiryFile }, { org_id: string; id: string }>({
      query: ({ org_id, id }) => ({ url: `/${org_id}/enquiries/${id}/file`, method: 'GET' }),
      providesTags: (result, error, arg) => [{ type: 'enquiry-file', id: arg.id }],
    }),

    getEnquiryFileTasks: build.query<
      { tasks: Task[]; counts: { open: number; done: number; dismissed: number } },
      { org_id: string; id: string }
    >({
      query: ({ org_id, id }) => ({ url: `/${org_id}/enquiries/${id}/tasks`, method: 'GET' }),
      providesTags: (result, error, arg) => [{ type: 'tasks', id: `ENQUIRY:${arg.id}` }],
    }),

    // Enquiry verbs --------------------------------------------------------

    moveEnquiryToStage: build.mutation<
      { file: EnquiryFile },
      { org_id: string; id: string } & MoveToStageBody
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/move-to-stage`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // The refusal is surfaced by the caller; nothing to roll back.
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),

    markEnquiryLost: build.mutation<
      { file: EnquiryFile },
      { org_id: string; id: string } & MarkLostBody
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/mark-lost`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),

    reopenEnquiry: build.mutation<
      { file: EnquiryFile },
      { org_id: string; id: string } & ReopenBody
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/reopen`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),

    setEnquiryOwner: build.mutation<
      { file: EnquiryFile },
      { org_id: string; id: string } & AssignOwnerBody
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/assign-owner`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),

    subscribeToEnquiry: build.mutation<
      { file: EnquiryFile },
      { org_id: string; id: string } & SubscribeBody
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/subscribe`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),

    unsubscribeFromEnquiry: build.mutation<
      { file: EnquiryFile },
      { org_id: string; id: string } & UnsubscribeBody
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/unsubscribe`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),

    /** R16a: clear the waiting flag without a reply; refreshes the file, its tasks, the queue and the board. */
    waiveEnquiryReply: build.mutation<{ file: EnquiryFile }, { org_id: string; id: string } & WaiveReplyBody>({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/waive-reply`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => [...enquiryVerbTags(arg.id), { type: 'tasks', id: `ENQUIRY:${arg.id}` }, { type: 'tasks', id: 'LIST' }],
    }),
    // Workflow runs on an enquiry (or a workflow): zion's record, everyone may read.
    getWorkflowRuns: build.query<
      { runs: WorkflowRunRow[] },
      { org_id: string; enquiry_id?: string; workflow_id?: string; status?: string }
    >({
      query: ({ org_id, enquiry_id, workflow_id, status }) => ({
        url: `/${org_id}/enquiries/workflows/runs`,
        method: 'GET',
        params: { ...(enquiry_id ? { enquiry_id } : {}), ...(workflow_id ? { workflow_id } : {}), ...(status ? { status } : {}) },
      }),
      providesTags: (_r, _e, arg) => [{ type: 'workflow-runs', id: arg.enquiry_id ?? arg.workflow_id ?? 'LIST' }, { type: 'workflow-runs', id: 'LIST' }],
    }),
    // One verb over many enquiries (B1-B5); each id its own transaction, reported per id
    batchEnquiries: build.mutation<BatchResult, { org_id: string; verb: BatchEnquiryVerb; ids: string[]; payload?: Record<string, unknown> }>({
      query: ({ org_id, ...body }) => ({ url: `/${org_id}/enquiries/batch`, method: 'POST', data: body }),
      invalidatesTags: (_r, _e, { ids }) => [
        { type: 'enquiry-board', id: 'LIST' },
        { type: 'enquiries', id: 'LIST' },
        { type: 'work-queue', id: 'LIST' },
        { type: 'work-queue', id: 'COUNTS' },
        { type: 'tasks', id: 'LIST' },
        ...ids.flatMap((id) => [{ type: 'enquiry-file' as const, id }, { type: 'enquiry-activities' as const, id }, { type: 'tasks' as const, id: `ENQUIRY:${id}` }]),
      ],
    }),
    // The money ledger (F8)
    getEnquiryMoney: build.query<MoneyLedger, { org_id: string; serial?: string | null; bucket?: MoneyBucket; owner?: string | null; filter?: string }>({
      query: ({ org_id, serial, bucket, owner, filter }) => ({ url: `/${org_id}/enquiries/money`, method: 'GET', params: { ...(serial ? { serial } : {}), ...(bucket ? { bucket } : {}), ...(owner ? { owner } : {}), ...(filter ? { filter } : {}) } }),
      providesTags: [{ type: 'enquiry-money', id: 'LIST' }],
    }),
    // The event's run sheet (rule E11)
    getRunSheet: build.query<{ run_sheet: RunSheetRow | null }, { org_id: string; id: string }>({
      query: ({ org_id, id }) => ({ url: `/${org_id}/enquiries/${id}/run-sheet`, method: 'GET' }),
      providesTags: (_r, _e, { id }) => [{ type: 'run-sheet', id }],
    }),
    setRunSheet: build.mutation<{ run_sheet: RunSheetRow }, { org_id: string; id: string; body: RunSheetBody }>({
      query: ({ org_id, id, body }) => ({ url: `/${org_id}/enquiries/${id}/run-sheet`, method: 'PUT', data: body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'run-sheet', id }, { type: 'enquiry-activities', id }],
    }),
    // Notes on the file (rule E10)
    getEnquiryNotes: build.query<{ notes: EnquiryNoteRow[] }, { org_id: string; id: string }>({
      query: ({ org_id, id }) => ({ url: `/${org_id}/enquiries/${id}/notes`, method: 'GET' }),
      providesTags: (_r, _e, { id }) => [{ type: 'enquiry-notes', id }],
    }),
    createEnquiryNote: build.mutation<{ note: EnquiryNoteRow }, { org_id: string; id: string; body: CreateNoteBody }>({
      query: ({ org_id, id, body }) => ({ url: `/${org_id}/enquiries/${id}/notes`, method: 'POST', data: body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'enquiry-notes', id }, { type: 'enquiry-file', id }, { type: 'enquiry-activities', id }],
    }),
    updateEnquiryNote: build.mutation<{ note: EnquiryNoteRow }, { org_id: string; id: string; note_id: string; body: UpdateNoteBody }>({
      query: ({ org_id, id, note_id, body }) => ({ url: `/${org_id}/enquiries/${id}/notes/${note_id}`, method: 'PATCH', data: body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'enquiry-notes', id }, { type: 'enquiry-activities', id }],
    }),
    deleteEnquiryNote: build.mutation<{ ok: boolean }, { org_id: string; id: string; note_id: string }>({
      query: ({ org_id, id, note_id }) => ({ url: `/${org_id}/enquiries/${id}/notes/${note_id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'enquiry-notes', id }, { type: 'enquiry-file', id }, { type: 'enquiry-activities', id }],
    }),
    setEnquirySummary: build.mutation<
      { file: EnquiryFile },
      { org_id: string; id: string } & SetSummaryBody
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/set-summary`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),
    setEnquiryDetails: build.mutation<
      { file: EnquiryFile },
      { org_id: string; id: string } & SetDetailsBody
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/enquiries/${id}/set-details`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ org_id, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(zionApi.util.upsertQueryData('getEnquiryFile', { org_id, id }, data))
        } catch {
          // surfaced by the caller
        }
      },
      invalidatesTags: (result, error, arg) => enquiryVerbTags(arg.id),
    }),

    // Task verbs -----------------------------------------------------------

    getTasks: build.query<
      { tasks: Task[] },
      {
        org_id: string
        serial?: string[]
        enquiry_id?: string
        booking_id?: string
        assigned_to?: string
        status?: TaskStatus[]
        due_from?: string
        due_to?: string
        limit?: number
      }
    >({
      query: ({ org_id, ...params }) => ({
        url: buildUrl(`/${org_id}/tasks`, params),
        method: 'GET',
      }),
      providesTags: [{ type: 'tasks', id: 'LIST' }],
    }),

    createTask: build.mutation<TaskVerbResult, { org_id: string } & CreateTaskBody>({
      query: ({ org_id, ...data }) => ({
        url: data.enquiry_id
          ? `/${org_id}/enquiries/${data.enquiry_id}/create-task`
          : `/${org_id}/tasks`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, arg) => taskVerbTags(arg.enquiry_id ?? result?.task.enquiry_id),
    }),

    updateTask: build.mutation<TaskVerbResult, { org_id: string; task_id: string } & UpdateTaskBody>({
      query: ({ org_id, task_id, ...data }) => ({
        url: `/${org_id}/tasks/${task_id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result) => taskVerbTags(result?.task.enquiry_id),
    }),

    completeTask: build.mutation<TaskVerbResult, { org_id: string; task_id: string }>({
      query: ({ org_id, task_id }) => ({
        url: `/${org_id}/tasks/${task_id}/complete`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: (result) => taskVerbTags(result?.task.enquiry_id),
    }),

    snoozeTask: build.mutation<TaskVerbResult, { org_id: string; task_id: string } & SnoozeTaskBody>({
      query: ({ org_id, task_id, ...data }) => ({
        url: `/${org_id}/tasks/${task_id}/snooze`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result) => taskVerbTags(result?.task.enquiry_id),
    }),

    dismissTask: build.mutation<TaskVerbResult, { org_id: string; task_id: string } & DismissTaskBody>({
      query: ({ org_id, task_id, ...data }) => ({
        url: `/${org_id}/tasks/${task_id}/dismiss`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result) => taskVerbTags(result?.task.enquiry_id),
    }),

    reassignTask: build.mutation<TaskVerbResult, { org_id: string; task_id: string } & ReassignTaskBody>({
      query: ({ org_id, task_id, ...data }) => ({
        url: `/${org_id}/tasks/${task_id}/reassign`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result) => taskVerbTags(result?.task.enquiry_id),
    }),

    reopenTask: build.mutation<TaskVerbResult, { org_id: string; task_id: string }>({
      query: ({ org_id, task_id }) => ({
        url: `/${org_id}/tasks/${task_id}/reopen`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: (result) => taskVerbTags(result?.task.enquiry_id),
    }),

    // Today and notifications ----------------------------------------------

    getWorkQueue: build.query<
      WorkQueue,
      { org_id: string; serial?: string[]; assignee?: string; limit?: number }
    >({
      query: ({ org_id, ...params }) => ({
        url: buildUrl(`/${org_id}/work-queue`, params),
        method: 'GET',
      }),
      providesTags: [{ type: 'work-queue', id: 'LIST' }],
    }),

    getWorkQueueCounts: build.query<
      { counts: WorkQueue['counts']; scope: WorkQueue['scope'] },
      { org_id: string; serial?: string[]; assignee?: string }
    >({
      query: ({ org_id, ...params }) => ({
        url: buildUrl(`/${org_id}/work-queue/counts`, params),
        method: 'GET',
      }),
      providesTags: [{ type: 'work-queue', id: 'COUNTS' }],
    }),

    getWorkspaceNotifications: build.query<
      { notifications: WorkspaceNotification[]; counts: { unseen: number } },
      { org_id: string; unseen?: boolean; limit?: number }
    >({
      query: ({ org_id, unseen, limit }) => ({
        url: buildUrl(`/${org_id}/notifications`, {
          ...(unseen ? { unseen: '1' } : {}),
          ...(limit ? { limit } : {}),
        }),
        method: 'GET',
      }),
      providesTags: [{ type: 'workspace-notifications', id: 'LIST' }],
    }),

    markNotificationSeen: build.mutation<{ seen: number }, { org_id: string; id: string }>({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/notifications/${id}/seen`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: [{ type: 'workspace-notifications', id: 'LIST' }],
    }),

    markAllNotificationsSeen: build.mutation<{ seen: number }, { org_id: string }>({
      query: ({ org_id }) => ({
        url: `/${org_id}/notifications/seen-all`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: [{ type: 'workspace-notifications', id: 'LIST' }],
    }),

    dismissNotification: build.mutation<{ dismissed: number }, { org_id: string; id: string }>({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/notifications/${id}/dismiss`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: [{ type: 'workspace-notifications', id: 'LIST' }],
    }),

    getDeposits: build.query<
      DepositsResponse,
      {
        org_id: string
        serial?: string
        query?: {
          page?: number
          limit?: number
          serial?: string[]
          search?: string
          status?: DepositStatus[]
          date_from?: string
          date_to?: string
          sort_by?: 'created_at' | 'amount' | 'status' | 'serial'
          sort_order?: 'asc' | 'desc'
        }
      }
    >({
      query: ({ org_id, serial, query = { page: 1, limit: 20 } }) => {
        const params: Record<string, unknown> = { ...query }

        if (serial && !query.serial) {
          params['serial[]'] = [serial]
        }

        if (query.serial?.length) {
          params['serial[]'] = query.serial
          delete params.serial
        }
        if (query.status?.length) {
          params['status[]'] = query.status
          delete params.status
        }

        return {
          url: buildUrl(`/${org_id}/deposits`, params),
          method: 'GET',
        }
      },
      providesTags: [{ type: 'deposits', id: 'LIST' }],
    }),

    /**
     * Issue a refund against any of the four charge aggregates.
     *
     * `amount` is PENCE, and `client_request_id` is minted once per dialog open
     * by `components/Refunds/RefundDialog.tsx`. Zion turns that id into its
     * idempotency key, so a double-click, a retried fetch or a duplicated
     * request collapses to one refund rather than refunding the guest twice.
     *
     * Invalidates `deposits` and `charges` as well as `refunds`: a refund moves
     * the target's refunded amount and can move its status, so a stale list
     * would show money as still fully collected, and the charges table would go
     * on offering a Refund action against a balance that has already gone.
     */
    /** Social bookings (A2 4.4): price, follow-up and link prefill of a proposal. Writes nothing. */
    pricePreview: build.mutation<PricePreviewResponse, { org_id: string; serial: string } & PricePreviewBody>({
      query: ({ org_id, serial, ...body }) => ({
        url: `/${org_id}/${serial}/bookings/manual/price-preview`,
        method: 'POST',
        data: body,
      }),
    }),

    /** Social bookings (A2 4.4, I35): set a booking's price override. */
    setPriceOverride: build.mutation<PriceOverrideResponse, { org_id: string; serial: string; booking_id: string } & PriceOverrideBody>({
      query: ({ org_id, serial, booking_id, ...body }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/price-override`,
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['bookings'],
    }),

    /** Social bookings (A5 3.13, D33): move a provisional hold's expiry later. */
    extendBookingHold: build.mutation<{ id: string; provisional_until: string }, { org_id: string; serial: string; booking_id: string; provisional_until: string }>({
      query: ({ org_id, serial, booking_id, provisional_until }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/extend-hold`,
        method: 'POST',
        data: { provisional_until },
      }),
      invalidatesTags: ['bookings'],
    }),

    /** Social bookings (A5 3.5, D31): move one activity of a night; `dry_run` previews the cascade. */
    moveBookingSegment: build.mutation<
      MoveSegmentPreview | (NightSaveAdditions & { segments: SegmentView[] }),
      { org_id: string; serial: string; booking_id: string; segment_id: string } & MoveSegmentBody
    >({
      query: ({ org_id, serial, booking_id, segment_id, ...body }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/segments/${segment_id}/move`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_r, _e, arg) => (arg.dry_run ? [] : ['bookings']),
    }),

    /** Social bookings (A5 3.6): check in, mark no-show or undo a no-show on one activity. */
    setBookingSegmentStatus: build.mutation<
      { segments: SegmentView[]; warnings: NightStatusWarning[] },
      { org_id: string; serial: string; booking_id: string; segment_id: string; action: SegmentStatusAction }
    >({
      query: ({ org_id, serial, booking_id, segment_id, action }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/segments/${segment_id}/status`,
        method: 'PUT',
        data: { action },
      }),
      invalidatesTags: ['bookings'],
    }),

    /** Social bookings (A5 4.1): the product's add-ons after the last row, for the staff chips. */
    getStaffAddOnOffers: build.query<
      StaffAddOnOffer[],
      { org_id: string; serial: string; product_type_id: string; date: string; party_size: number; after: string; booking_id?: string }
    >({
      query: ({ org_id, serial, ...params }) => ({
        url: buildUrl(`/${org_id}/${serial}/bookings/manual/add-on-offers`, params),
        method: 'GET',
      }),
    }),

    /** Social bookings (A5 3.14): a package's starts for staff; with `at`, that night and its conflicts. */
    getStaffPackageStarts: build.query<
      StaffPackageNight[],
      { org_id: string; serial: string; package_type_id: string; date: string; party_size: number; optional_activity_ids?: string; at?: string }
    >({
      query: ({ org_id, serial, ...params }) => ({
        url: buildUrl(`/${org_id}/${serial}/bookings/manual/package-starts`, params),
        method: 'GET',
      }),
      transformResponse: (body: { nights: StaffPackageNight[] }) => body.nights,
      keepUnusedDataFor: 0,
    }),

    /** Social bookings (A2 4.4, I35): remove a booking's price override. */
    clearPriceOverride: build.mutation<PriceOverrideResponse, { org_id: string; serial: string; booking_id: string }>({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/price-override`,
        method: 'DELETE',
      }),
      invalidatesTags: ['bookings'],
    }),

    createRefund: build.mutation<
      CreateRefundResponse,
      { org_id: string } & CreateRefundPayload
    >({
      query: ({ org_id, ...body }) => ({
        url: `/${org_id}/refunds`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (result, error, { target_id }) => [
        { type: 'refunds' as const, id: 'LIST' },
        { type: 'refunds' as const, id: target_id },
        { type: 'deposits' as const, id: 'LIST' },
        { type: 'charges' as const, id: 'LIST' },
      ],
    }),

    getRefunds: build.query<
      RefundsResponse,
      {
        org_id: string
        query?: {
          target_type?: RefundTargetType
          target_id?: string
          serial?: string[]
          status?: RefundState[]
          date_from?: string
          date_to?: string
          page?: number
          limit?: number
        }
      }
    >({
      query: ({ org_id, query = {} }) => {
        const params: Record<string, unknown> = { ...query }

        // Array filters go over the wire as `serial[]` / `status[]`, matching
        // the deposits list. The scalar key is deleted so the two do not both
        // arrive and disagree.
        if (query.serial?.length) {
          params['serial[]'] = query.serial
          delete params.serial
        }
        if (query.status?.length) {
          params['status[]'] = query.status
          delete params.status
        }

        return {
          url: buildUrl(`/${org_id}/refunds`, params),
          method: 'GET',
        }
      },
      providesTags: (result, error, { query }) =>
        query?.target_id
          ? [
              { type: 'refunds' as const, id: 'LIST' },
              { type: 'refunds' as const, id: query.target_id },
            ]
          : [{ type: 'refunds' as const, id: 'LIST' }],
    }),

    /**
     * Every sum of money taken from a guest, across all four charge aggregates,
     * in one paginated list. Backs `components/Charges`.
     *
     * `serial` narrows to a venue-scoped route. It is folded into the array
     * filter rather than sent alongside it, so a venue page and a venue picked
     * in the filter panel cannot arrive together and disagree.
     *
     * Array filters are passed under their PLAIN key: `buildUrl` stringifies
     * with `arrayFormat: 'bracket'` and appends the `[]` itself, which is what
     * the endpoint's zod union expects to read back.
     */
    getCharges: build.query<
      ChargesResponse,
      { org_id: string; serial?: string; query?: ChargesQuery }
    >({
      query: ({ org_id, serial, query = {} }) => {
        const params: Record<string, unknown> = { ...query }

        const serials = query.serial?.length
          ? query.serial
          : serial
            ? [serial]
            : undefined
        params.serial = serials

        // An empty array would otherwise stringify to nothing while still
        // reading as "filtered", so drop it rather than send a hollow filter.
        if (!query.target_type?.length) delete params.target_type
        if (!query.status?.length) delete params.status

        return {
          url: buildUrl(`/${org_id}/charges`, params),
          method: 'GET',
        }
      },
      providesTags: [{ type: 'charges' as const, id: 'LIST' }],
    }),

    getBookingReports: build.query<
      BookingReportResponse,
      {
        org_id: string
        serial?: string
        query: {
          date_from: string
          date_to: string
          page?: number
          limit?: number
          serial?: string[]
          status?: BookingReportStatus[]
          booking_type_id?: string[]
          sort_by?: 'start_time' | 'created_at' | 'party_size'
          sort_order?: 'asc' | 'desc'
          date_mode?: 'booking_date' | 'created_date'
          /** Social bookings (zion ARD A8 3.7): with a type filter, match the product only. */
          starting_only?: 'true' | 'false'
        }
      }
    >({
      query: ({ org_id, serial, query }) => {
        const params: Record<string, unknown> = { ...query }

        // Array filters go under their PLAIN key, as in `getCharges` above:
        // `buildUrl` stringifies with `arrayFormat: 'bracket'` and appends the
        // `[]` itself. Pre-bracketing them here put `serial[][]=` on the wire,
        // which express's qs happened to flatten back into a flat array, so the
        // filters worked by luck rather than by contract.
        //
        // The venue-scoped route's `serial` is folded into the array filter
        // rather than sent beside it, so a venue page and a venue picked in the
        // filter panel cannot arrive together and disagree.
        const venueSerial = serial ? [serial] : undefined
        params.serial = query.serial?.length ? query.serial : venueSerial

        // An empty array stringifies to nothing while still reading as
        // "filtered", so drop it rather than send a hollow filter.
        if (!query.status?.length) delete params.status
        if (!query.booking_type_id?.length) delete params.booking_type_id

        return {
          url: buildUrl(`/${org_id}/reports/bookings`, params),
          method: 'GET',
        }
      },
      providesTags: [{ type: 'booking-reports', id: 'LIST' }],
    }),

    /** Social bookings (A8 3.14): per-activity counts, revenue and attach rate for the window. */
    getActivitiesReport: build.query<
      ActivitiesReport,
      { org_id: string; query: { date_from: string; date_to: string; serial?: string[]; date_mode?: 'booking_date' | 'created_date' } }
    >({
      query: ({ org_id, query }) => {
        const params: Record<string, unknown> = { date_from: query.date_from, date_to: query.date_to }
        if (query.date_mode) params.date_mode = query.date_mode
        if (query.serial?.length) params.serial = query.serial
        return { url: buildUrl(`/${org_id}/reports/bookings/activities`, params), method: 'GET' }
      },
      providesTags: [{ type: 'booking-reports', id: 'ACTIVITIES' }],
    }),

    getBookingsOverview: build.query<
      BookingsOverviewResponse,
      {
        org_id: string
        query: {
          date_from: string
          date_to: string
          serial?: string[]
          date_mode?: 'booking_date' | 'created_date'
        }
      }
    >({
      query: ({ org_id, query }) => {
        const params: Record<string, unknown> = {
          date_from: query.date_from,
          date_to: query.date_to,
        }
        if (query.date_mode) params.date_mode = query.date_mode
        if (query.serial?.length) params.serial = query.serial
        return {
          url: buildUrl(`/${org_id}/reports/bookings/overview`, params),
          method: 'GET',
        }
      },
      providesTags: [{ type: 'booking-reports', id: 'OVERVIEW' }],
    }),

    // Fresh Booking Areas API — points at /:org_id/:serial/bookings/areas
    // (no config_id plumbing in the caller, the backend resolves it).
    listBookingAreas: build.query<
      BookingArea[],
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/bookings/areas`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({
                type: 'booking-areas' as const,
                id: a.id,
              })),
              { type: 'booking-areas', id: 'LIST' },
            ]
          : [{ type: 'booking-areas', id: 'LIST' }],
    }),
    createBookingArea: build.mutation<
      BookingArea,
      {
        org_id: string
        serial: string
        data: { name: string; emoji?: string; hidden?: boolean }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/bookings/areas`,
        method: 'POST',
        data,
      }),
      // Legacy `areas` too — the planners read config-scoped getAreas.
      invalidatesTags: [
        { type: 'booking-areas', id: 'LIST' },
        { type: 'areas', id: 'LIST' },
      ],
    }),
    updateBookingArea: build.mutation<
      BookingArea,
      {
        org_id: string
        serial: string
        id: string
        data: { name?: string; emoji?: string; hidden?: boolean }
      }
    >({
      query: ({ org_id, serial, id, data }) => ({
        url: `/${org_id}/${serial}/bookings/areas/${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'booking-areas', id },
        { type: 'booking-areas', id: 'LIST' },
        { type: 'areas', id: 'LIST' },
      ],
    }),
    deleteBookingArea: build.mutation<
      { id: string },
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/bookings/areas/${id}`,
        method: 'DELETE',
      }),
      // Legacy `areas` too — the planners read config-scoped getAreas.
      invalidatesTags: [
        { type: 'booking-areas', id: 'LIST' },
        { type: 'areas', id: 'LIST' },
      ],
    }),
    reorderBookingAreas: build.mutation<
      { ok: true },
      {
        org_id: string
        serial: string
        positions: { id: string; position: number }[]
      }
    >({
      query: ({ org_id, serial, positions }) => ({
        url: `/${org_id}/${serial}/bookings/areas/positions`,
        method: 'PUT',
        data: { positions },
      }),
      async onQueryStarted(
        { org_id, serial, positions },
        { dispatch, queryFulfilled }
      ) {
        const byId = new Map(positions.map((p) => [p.id, p.position]))
        const patch = dispatch(
          zionApi.util.updateQueryData(
            'listBookingAreas',
            { org_id, serial },
            (draft) => {
              for (const a of draft) {
                const next = byId.get(a.id)
                if (next != null) a.position = next
              }
              draft.sort((a, b) => a.position - b.position)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      // Legacy `areas` too — the planners read config-scoped getAreas.
      invalidatesTags: [
        { type: 'booking-areas', id: 'LIST' },
        { type: 'areas', id: 'LIST' },
      ],
    }),

    // Fresh Booking Tables API — pairs with the booking-areas one. Backend
    // resolves config_id internally; positions are scoped within an area.
    listBookingTables: build.query<
      BookingTable[],
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/bookings/tables`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((t) => ({
                type: 'booking-tables' as const,
                id: t.id,
              })),
              { type: 'booking-tables', id: 'LIST' },
            ]
          : [{ type: 'booking-tables', id: 'LIST' }],
    }),
    createBookingTable: build.mutation<
      BookingTable,
      {
        org_id: string
        serial: string
        data: {
          area_id: string
          name: string
          min_capacity: number
          max_capacity: number
          highchair?: boolean
          buggy?: boolean
          wheelchair?: boolean
          dog_friendly?: boolean
          active?: boolean
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/bookings/tables`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        { type: 'booking-tables', id: 'LIST' },
        { type: 'booking-areas', id: 'LIST' },
        { type: 'areas', id: 'LIST' },
      ],
    }),
    updateBookingTable: build.mutation<
      BookingTable,
      {
        org_id: string
        serial: string
        id: string
        data: {
          area_id?: string
          name?: string
          min_capacity?: number
          max_capacity?: number
          highchair?: boolean
          buggy?: boolean
          wheelchair?: boolean
          dog_friendly?: boolean
          active?: boolean
        }
      }
    >({
      query: ({ org_id, serial, id, data }) => ({
        url: `/${org_id}/${serial}/bookings/tables/${id}`,
        method: 'PATCH',
        data,
      }),
      async onQueryStarted(
        { org_id, serial, id, data },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          zionApi.util.updateQueryData(
            'listBookingTables',
            { org_id, serial },
            (draft) => {
              const t = draft.find((x) => x.id === id)
              if (!t) return
              Object.assign(t, data)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'booking-tables', id },
        { type: 'booking-tables', id: 'LIST' },
        { type: 'booking-areas', id: 'LIST' },
        { type: 'areas', id: 'LIST' },
      ],
    }),
    deleteBookingTable: build.mutation<
      { id: string },
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/bookings/tables/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'booking-tables', id: 'LIST' },
        { type: 'booking-areas', id: 'LIST' },
        { type: 'areas', id: 'LIST' },
      ],
    }),
    reorderBookingTables: build.mutation<
      { ok: true },
      {
        org_id: string
        serial: string
        area_id: string
        positions: { id: string; position: number }[]
      }
    >({
      query: ({ org_id, serial, area_id, positions }) => ({
        url: `/${org_id}/${serial}/bookings/tables/positions`,
        method: 'PUT',
        data: { area_id, positions },
      }),
      async onQueryStarted(
        { org_id, serial, area_id, positions },
        { dispatch, queryFulfilled }
      ) {
        const byId = new Map(positions.map((p) => [p.id, p.position]))
        const patch = dispatch(
          zionApi.util.updateQueryData(
            'listBookingTables',
            { org_id, serial },
            (draft) => {
              for (const t of draft) {
                if (t.area_id !== area_id) continue
                const next = byId.get(t.id)
                if (next != null) t.area_position = next
              }
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      // Legacy `areas` too — the planners read config-scoped getAreas.
      invalidatesTags: [
        { type: 'booking-tables', id: 'LIST' },
        { type: 'areas', id: 'LIST' },
      ],
    }),

    getCampaignBookingReport: build.query<
      CampaignBookingReportResponse,
      { org_id: string; campaign_id: string }
    >({
      query: ({ org_id, campaign_id }) => ({
        url: `/${org_id}/reports/bookings/campaign/${campaign_id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { campaign_id }) => [
        { type: 'booking-reports', id: `campaign-${campaign_id}` },
      ],
    }),

    getCampaignBookingsSummary: build.query<
      CampaignBookingSummaryResponse,
      {
        org_id: string
        query: {
          date_from: string
          date_to: string
          page?: number
          limit?: number
          serial?: string[]
          utm_id?: string[]
          sort_by?: string
          sort_order?: 'asc' | 'desc'
        }
      }
    >({
      query: ({ org_id, query }) => {
        const params: Record<string, unknown> = { ...query }

        if (query.serial?.length) {
          params['serial[]'] = query.serial
          delete params.serial
        }
        if (query.utm_id?.length) {
          params['utm_id[]'] = query.utm_id
          delete params.utm_id
        }

        return {
          url: buildUrl(`/${org_id}/reports/bookings/campaigns`, params),
          method: 'GET',
        }
      },
      providesTags: [{ type: 'booking-reports', id: 'campaigns-summary' }],
    }),

    getBookingsSearch: build.query<
      ServiceCursorPaginatedResponseType<Booking>,
      {
        serial: string
        org_id: string
        limit: number
        search: string
        cursor: string
      }
    >({
      query: ({ org_id, search, serial, cursor, limit }) => {
        return {
          url: buildUrl(`/${org_id}/${serial}/bookings/list`, {
            search,
            cursor,
            limit,
          }),
          method: 'GET',
        }
      },
      transformResponse: (
        items: ServiceCursorPaginatedResponseType<Booking>
      ) => {
        /**
         *  @todo - do this on backend
         */

        if ('data' in items) {
          //@ts-ignore
          items.data = items.data.map((item: any) => ({
            ...item,
            //@ts-ignore
            allergens: item?.allergens?.length ? item?.allergens : [],
            // Handle booking_tables - safely check if it exists and handle different formats
            booking_tables: item?.booking_tables
              ? Array.isArray(item?.booking_tables)
                ? typeof item?.booking_tables[0] === 'string'
                  ? item?.booking_tables
                  : item?.booking_tables.map(
                      (bt: any) => bt.table_id || bt.id || bt
                    )
                : []
              : [],
            duration:
              item?.end_time && item?.start_time
                ? differenceInSeconds(
                    new Date(item?.end_time),
                    new Date(item?.start_time)
                  )
                : 0,
          }))

          return items
        }

        return items
      },
    }),

    getBookingsFastView: build.query<
      ServiceCursorPaginatedResponseType<Booking>,
      {
        serial: string
        org_id: string
        date: string
        limit?: number
        search?: string
        first_name?: string
        last_name?: string
        email?: string
        reference?: string
        phone?: string
        cursor?: string
      }
    >({
      query: ({
        org_id,
        serial,
        date,
        limit = 50,
        search,
        first_name,
        last_name,
        email,
        reference,
        phone,
        cursor,
      }) => {
        const params: Record<string, any> = {
          date,
          limit,
        }

        if (cursor) params.cursor = cursor
        if (search) params.search = search
        if (first_name) params.first_name = first_name
        if (last_name) params.last_name = last_name
        if (email) params.email = email
        if (reference) params.reference = reference
        if (phone) params.phone = phone

        return {
          url: buildUrl(
            `/${org_id}/${serial}/fast-view/bookings/search`,
            params
          ),
          method: 'GET',
        }
      },
      transformResponse: (
        items: ServiceCursorPaginatedResponseType<Booking>
      ) => {
        if ('data' in items) {
          //@ts-ignore
          items.data = items.data.map((item: any) => ({
            ...item,
            //@ts-ignore
            allergens: item?.allergens?.length ? item?.allergens : [],
            // Keep the original tables array from server response for fast view
            tables: item?.tables || [],
            // Also maintain booking_tables for backward compatibility
            booking_tables: item?.booking_tables || [],
            duration: differenceInSeconds(
              new Date(item?.end_time),
              new Date(item?.start_time)
            ),
            startDate: new Date(item?.start_time),
            endDate: new Date(item?.end_time),
          }))

          return items
        }

        return items
      },
      providesTags: () => [{ type: 'search-bookings' }],
    }),

    getActiveBookings: build.query<
      ServiceCursorPaginatedResponseType<Booking>,
      {
        serial: string
        org_id: string
        date: string
        limit?: number
        cursor?: string
      }
    >({
      query: ({ org_id, serial, date, limit = 50, cursor }) => {
        const params: Record<string, any> = {
          date,
          limit,
          status: 'active', // Filter by active status
        }

        if (cursor) params.cursor = cursor

        return {
          url: buildUrl(
            `/${org_id}/${serial}/fast-view/bookings/active`,
            params
          ),
          method: 'GET',
        }
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          org_id: queryArgs.org_id,
          serial: queryArgs.serial,
          date: queryArgs.date,
          limit: queryArgs.limit,
          status: 'active', // Include status to differentiate cache keys
          cursor: '', // Normalize cursor for caching
        })
      },
      merge: (currentCache, newItems) => {
        if ('data' in currentCache && 'data' in newItems) {
          currentCache.links = newItems.links
          currentCache.meta = newItems.meta

          newItems.data.forEach((item: any) => {
            const current = currentCache.data.findIndex(
              (it: any) => it?.id === item?.id
            )
            if (current >= 0) {
              currentCache.data[current] = item
            } else {
              currentCache.data.push(item)
            }
          })
        } else {
          return newItems
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return Boolean(
          currentArg?.org_id !== previousArg?.org_id ||
            currentArg?.serial !== previousArg?.serial ||
            currentArg?.date !== previousArg?.date ||
            currentArg?.limit !== previousArg?.limit ||
            (currentArg?.cursor && currentArg.cursor !== previousArg?.cursor)
        )
      },
      transformResponse: (
        items: ServiceCursorPaginatedResponseType<Booking>
      ) => {
        if ('data' in items) {
          //@ts-ignore
          items.data = items.data.map((item: any) => ({
            ...item,
            //@ts-ignore
            allergens: item?.allergens?.length ? item?.allergens : [],
            tables: item?.tables || [],
            // Handle booking_tables - safely check if it exists and handle different formats
            booking_tables: item?.booking_tables
              ? Array.isArray(item?.booking_tables)
                ? typeof item?.booking_tables[0] === 'string'
                  ? item?.booking_tables
                  : item?.booking_tables.map(
                      (bt: any) => bt.table_id || bt.id || bt
                    )
                : []
              : [],
            duration:
              item?.end_time && item?.start_time
                ? differenceInSeconds(
                    new Date(item?.end_time),
                    new Date(item?.start_time)
                  )
                : 0,
          }))

          return items
        }

        return items
      },
      providesTags: (result, error, { org_id, serial, date }) => [
        { type: 'bookings' as const, id: `active-${org_id}-${serial}-${date}` },
      ],
    }),

    getSeatedBookings: build.query<
      ServiceCursorPaginatedResponseType<Booking>,
      {
        serial: string
        org_id: string
        date: string
        limit?: number
        cursor?: string
      }
    >({
      query: ({ org_id, serial, date, limit = 50, cursor }) => {
        const params: Record<string, any> = {
          date,
          limit,
          status: 'seated', // Filter by seated status
        }

        if (cursor) params.cursor = cursor

        return {
          url: buildUrl(
            `/${org_id}/${serial}/fast-view/bookings/seated`,
            params
          ),
          method: 'GET',
        }
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          org_id: queryArgs.org_id,
          serial: queryArgs.serial,
          date: queryArgs.date,
          limit: queryArgs.limit,
          status: 'seated', // Include status to differentiate cache keys
          cursor: '', // Normalize cursor for caching
        })
      },
      merge: (currentCache, newItems) => {
        if ('data' in currentCache && 'data' in newItems) {
          currentCache.links = newItems.links
          currentCache.meta = newItems.meta

          newItems.data.forEach((item: any) => {
            const current = currentCache.data.findIndex(
              (it: any) => it?.id === item?.id
            )
            if (current >= 0) {
              currentCache.data[current] = item
            } else {
              currentCache.data.push(item)
            }
          })
        } else {
          return newItems
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return Boolean(
          currentArg?.org_id !== previousArg?.org_id ||
            currentArg?.serial !== previousArg?.serial ||
            currentArg?.date !== previousArg?.date ||
            currentArg?.limit !== previousArg?.limit ||
            (currentArg?.cursor && currentArg.cursor !== previousArg?.cursor)
        )
      },
      transformResponse: (
        items: ServiceCursorPaginatedResponseType<Booking>
      ) => {
        if ('data' in items) {
          //@ts-ignore
          items.data = items.data.map((item: any) => ({
            ...item,
            //@ts-ignore
            allergens: item?.allergens?.length ? item?.allergens : [],
            tables: item?.tables || [],
            // Handle booking_tables - safely check if it exists and handle different formats
            booking_tables: item?.booking_tables
              ? Array.isArray(item?.booking_tables)
                ? typeof item?.booking_tables[0] === 'string'
                  ? item?.booking_tables
                  : item?.booking_tables.map(
                      (bt: any) => bt.table_id || bt.id || bt
                    )
                : []
              : [],
            duration:
              item?.end_time && item?.start_time
                ? differenceInSeconds(
                    new Date(item?.end_time),
                    new Date(item?.start_time)
                  )
                : 0,
          }))

          return items
        }

        return items
      },
      providesTags: (result, error, { org_id, serial, date }) => [
        { type: 'bookings' as const, id: `seated-${org_id}-${serial}-${date}` },
      ],
    }),

    getCompleteBookings: build.query<
      ServiceCursorPaginatedResponseType<Booking>,
      {
        serial: string
        org_id: string
        date: string
        limit?: number
        cursor?: string
      }
    >({
      query: ({ org_id, serial, date, limit = 50, cursor }) => {
        const params: Record<string, any> = {
          date,
          limit,
          status: 'complete', // Filter by complete status
        }

        if (cursor) params.cursor = cursor

        return {
          url: buildUrl(
            `/${org_id}/${serial}/fast-view/bookings/complete`,
            params
          ),
          method: 'GET',
        }
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          org_id: queryArgs.org_id,
          serial: queryArgs.serial,
          date: queryArgs.date,
          limit: queryArgs.limit,
          status: 'complete', // Include status to differentiate cache keys
          cursor: '', // Normalize cursor for caching
        })
      },
      merge: (currentCache, newItems) => {
        if ('data' in currentCache && 'data' in newItems) {
          currentCache.links = newItems.links
          currentCache.meta = newItems.meta

          newItems.data.forEach((item: any) => {
            const current = currentCache.data.findIndex(
              (it: any) => it?.id === item?.id
            )
            if (current >= 0) {
              currentCache.data[current] = item
            } else {
              currentCache.data.push(item)
            }
          })
        } else {
          return newItems
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return Boolean(
          currentArg?.org_id !== previousArg?.org_id ||
            currentArg?.serial !== previousArg?.serial ||
            currentArg?.date !== previousArg?.date ||
            currentArg?.limit !== previousArg?.limit ||
            (currentArg?.cursor && currentArg.cursor !== previousArg?.cursor)
        )
      },
      transformResponse: (
        items: ServiceCursorPaginatedResponseType<Booking>
      ) => {
        if ('data' in items) {
          //@ts-ignore
          items.data = items.data.map((item: any) => ({
            ...item,
            //@ts-ignore
            allergens: item?.allergens?.length ? item?.allergens : [],
            tables: item?.tables || [],
            // Handle booking_tables - safely check if it exists and handle different formats
            booking_tables: item?.booking_tables
              ? Array.isArray(item?.booking_tables)
                ? typeof item?.booking_tables[0] === 'string'
                  ? item?.booking_tables
                  : item?.booking_tables.map(
                      (bt: any) => bt.table_id || bt.id || bt
                    )
                : []
              : [],
            duration:
              item?.end_time && item?.start_time
                ? differenceInSeconds(
                    new Date(item?.end_time),
                    new Date(item?.start_time)
                  )
                : 0,
          }))

          return items
        }

        return items
      },
      providesTags: (result, error, { org_id, serial, date }) => [
        {
          type: 'bookings' as const,
          id: `complete-${org_id}-${serial}-${date}`,
        },
      ],
    }),

    getBookingsActivity: build.query<
      ServiceCursorPaginatedResponseType<ActivityLog>,
      {
        serial: string
        org_id: string
        limit: number
        search: string
        cursor: string
        oauth_id: string
      }
    >({
      query: ({ org_id, search, serial, cursor, limit, oauth_id }) => {
        return {
          url: buildUrl(`/${org_id}/${serial}/activity-log`, {
            search,
            cursor,
            limit,
            oauth_id,
          }),
          method: 'GET',
        }
      },
    }),
    createMenu: build.mutation<
      { id: string; name: string },
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          name: string
          branding_id?: string
          creation_img?: string
        }
      }
    >({
      query: ({ org_id, serial, data, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'menus', id: 'LIST' }],
    }),
    duplicateMenu: build.mutation<
      any,
      { org_id: string; serial: string; config_id: string; menu_id: string }
    >({
      query: ({ org_id, serial, config_id, menu_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'menus', id: 'LIST' }],
    }),
    updateMenu: build.mutation<
      any,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        data: {
          id: string
          active: boolean
          name: string
          creation_img?: string
          branding_id?: string
          description?: string
          header_img?: string | null
          gallery_imgs?: string[]
          reminder_enabled?: boolean
          reminder_frequency_days?: number | null
          reminder_cutoff_hours?: number | null
          pay_ahead?: boolean
          individual_payment?: boolean
          allow_hide_prices_from_guests?: boolean
          pre_order_cutoff?: number | null
          min_party_size?: number | null
          max_party_size?: number | null
          online_ordering?: boolean
          online_ordering_fulfilment_type?: 'IN_STORE' | 'PICKUP' | null
          sold_out_buffer_enabled?: boolean
          sold_out_buffer?: number
        }
      }
    >({
      query: ({ org_id, serial, data, config_id, menu_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}`,
        method: 'put',
        data,
      }),
      invalidatesTags: (result: any) => [
        { type: 'menu', id: result.id },
        { type: 'menus', id: 'LIST' },
      ],
    }),
    createMenuCategory: build.mutation<
      MenuCategoryUpsert,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        data: MenuCategoryUpsert
      }
    >({
      query: ({ org_id, serial, data, config_id, menu_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_categories`,
        method: 'post',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu', id: menu_id },
      ],
    }),
    updateMenuCategoryOrder: build.mutation<
      MenuCategoryUpsert,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        data: {
          menu_category_ids: string[]
        }
      }
    >({
      query: ({ org_id, serial, data, config_id, menu_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_categories/order`,
        method: 'put',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu', id: menu_id },
      ],
    }),
    updateMenuCategoryMenuItemOrder: build.mutation<
      void,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        menu_category_id: string
        data: {
          menu_item_ids: string[]
        }
      }
    >({
      query: ({
        org_id,
        serial,
        data,
        config_id,
        menu_id,
        menu_category_id,
      }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_categories/${menu_category_id}/menu_items/order`,
        method: 'put',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu', id: menu_id },
      ],
    }),
    updateMenuCategory: build.mutation<
      MenuCategoryUpsert,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        menu_category_id: string
        data: MenuCategoryUpsert
      }
    >({
      query: ({
        org_id,
        serial,
        data,
        config_id,
        menu_id,
        menu_category_id,
      }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_categories/${menu_category_id}`,
        method: 'put',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu', id: menu_id },
      ],
    }),
    fetchMenus: build.query<
      Menu[],
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'menus', id: 'LIST' }],
    }),
    fetchMenu: build.query<
      Menu,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
      }
    >({
      query: ({ org_id, serial, config_id, menu_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'menu', id: result?.id }],
    }),
    fetchItems: build.query<
      ServiceCursorPaginatedResponseType<Item>,
      {
        org_id: string
        serial: string
        config_id: string
        query?: { cursor: string; limit: number; search?: string }
      }
    >({
      query: ({
        config_id,
        org_id,
        serial,
        query = {
          cursor: '',
          limit: 25,
          search: '',
        },
      }) => ({
        url: buildUrl(`/${org_id}/${serial}/config/${config_id}/menus/items`, {
          ...query,
        }),
        method: 'get',
      }),
      providesTags: (result, _error) => [{ type: 'items', id: 'LIST' }],
    }),
    fetchMenuItems: build.query<
      ServiceCursorPaginatedResponseType<MenuItem>,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        query?: { cursor: string; limit: number; search?: string }
      }
    >({
      query: ({
        config_id,
        org_id,
        serial,
        menu_id,
        query = {
          cursor: '',
          limit: 25,
          search: '',
        },
      }) => ({
        url: buildUrl(
          `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_items`,
          {
            ...query,
          }
        ),
        method: 'get',
      }),
      providesTags: (result, _error) => [{ type: 'menu-items', id: 'LIST' }],
    }),

    upsertMenuItems: build.mutation<
      MenuItemUpsert,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        menu_item_id: string
        data: MenuItemUpsert
      }
    >({
      query: ({ config_id, org_id, serial, menu_id, data, menu_item_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_items/${menu_item_id}`,
        method: 'put',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id, menu_item_id }) => [
        { type: 'menu-items', id: 'LIST' },
        { type: 'menu-items', id: menu_item_id },
        { type: 'menu', id: menu_id },
      ],
    }),

    createMenuItem: build.mutation<
      MenuItemUpsert,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        data: MenuItemUpsert
      }
    >({
      query: ({ config_id, org_id, serial, menu_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_items`,
        method: 'post',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu-items', id: 'LIST' },
        { type: 'menu', id: menu_id },
      ],
    }),

    updateMenuItem: build.mutation<
      MenuItem,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        menu_item_id: string
        data: MenuItemUpsert
      }
    >({
      query: ({ config_id, org_id, serial, menu_id, data, menu_item_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_items/${menu_item_id}`,
        method: 'put',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id, menu_item_id }) => [
        { type: 'menu-items', id: 'LIST' },
        { type: 'menu-items', id: menu_item_id },
        { type: 'menu', id: menu_id },
      ],
    }),

    fetchMenuItem: build.query<
      MenuItem,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        menu_item_id: string
      }
    >({
      query: ({ config_id, org_id, serial, menu_id, menu_item_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_items/${menu_item_id}`,
        method: 'get',
      }),
      providesTags: (result) => [{ type: 'menu-items', id: result?.id }],
    }),

    deleteMenu: build.mutation<
      void,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
      }
    >({
      query: ({ config_id, org_id, serial, menu_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menus', id: 'LIST' },
        { type: 'menu', id: menu_id },
      ],
    }),

    deleteMenuItem: build.mutation<
      void,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        menu_item_id: string
      }
    >({
      query: ({ config_id, org_id, serial, menu_id, menu_item_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_items/${menu_item_id}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu-items', id: 'LIST' },
        { type: 'menu', id: menu_id },
      ],
    }),

    deleteMenuCategory: build.mutation<
      void,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        menu_category_id: string
      }
    >({
      query: ({ config_id, org_id, serial, menu_id, menu_category_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_categories/${menu_category_id}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu', id: menu_id },
      ],
    }),

    // ordering.stampede.ai table ordering (MenuTable's own type comment) -
    // a menu's guest-orderable table list.
    getMenuTables: build.query<
      MenuTable[],
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
      }
    >({
      query: ({ org_id, serial, config_id, menu_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/tables`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { menu_id }) => [
        { type: 'menu-tables', id: menu_id },
      ],
    }),
    createMenuTable: build.mutation<
      MenuTable,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        // `data.table_id` is the real Table row being mapped in (from
        // `useGetTablesQuery`) - not the MenuTable join row's own id, which
        // doesn't exist yet at create time. `table_ref` is never sent -
        // zion generates (or reuses, on a re-add of the same table) it.
        data: { table_id: string }
      }
    >({
      query: ({ org_id, serial, config_id, menu_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/tables`,
        method: 'post',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu-tables', id: menu_id },
      ],
    }),
    updateMenuTable: build.mutation<
      MenuTable,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        // The MenuTable join row's own id (route param), not a Table id.
        table_id: string
        data: { active?: boolean }
      }
    >({
      query: ({ org_id, serial, config_id, menu_id, table_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/tables/${table_id}`,
        method: 'put',
        data,
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu-tables', id: menu_id },
      ],
    }),
    deleteMenuTable: build.mutation<
      void,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        table_id: string
      }
    >({
      query: ({ org_id, serial, config_id, menu_id, table_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/tables/${table_id}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu-tables', id: menu_id },
      ],
    }),

    /* ---------------------------------------------------------------------
     * Menu import from PDF (MENU_PDF_VIEW_ONLY_ARD.md A4)
     *
     * Config-scoped like the rest of menu CRUD. Extraction is queued, so the
     * create call returns a job id and the client polls `fetchMenuImport`
     * until the status is terminal. No `Menu` row exists until commit, which
     * is why only `commitMenuImport` invalidates the menus list.
     * ------------------------------------------------------------------ */

    createMenuImport: build.mutation<
      MenuImportCreateResponse,
      {
        org_id: string
        serial: string
        config_id: string
        source_type: MenuImportSourceType
        /** One entry for `PDF`, up to 20 for `IMAGES`. Order is page order. */
        source_urls: string[]
        original_filename?: string
      }
    >({
      // The venue supplies links to a menu they already host; no binary
      // crosses any wire. Zion validates every entry (https only, no private
      // ranges, no embedded credentials) and rejects naming the failing index.
      // `original_filename` is optional and zion derives one when omitted.
      query: ({
        org_id,
        serial,
        config_id,
        source_type,
        source_urls,
        original_filename,
      }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/imports`,
        method: 'POST',
        data: {
          source_type,
          source_urls,
          ...(original_filename ? { original_filename } : {}),
        },
      }),
      invalidatesTags: [{ type: 'menu-imports', id: 'LIST' }],
    }),

    fetchMenuImports: build.query<
      MenuImportJob[],
      { org_id: string; serial: string; config_id: string }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/imports`,
        method: 'GET',
      }),
      // Zion answers `{ jobs: [...] }`, not a bare array. Without this the
      // hook hands an object to every consumer, so the page banner and the
      // dialog's resume list both silently render nothing: a closed import
      // looked lost when it was sitting on the server the whole time.
      // Tolerates a bare array too, so this keeps working if zion is ever
      // brought into line with its other list endpoints.
      transformResponse: (
        response: MenuImportJob[] | { jobs?: MenuImportJob[] }
      ) => (Array.isArray(response) ? response : (response?.jobs ?? [])),
      providesTags: [{ type: 'menu-imports', id: 'LIST' }],
    }),

    fetchMenuImport: build.query<
      MenuImportJob,
      { org_id: string; serial: string; config_id: string; job_id: string }
    >({
      query: ({ org_id, serial, config_id, job_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/imports/${job_id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { job_id }) => [
        { type: 'menu-imports', id: job_id },
      ],
    }),

    commitMenuImport: build.mutation<
      MenuImportCommitResponse,
      {
        org_id: string
        serial: string
        config_id: string
        job_id: string
        data: MenuImportCommitPayload
      }
    >({
      query: ({ org_id, serial, config_id, job_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/imports/${job_id}/commit`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { job_id, serial }) => [
        { type: 'menu-imports', id: job_id },
        { type: 'menu-imports', id: 'LIST' },
        { type: 'menus', id: 'LIST' },
        // The attach step can add the new menu to booking types, events and
        // special dates, so their cached copies are stale too. The venue
        // config is keyed by serial, not 'LIST' (see `getConfig`).
        { type: 'booking-types', id: 'LIST' },
        { type: 'events', id: 'LIST' },
        { type: 'special-dates', id: 'LIST' },
        { type: 'booking-config', id: serial },
      ],
    }),

    /** Re-enqueue a FAILED job. The PDF is already in S3, so nothing re-uploads. */
    retryMenuImport: build.mutation<
      MenuImportJob,
      { org_id: string; serial: string; config_id: string; job_id: string }
    >({
      query: ({ org_id, serial, config_id, job_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/imports/${job_id}/retry`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { job_id }) => [
        { type: 'menu-imports', id: job_id },
        { type: 'menu-imports', id: 'LIST' },
      ],
    }),

    removeMenuItemFromCategory: build.mutation<
      void,
      {
        org_id: string
        serial: string
        config_id: string
        menu_id: string
        menu_category_id: string
        menu_item_id: string
      }
    >({
      query: ({
        config_id,
        org_id,
        serial,
        menu_id,
        menu_category_id,
        menu_item_id,
      }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/${menu_id}/menu_categories/${menu_category_id}/menu_items/${menu_item_id}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { menu_id }) => [
        { type: 'menu', id: menu_id },
      ],
    }),
    fetchModifiers: build.query<
      ServiceCursorPaginatedResponseType<Modifier>,
      {
        org_id: string
        serial: string
        config_id: string
        query?: { cursor: string; limit: number; search?: string }
      }
    >({
      query: ({
        config_id,
        org_id,
        serial,
        query = {
          cursor: '',
          limit: 25,
          search: '',
        },
      }) => ({
        url: buildUrl(
          `/${org_id}/${serial}/config/${config_id}/menus/modifiers`,
          {
            ...query,
          }
        ),
        method: 'get',
      }),
      providesTags: (result, _error) => [{ type: 'modifiers', id: 'LIST' }],
    }),
    fetchModifierCategories: build.query<
      ServiceCursorPaginatedResponseType<ModifierCategory>,
      {
        org_id: string
        serial: string
        config_id: string
        query?: { cursor: string; limit: number; search?: string }
      }
    >({
      query: ({
        config_id,
        org_id,
        serial,
        query = {
          cursor: '',
          limit: 25,
          search: '',
        },
      }) => ({
        url: buildUrl(
          `/${org_id}/${serial}/config/${config_id}/menus/modifier-categories`,
          {
            ...query,
          }
        ),
        method: 'get',
      }),
      providesTags: (result, _error) => [
        { type: 'modifier-categories', id: 'LIST' },
      ],
    }),

    fetchAllergens: build.query<
      ServiceCursorPaginatedResponseType<{ id: string; name: string }>,
      {
        org_id: string
        serial: string
        config_id: string
        query?: { cursor: string; limit: number; search?: string }
      }
    >({
      query: ({
        config_id,
        org_id,
        serial,
        query = {
          cursor: '',
          limit: 25,
          search: '',
        },
      }) => ({
        url: buildUrl(`/${org_id}/${serial}/config/${config_id}/allergens`, {
          ...query,
        }),
        method: 'get',
      }),
    }),

    fetchDietary: build.query<
      ServiceCursorPaginatedResponseType<{ id: string; name: string }>,
      {
        org_id: string
        serial: string
        config_id: string
        query?: { cursor: string; limit: number; search?: string }
      }
    >({
      query: ({
        config_id,
        org_id,
        serial,
        query = {
          cursor: '',
          limit: 25,
          search: '',
        },
      }) => ({
        url: buildUrl(`/${org_id}/${serial}/config/${config_id}/dietary`, {
          ...query,
        }),
        method: 'get',
      }),
    }),

    fetchTags: build.query<
      ServiceCursorPaginatedResponseType<{ id: string; name: string }>,
      {
        org_id: string
        serial: string
        config_id: string
        query?: { cursor: string; limit: number; search?: string }
      }
    >({
      query: ({
        config_id,
        org_id,
        serial,
        query = {
          cursor: '',
          limit: 25,
          search: '',
        },
      }) => ({
        url: buildUrl(`/${org_id}/${serial}/config/${config_id}/tags`, {
          ...query,
        }),
        method: 'get',
      }),
      providesTags: (result, _error) => [{ type: 'tags', id: 'LIST' }],
    }),

    upsertTags: build.mutation<
      { id: string; name: string }[],
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          tag_names: string[]
        }
      }
    >({
      query: ({ config_id, org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/tags`,
        method: 'put',
        data,
      }),
      invalidatesTags: [{ type: 'tags', id: 'LIST' }],
    }),

    createModifierCategory: build.mutation<
      ModifierCategory,
      {
        org_id: string
        serial: string
        config_id: string
        data: ModifierCategoryCreate
      }
    >({
      query: ({ config_id, org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/modifier-categories`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'modifier-categories', id: 'LIST' }],
    }),

    updateModifierCategory: build.mutation<
      ModifierCategory,
      {
        org_id: string
        serial: string
        config_id: string
        modifier_category_id: string
        data: ModifierCategoryCreate
      }
    >({
      query: ({ config_id, org_id, serial, modifier_category_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/modifier-categories/${modifier_category_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [{ type: 'modifier-categories', id: 'LIST' }],
    }),

    creatItem: build.mutation<
      ItemCreation,
      {
        org_id: string
        serial: string
        config_id: string
        data: ItemCreation
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/items`,
        method: 'post',
        data,
      }),
      invalidatesTags: [{ type: 'items', id: 'LIST' }],
    }),
    updateItem: build.mutation<
      ItemCreation,
      {
        org_id: string
        serial: string
        config_id: string
        data: ItemCreation
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/items/${data.id}`,
        method: 'put',
        data,
      }),
      invalidatesTags: [{ type: 'items', id: 'LIST' }],
    }),

    deleteItem: build.mutation<
      Item,
      {
        org_id: string
        serial: string
        config_id: string
        item_id: string
      }
    >({
      query: ({ org_id, serial, config_id, item_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/items/${item_id}`,
        method: 'delete',
      }),
      invalidatesTags: [{ type: 'items', id: 'LIST' }],
    }),

    creatModifier: build.mutation<
      Modifier,
      {
        org_id: string
        serial: string
        config_id: string
        data: CreateModifier
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/modifiers`,
        method: 'post',
        data,
      }),
      invalidatesTags: [{ type: 'modifiers', id: 'LIST' }],
    }),
    updateModifier: build.mutation<
      Modifier,
      {
        org_id: string
        serial: string
        config_id: string
        data: CreateModifier
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/modifiers/${data.id}`,
        method: 'put',
        data,
      }),
      invalidatesTags: [{ type: 'modifiers', id: 'LIST' }],
    }),

    // Booking Upgrades endpoints
    getBookingUpgrades: build.query<
      BookingUpgradesResponse,
      BookingUpgradeParams
    >({
      query: ({ org_id, serial, config_id, page = 1, limit = 20, search }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-upgrades`,
        method: 'GET',
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: 'booking-upgrades' as const,
                id,
              })),
              { type: 'booking-upgrades', id: 'LIST' },
            ]
          : [{ type: 'booking-upgrades', id: 'LIST' }],
    }),

    getBookingUpgrade: build.query<
      BookingUpgrade,
      {
        org_id: string
        serial: string
        config_id: string
        upgrade_id: string
      }
    >({
      query: ({ org_id, serial, config_id, upgrade_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-upgrades/${upgrade_id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { upgrade_id }) => [
        { type: 'booking-upgrades', id: upgrade_id },
      ],
    }),

    createBookingUpgrade: build.mutation<
      BookingUpgrade,
      {
        org_id: string
        serial: string
        config_id: string
        data: CreateBookingUpgradePayload
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-upgrades`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'booking-upgrades', id: 'LIST' }],
    }),

    updateBookingUpgrade: build.mutation<
      BookingUpgrade,
      {
        org_id: string
        serial: string
        config_id: string
        upgrade_id: string
        data: UpdateBookingUpgradePayload
      }
    >({
      query: ({ org_id, serial, config_id, upgrade_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-upgrades/${upgrade_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { upgrade_id }) => [
        { type: 'booking-upgrades', id: upgrade_id },
        { type: 'booking-upgrades', id: 'LIST' },
      ],
    }),

    deleteBookingUpgrade: build.mutation<
      { message: string; data: BookingUpgrade },
      {
        org_id: string
        serial: string
        config_id: string
        upgrade_id: string
      }
    >({
      query: ({ org_id, serial, config_id, upgrade_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-upgrades/${upgrade_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { upgrade_id }) => [
        { type: 'booking-upgrades', id: upgrade_id },
        { type: 'booking-upgrades', id: 'LIST' },
      ],
    }),

    getSquareCatalogItems: build.query<
      SquareCatalogItemsResponse,
      SquareCatalogItemsParams
    >({
      query: ({
        org_id,
        serial,
        limit,
        offset,
        search,
        categoryId,
        sortBy,
        sortOrder,
      }) => ({
        url: `/${org_id}/${serial}/neo/catalog/items`,
        method: 'GET',
        params: {
          ...(limit !== undefined ? { limit } : {}),
          ...(offset !== undefined ? { offset } : {}),
          ...(search ? { search } : {}),
          ...(categoryId ? { categoryId } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(sortOrder ? { sortOrder } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: 'square-catalog-items' as const,
                id,
              })),
              { type: 'square-catalog-items', id: 'LIST' },
            ]
          : [{ type: 'square-catalog-items', id: 'LIST' }],
    }),

    // Kicks off a Square catalogue/modifier sync as a background job rather than a
    // blocking request: a real venue's catalogue easily runs past this app's request
    // timeout, which made a slow-but-succeeding sync look failed. Poll the job with
    // getNeoSyncStatus below instead - same shape as the CSV importer's fix, which hit
    // the same class of problem with SSE (auth header on a non-axios fetch, proxy
    // buffering in prod) and settled on plain polling over customAxios.
    startNeoSync: build.mutation<
      { job_id: string },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/neo/sync`,
        method: 'POST',
      }),
    }),

    /**
     * Force a booking's deposit onto its Square reservation.
     *
     * Square only applies a deposit to a check that already exists when the
     * update lands, so this fails usefully rather than silently: a 409 means
     * nothing is open on the till yet, and its message is the instruction to
     * show the user. Zion computes the amount, the browser never names it.
     */
    pushSquareDeposit: build.mutation<
      {
        success: boolean
        action: 'pushed' | 'already_applied'
        check: { squareCheckId: string; tableName: string | null }
      },
      { org_id: string; serial: string; booking_id: string }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/square/push-deposit`,
        method: 'POST',
      }),
    }),

    getNeoSyncStatus: build.query<
      {
        id: string
        status: 'pending' | 'running' | 'done' | 'failed'
        phase: 'modifiers' | 'catalog_items'
        last_message: string | null
        modifiers_result: {
          categories_created?: number
          categories_updated?: number
          modifiers_created?: number
          modifiers_updated?: number
        } | null
        catalog_result: {
          created?: number
          linked_by_name?: number
          already_linked?: number
        } | null
        error_message: string | null
      },
      { org_id: string; serial: string; job_id: string }
    >({
      query: ({ org_id, serial, job_id }) => ({
        url: `/${org_id}/${serial}/neo/sync/${job_id}`,
        method: 'GET',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data.status === 'done') {
            dispatch(
              zionApi.util.invalidateTags([
                { type: 'items', id: 'LIST' },
                { type: 'square-catalog-items', id: 'LIST' },
                { type: 'modifiers', id: 'LIST' },
                { type: 'modifier-categories', id: 'LIST' },
              ])
            )
          }
        } catch {
          // Polling error surfaces via the hook's own error state - nothing to do here.
        }
      },
    }),

    getModifiers: build.query<
      { modifiers: Modifier[]; cursor?: string; total?: number },
      {
        org_id: string
        serial: string
        config_id: string
        limit?: number
        cursor?: string
        search?: string
      }
    >({
      query: ({ org_id, serial, config_id, limit, cursor, search }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/modifiers`,
        method: 'GET',
        params: {
          ...(limit !== undefined ? { limit } : {}),
          ...(cursor ? { cursor } : {}),
          ...(search ? { search } : {}),
        },
      }),
      // Backend cursorPaginator returns { data, cursor, total }; map data -> modifiers.
      transformResponse: (response: any) => ({
        modifiers: response?.data ?? response?.modifiers ?? [],
        cursor: response?.cursor,
        total: response?.total,
      }),
      providesTags: [{ type: 'modifiers', id: 'LIST' }],
    }),

    getModifierCategories: build.query<
      {
        modifier_categories: ModifierCategory[]
        cursor?: string
        total?: number
      },
      {
        org_id: string
        serial: string
        config_id: string
        limit?: number
        cursor?: string
        search?: string
      }
    >({
      query: ({ org_id, serial, config_id, limit, cursor, search }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/menus/modifier-categories`,
        method: 'GET',
        params: {
          ...(limit !== undefined ? { limit } : {}),
          ...(cursor ? { cursor } : {}),
          ...(search ? { search } : {}),
        },
      }),
      // Backend cursorPaginator returns { data, cursor, total }; map data -> modifier_categories.
      transformResponse: (response: any) => ({
        modifier_categories:
          response?.data ?? response?.modifier_categories ?? [],
        cursor: response?.cursor,
        total: response?.total,
      }),
      providesTags: [{ type: 'modifier-categories', id: 'LIST' }],
    }),

    getCoverLimits: build.query<
      CoverLimitDaysType,
      {
        org_id: string
        serial: string
        config_id: string
        other_id?: string
        type: 'base' | 'special-dates' | 'booking-types'
      }
    >({
      query: ({ org_id, config_id, serial, type, other_id }) => {
        let url = `/${org_id}/${serial}/config/${config_id}/cover-limits`
        if (type === 'booking-types') {
          url = `/${org_id}/${serial}/config/${config_id}/booking-types/${other_id}/cover-limits`
        }
        if (type === 'special-dates') {
          url = `/${org_id}/${serial}/config/${config_id}/special-dates/${other_id}/cover-limits`
        }

        return {
          url,
          method: 'GET',
        }
      },
      providesTags: (_, _2, _meta) => [
        {
          type: 'cover-limits',
          id: `${_meta?.type}_${_meta?.config_id}_${_meta?.other_id}`,
        },
      ],
      transformResponse: (items: CoverLimitDaysType) => {
        console.log({ items }, 'cover-limits')

        return items
      },
    }),
    updateCoverLimits: build.mutation<
      CoverLimitDaysType,
      {
        org_id: string
        serial: string
        config_id: string
        other_id?: string
        type: 'base' | 'special-dates' | 'booking-types'
        data: CoverLimitDaysType
      }
    >({
      query: ({ org_id, config_id, serial, type, other_id, data }) => {
        let url = `/${org_id}/${serial}/config/${config_id}/cover-limits`
        if (type === 'booking-types') {
          url = `/${org_id}/${serial}/config/${config_id}/booking-types/${other_id}/cover-limits`
        }
        if (type === 'special-dates') {
          url = `/${org_id}/${serial}/config/${config_id}/special-dates/${other_id}/cover-limits`
        }

        return {
          url,
          method: 'PUT',
          data,
        }
      },
      invalidatesTags: (_, _2, _meta) => [
        {
          type: 'cover-limits',
          id: `${_meta?.type}_${_meta?.config_id}_${_meta?.other_id}`,
        },
      ],
    }),

    getConfigs: build.query<ZionConfigWithBranding[], { org_id: string }>({
      query: ({ org_id }) => ({
        url: `/${org_id}/config`,
        method: 'GET',
      }),
    }),

    getConfig: build.query<ZionVenueConfig, { org_id: string; serial: string }>(
      {
        query: ({ org_id, serial }) => ({
          url: `/${org_id}/${serial}/config/serial`,
          method: 'GET',
        }),
        providesTags: (res: any) => {
          return [{ id: res?.serial, type: 'booking-config' }]
        },
      }
    ),
    getDynamicConfig: build.query<
      ZionDynamicConfig,
      {
        org_id: string
        serial: string
        date: string
        party_size: number
        booking_type_id?: string
      }
    >({
      query: ({ org_id, serial, date, party_size, booking_type_id }) => ({
        url: `/public/${org_id}/${serial}/config?date=${date}&party_size=${party_size}${booking_type_id ? `&booking_type_id=${booking_type_id}` : ''}`,
        method: 'GET',
      }),
      providesTags: (res: any) => {
        return [{ id: res?.serial, type: 'booking-config' }]
      },
    }),
    getGoogleConfig: build.query<
      GoogleConfig,
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/google/config`,
        method: 'GET',
      }),
      providesTags: (res: any) => {
        return [{ id: res?.serial, type: 'google-config' }]
      },
    }),

    updateGoogleConfig: build.mutation<
      GoogleConfig,
      {
        org_id: string
        serial: string
        data: GoogleConfig
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/google/config`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (res: any) => {
        return [{ id: res?.id, type: 'google-config' }]
      },
    }),
    // ----------------------------------------------------------------------
    // Custom enquiry questions and offered subjects
    // (ENQUIRIES_CUSTOM_QUESTIONS_ARD.md §3.1, §3.2, §9.1). Every write also
    // clears the guest-facing config cache on the server.
    // ----------------------------------------------------------------------
    getEnquiryQuestions: build.query<
      EnquiryQuestion[],
      { org_id: string; serial: string; config_id: string }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/enquiry-questions`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'enquiry-questions' as const, id })),
              { type: 'enquiry-questions' as const, id: 'LIST' },
            ]
          : [{ type: 'enquiry-questions' as const, id: 'LIST' }],
    }),
    createEnquiryQuestion: build.mutation<
      EnquiryQuestion,
      { org_id: string; serial: string; config_id: string; data: EnquiryQuestionInput }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/enquiry-questions`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'enquiry-questions', id: 'LIST' }],
    }),
    updateEnquiryQuestion: build.mutation<
      EnquiryQuestion,
      {
        org_id: string
        serial: string
        config_id: string
        question_id: string
        data: EnquiryQuestionInput
      }
    >({
      query: ({ org_id, serial, config_id, question_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/enquiry-questions/${question_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_res, _err, { question_id }) => [
        { type: 'enquiry-questions', id: question_id },
        { type: 'enquiry-questions', id: 'LIST' },
      ],
    }),
    deleteEnquiryQuestion: build.mutation<
      void,
      { org_id: string; serial: string; config_id: string; question_id: string }
    >({
      query: ({ org_id, serial, config_id, question_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/enquiry-questions/${question_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'enquiry-questions', id: 'LIST' }],
    }),
    sortEnquiryQuestions: build.mutation<
      void,
      { org_id: string; serial: string; config_id: string; ids: string[] }
    >({
      query: ({ org_id, serial, config_id, ids }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/enquiry-questions/positions`,
        method: 'PUT',
        data: { ids },
      }),
      invalidatesTags: [{ type: 'enquiry-questions', id: 'LIST' }],
    }),
    getEnquirySubjects: build.query<
      EnquirySubjects,
      { org_id: string; serial: string; config_id: string }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/enquiry-questions/subjects`,
        method: 'GET',
      }),
      providesTags: [{ type: 'enquiry-subjects', id: 'LIST' }],
    }),
    updateEnquirySubjects: build.mutation<
      EnquirySubjects,
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          booking_types?: { id: string; enquiries_offered: boolean }[]
          events?: { id: string; enquiries_offered: boolean }[]
        }
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/enquiry-questions/subjects`,
        method: 'PUT',
        data,
      }),
      // The flag also lives on the type and event rows other screens read.
      invalidatesTags: [
        { type: 'enquiry-subjects', id: 'LIST' },
        { type: 'booking-types', id: 'LIST' },
        { type: 'events', id: 'LIST' },
      ],
    }),
    getBookingTypes: build.query<
      BookingType[],
      { org_id: string; serial: string; config_id: string }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-types`,
        method: 'GET',
      }),
      providesTags: (result: any) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'booking-types',
                id: (id as string | undefined) || undefined,
              })),
              { type: 'booking-types', id: 'LIST' },
            ]
          : [{ type: 'booking-types', id: 'LIST' }],
    }),
    /** The venue's party tiers (social bookings A7 4.2), each naming the types that price it. */
    getPartyTiers: build.query<PartyTier[], { org_id: string; serial: string; config_id: string }>({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/party-tiers`,
        method: 'GET',
      }),
      transformResponse: (body: { tiers: PartyTier[] }) => body.tiers,
      providesTags: [{ type: 'party-tiers', id: 'LIST' }],
    }),
    /** Saves the whole list; a removed tier a type prices is 409 TIER_IN_USE. */
    updatePartyTiers: build.mutation<
      PartyTier[],
      { org_id: string; serial: string; config_id: string; tiers: Array<{ id?: string; name: string; position: number; active: boolean }> }
    >({
      query: ({ org_id, serial, config_id, tiers }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/party-tiers`,
        method: 'PUT',
        data: { tiers },
      }),
      transformResponse: (body: { tiers: PartyTier[] }) => body.tiers,
      invalidatesTags: [
        { type: 'party-tiers', id: 'LIST' },
        { type: 'booking-types', id: 'LIST' },
      ],
    }),
    /** One booking type with its rate card (social bookings A2 4.2); the list omits the rows. */
    getBookingType: build.query<
      BookingType,
      { org_id: string; serial: string; config_id: string; booking_type_id: string }
    >({
      query: ({ org_id, serial, config_id, booking_type_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-types/${booking_type_id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { booking_type_id }) => [
        { type: 'booking-types', id: booking_type_id },
      ],
    }),
    getBookingTypeNameById: build.query<
      {
        name: string
      },
      { org_id: string; booking_type_id: string }
    >({
      query: ({ org_id, booking_type_id }) => ({
        url: `/${org_id}/booking-types/${booking_type_id}/name`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { booking_type_id }) => [
        { type: 'booking-types', id: booking_type_id },
      ],
    }),
    createBookingType: build.mutation<
      BookingType,
      { org_id: string; serial: string; config_id: string; data: BookingType }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-types`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'booking-types', id: 'LIST' }],
    }),
    updateBookingType: build.mutation<
      BookingType,
      {
        org_id: string
        serial: string
        config_id: string
        data: BookingType
        booking_type_id: string
      }
    >({
      query: ({ org_id, serial, config_id, data, booking_type_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-types/${booking_type_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { booking_type_id }) => [
        { type: 'booking-types', id: 'LIST' },
        { type: 'booking-types', id: booking_type_id },
      ],
    }),
    updateBookingTypePositions: build.mutation<
      BookingType[],
      { org_id: string; serial: string; config_id: string; data: BookingType[] }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/booking-types/positions`,
        method: 'PUT',
        data,
      }),
    }),
    archiveBookingType: build.mutation<
      BookingType,
      {
        org_id: string
        serial: string
        config_id: string
        booking_type_id: string
      }
    >({
      query: ({ org_id, serial, config_id, booking_type_id }) => ({
        method: 'DELETE',
        url: `/${org_id}/${serial}/config/${config_id}/booking-types/${booking_type_id}`,
      }),
      invalidatesTags: (result, error, { booking_type_id }) => [
        { type: 'booking-types', id: booking_type_id },
      ],
    }),
    updateConfig: build.mutation<
      ZionVenueConfig,
      {
        org_id: string
        serial: string
        config_id: string
        data: Partial<ZionVenueConfig>
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (res: any) => {
        return [
          { id: res?.serial, type: 'booking-config' },
          { id: res?.id, type: 'config-allergens' },
          { id: res?.id, type: 'config-dietary' },
          { id: res?.id, type: 'config-special-occasions' },
        ]
      },
    }),

    // ------------------------------------------------------------------
    // Table optimiser (TABLE_SHUFFLE_ARD.md D14): the "Test now" replay.
    // POST starts (or joins) a deterministic job; GET polls it. Poll with
    // `pollingInterval` while status === 'running'.
    // ------------------------------------------------------------------
    startOptimisationReplay: build.mutation<
      OptimisationReplayJob,
      {
        org_id: string
        serial: string
        from: string
        to: string
        flex?: {
          min_party: number
          max_party: number
          min_duration_minutes: number
        }[]
      }
    >({
      query: ({ org_id, serial, ...data }) => ({
        url: `/${org_id}/${serial}/optimisation/replay`,
        method: 'POST',
        data,
      }),
    }),

    getOptimisationReplay: build.query<
      OptimisationReplayJob,
      { org_id: string; serial: string; job_id: string }
    >({
      query: ({ org_id, serial, job_id }) => ({
        url: `/${org_id}/${serial}/optimisation/replay/${job_id}`,
        method: 'GET',
      }),
    }),

    // Current allergen selections for a config
    getConfigAllergens: build.query<
      { data: AllergenSelection[] },
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/allergens`,
        method: 'GET',
      }),
      providesTags: (result, error, { config_id }) => [
        { type: 'config-allergens', id: config_id },
      ],
    }),

    // Available allergen options for dropdown
    getConfigAllergenOptions: build.query<
      PaginatedResponse<ConfigOptionItem>,
      {
        org_id: string
        serial: string
        config_id: string
        limit?: number
        search?: string
        cursor?: string
      }
    >({
      query: ({ org_id, serial, config_id, limit = 50, search, cursor }) => {
        const params = new URLSearchParams()
        params.append('limit', limit.toString())
        if (search) params.append('search', search)
        if (cursor) params.append('cursor', cursor)

        return {
          url: `/${org_id}/${serial}/config/${config_id}/allergens/options?${params.toString()}`,
          method: 'GET',
        }
      },
      providesTags: [{ type: 'allergen-options', id: 'LIST' }],
    }),

    // Current dietary selections for a config
    getConfigDietary: build.query<
      { data: DietarySelection[] },
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/dietary`,
        method: 'GET',
      }),
      providesTags: (result, error, { config_id }) => [
        { type: 'config-dietary', id: config_id },
      ],
    }),

    // Available dietary options for dropdown
    getConfigDietaryOptions: build.query<
      PaginatedResponse<ConfigOptionItem>,
      {
        org_id: string
        serial: string
        config_id: string
        limit?: number
        search?: string
        cursor?: string
      }
    >({
      query: ({ org_id, serial, config_id, limit = 50, search, cursor }) => {
        const params = new URLSearchParams()
        params.append('limit', limit.toString())
        if (search) params.append('search', search)
        if (cursor) params.append('cursor', cursor)

        return {
          url: `/${org_id}/${serial}/config/${config_id}/dietary/options?${params.toString()}`,
          method: 'GET',
        }
      },
      providesTags: [{ type: 'dietary-options', id: 'LIST' }],
    }),

    // Current special occasion selections for a config
    getConfigSpecialOccasions: build.query<
      { data: SpecialOccasionSelection[] },
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/special-occasions`,
        method: 'GET',
      }),
      providesTags: (result, error, { config_id }) => [
        { type: 'config-special-occasions', id: config_id },
      ],
    }),

    // Available special occasion options for dropdown
    getConfigSpecialOccasionOptions: build.query<
      PaginatedResponse<ConfigOptionItem>,
      {
        org_id: string
        serial: string
        config_id: string
        limit?: number
        search?: string
        cursor?: string
      }
    >({
      query: ({ org_id, serial, config_id, limit = 50, search, cursor }) => {
        const params = new URLSearchParams()
        params.append('limit', limit.toString())
        if (search) params.append('search', search)
        if (cursor) params.append('cursor', cursor)

        return {
          url: `/${org_id}/${serial}/config/${config_id}/special-occasions/options?${params.toString()}`,
          method: 'GET',
        }
      },
      providesTags: [{ type: 'special-occasion-options', id: 'LIST' }],
    }),

    getVenues: build.query<
      MorpheusVenueWithinABookingContext[],
      { org_id: string }
    >({
      query: ({ org_id }) => ({
        url: `/morpheus/${org_id}/bookings/venues`,
        method: 'GET',
      }),
    }),
    getVenue: build.query<
      MorpheusVenueWithinABookingContext,
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/morpheus/${org_id}/bookings/venues/${serial}`,
        method: 'GET',
      }),
    }),
    getSpecialDates: build.query<
      SpecialDate[],
      { org_id: string; serial: string; config_id: string }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/special-dates`,
        method: 'GET',
      }),
      providesTags: (result: any) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'special-dates',
                id: (id as string | undefined) || undefined,
              })),
              { type: 'special-dates', id: 'LIST' },
            ]
          : [{ type: 'special-dates', id: 'LIST' }],
    }),
    createSpecialDate: build.mutation<
      SpecialDate,
      { org_id: string; serial: string; config_id: string; data: SpecialDate }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/special-dates`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'special-dates', id: 'LIST' }],
    }),
    updateSpecialDate: build.mutation<
      SpecialDate,
      {
        org_id: string
        serial: string
        config_id: string
        data: SpecialDate
        special_date_id: string
      }
    >({
      query: ({ org_id, serial, config_id, data, special_date_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/special-dates/${special_date_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { special_date_id }) => [
        { type: 'special-dates', id: special_date_id },
        { type: 'special-dates', id: 'LIST' },
      ],
    }),

    archiveSpecialDate: build.mutation<
      SpecialDate,
      {
        org_id: string
        serial: string
        config_id: string
        special_date_id: string
      }
    >({
      query: ({ org_id, serial, config_id, special_date_id }) => ({
        method: 'DELETE',
        url: `/${org_id}/${serial}/config/${config_id}/special-dates/${special_date_id}`,
      }),
      invalidatesTags: (result, error, { special_date_id }) => [
        { type: 'special-dates', id: special_date_id },
      ],
    }),
    getTables: build.query<Table[], { org_id: string; serial: string }>({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/tables`,
        method: 'GET',
      }),
      providesTags: (result: any) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'tables',
                id: (id as string | undefined) || undefined,
              })),
              { type: 'tables', id: 'LIST' },
            ]
          : [{ type: 'tables', id: 'LIST' }],
    }),
    getAreas: build.query<
      Area[],
      {
        org_id: string
        serial: string
        config_id: string
        /**
         * Opt-in, and only the Archived Tables screen opts in. Omit it and the
         * request, the cache key and the response are all exactly as before:
         * the day-planner table picker reads this endpoint and must not get
         * archived tables back (STA-4713).
         */
        include_archived?: boolean
      }
    >({
      query: ({ org_id, serial, config_id, include_archived }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/areas${
          include_archived ? '?include_archived=true' : ''
        }`,
        method: 'GET',
      }),
      providesTags: (result: any) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'areas',
                id: (id as string | undefined) || undefined,
              })),
              { type: 'areas', id: 'LIST' },
            ]
          : [{ type: 'areas', id: 'LIST' }],
    }),
    updateAreaPositions: build.mutation<
      Area[],
      { org_id: string; serial: string; config_id: string; data: Area[] }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/areas/positions`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [{ type: 'areas', id: 'LIST' }],
    }),
    createArea: build.mutation<
      Area,
      { org_id: string; serial: string; config_id: string; data: Area }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/areas`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'areas', id: 'LIST' }],
    }),
    updateArea: build.mutation<
      Area,
      {
        org_id: string
        serial: string
        config_id: string
        area_id: string
        data: Area
      }
    >({
      query: ({ org_id, serial, config_id, area_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/areas/${area_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { area_id }) => [
        { type: 'areas', id: area_id },
        { type: 'areas', id: 'LIST' },
      ],
    }),
    archiveAreaType: build.mutation<
      Area,
      {
        org_id: string
        serial: string
        config_id: string
        area_id: string
      }
    >({
      query: ({ org_id, serial, config_id, area_id }) => ({
        method: 'DELETE',
        url: `/${org_id}/${serial}/config/${config_id}/areas/${area_id}`,
      }),
      invalidatesTags: (result, error, { area_id }) => [
        { type: 'areas', id: area_id },
      ],
    }),

    createTable: build.mutation<
      Table,
      {
        org_id: string
        serial: string
        config_id: string
        area_id: string
        data: Table
      }
    >({
      query: ({ org_id, serial, config_id, area_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/areas/${area_id}/tables`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { area_id }) => [
        { type: 'areas', id: area_id },
        { type: 'tables', id: 'LIST' },
      ],
    }),
    updateTable: build.mutation<
      Table,
      {
        org_id: string
        serial: string
        config_id: string
        area_id: string
        table_id: string
        data: Table
      }
    >({
      query: ({ org_id, serial, config_id, area_id, table_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/areas/${area_id}/tables/${table_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { area_id }) => [
        { type: 'areas', id: area_id },
        { type: 'tables', id: 'LIST' },
      ],
    }),
    archiveTable: build.mutation<
      Table,
      {
        org_id: string
        serial: string
        config_id: string
        area_id: string
        table_id: string
      }
    >({
      query: ({ org_id, serial, config_id, area_id, table_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/areas/${area_id}/tables/${table_id}`,
        method: 'delete',
      }),
      invalidatesTags: (result, error, { area_id }) => [
        { type: 'areas', id: area_id },
        { type: 'tables', id: 'LIST' },
      ],
    }),
    updateTablePositions: build.mutation<
      Table[],
      {
        org_id: string
        serial: string
        config_id: string
        area_id: string
        data: Table[]
      }
    >({
      query: ({ org_id, serial, config_id, area_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/areas/${area_id}/tables/positions`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [{ type: 'areas', id: 'LIST' }],
    }),

    createCombination: build.mutation<
      Combination,
      {
        org_id: string
        serial: string
        config_id: string
        data: Combination
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/combinations`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        { type: 'combinations', id: 'LIST' },
        { type: 'generate-combination', id: 'LIST' },
      ],
    }),
    getCombinations: build.query<
      Combination[],
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/combinations`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'combinations', id: 'LIST' }],
    }),
    updateBulkCombinations: build.mutation<
      Combination[],
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          combination_ids: string[]
          action: 'activate' | 'deactivate'
        }
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/combinations/bulk-action-update`,
        method: 'PUT',
        data,
      }),

      invalidatesTags: [{ type: 'generate-combination', id: 'LIST' }],

      async onQueryStarted(
        { org_id, serial, config_id, data },
        { dispatch, queryFulfilled, getState }
      ) {
        // patch the cached getCombinations result
        const patchResult = dispatch(
          zionApi.util.updateQueryData(
            'getCombinations',
            { org_id, serial, config_id },
            (draft: any) => {
              for (const combo of draft) {
                if (data.combination_ids.includes(combo.id)) {
                  // toggle the flag based on your action
                  combo.active = data.action === 'activate'
                }
              }
            }
          )
        )
        try {
          // let the mutation run
          await queryFulfilled
        } catch {
          // if it fails, undo the patch
          patchResult.undo()
        }
      },
    }),

    updateCombination: build.mutation<
      Combination,
      {
        org_id: string
        serial: string
        config_id: string
        combination_id: string
        data: Combination
      }
    >({
      query: ({ org_id, serial, config_id, combination_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/combinations/${combination_id}`,
        method: 'PUT',
        data,
      }),
      async onQueryStarted(
        { org_id, serial, config_id, combination_id, data },
        { dispatch, queryFulfilled }
      ) {
        // Patch the cached getCombinations row immediately so the active
        // toggle (and any other field edit from the sheet) reflects in
        // the table without waiting for the network round-trip. Reverts
        // on failure.
        const patch = dispatch(
          zionApi.util.updateQueryData(
            'getCombinations',
            { org_id, serial, config_id },
            (draft) => {
              const idx = draft.findIndex((c) => c.id === combination_id)
              if (idx === -1) return
              draft[idx] = { ...draft[idx], ...data, id: combination_id }
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (result, error, { combination_id }) => [
        { type: 'combinations', id: 'LIST' },
        { type: 'combinations', id: combination_id },
        { type: 'generate-combination', id: 'LIST' },
      ],
    }),
    getCombinationGenerations: build.query<
      GenerateMoreCombination[],
      {
        org_id: string
        serial: string
        config_id: string
        combination_id: string
      }
    >({
      query: ({ org_id, serial, config_id, combination_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/combinations/${combination_id}/generate-more-combinations`,
        method: 'GET',
      }),
      providesTags: (result, error) => [
        { type: 'generate-combination', id: 'LIST' },
      ],
    }),
    createCombinationGenerations: build.mutation<
      GenerateMoreCombination[],
      {
        org_id: string
        serial: string
        config_id: string
        combination_id: string
        data: {
          generated_combinations: GenerateMoreCombination[]
        }
      }
    >({
      query: ({ org_id, serial, config_id, combination_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/combinations/${combination_id}/generate-more-combinations`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { combination_id }) => [
        { type: 'combinations', id: combination_id },
        { type: 'generate-combination', id: 'LIST' },
      ],
    }),
    archiveCombination: build.mutation<
      Combination,
      {
        org_id: string
        serial: string
        config_id: string
        combination_id: string
      }
    >({
      query: ({ org_id, serial, config_id, combination_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/combinations/${combination_id}`,
        method: 'delete',
      }),
      invalidatesTags: (result, error, { combination_id }) => [
        { type: 'combinations', id: combination_id },
      ],
    }),
    AutoByResource: build.query<
      AutoResourcePayload,
      {
        org_id: string
        serial: string
        config_id: string
        id: string
        start_time: string
      }
    >({
      query: ({ org_id, serial, config_id, id, start_time }) => ({
        url: buildUrl(
          `/${org_id}/${serial}/config/${config_id}/availability/auto-by-resource`,
          {
            id,
            start_time,
          }
        ),
        method: 'GET',
      }),
    }),
    getAvailability: build.query<
      Availability | undefined,
      {
        org_id: string
        serial: string
        config_id: string
        party_size: number
        booking_type: string
        date: string
        duration: string
        booking_id?: string
        event_id?: string
        wheelchair?: boolean
        buggy?: boolean
        highchair?: boolean
        dog_friendly?: boolean
        /** Social bookings (A5 3.14): a package row, whose units include its extras. */
        package_activity_id?: string
      }
    >({
      query: ({
        org_id,
        serial,
        config_id,
        party_size,
        booking_type,
        date,
        duration,
        booking_id,
        event_id,
        package_activity_id,
        wheelchair,
        buggy,
        highchair,
        dog_friendly,
      }) => ({
        url: buildUrl(`/${org_id}/${serial}/config/${config_id}/availability`, {
          booking_id,
          booking_type: booking_type === 'standard' ? null : booking_type,
          date,
          party_size,
          duration,
          ...(event_id ? { event_id } : {}),
          ...(package_activity_id ? { package_activity_id } : {}),
          ...(wheelchair ? { wheelchair: 'true' } : {}),
          ...(buggy ? { buggy: 'true' } : {}),
          ...(highchair ? { highchair: 'true' } : {}),
          ...(dog_friendly ? { dog_friendly: 'true' } : {}),
        }),
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    getAvailabilityConfig: build.query<
      AvailabilityConfig,
      {
        org_id: string
        serial: string
        config_id: string
        party_size: number
        booking_type_id?: string
        date: string
        duration?: string
        booking_id?: string
      }
    >({
      query: ({
        org_id,
        serial,
        config_id,
        party_size,
        booking_type_id,
        date,
        duration,
        booking_id,
      }) => ({
        url: buildUrl(
          `/${org_id}/${serial}/config/${config_id}/availability/config`,
          {
            booking_id,
            booking_type_id,
            date,
            party_size,
            duration,
          }
        ),
        method: 'GET',
      }),
    }),
    collisionsCheck: build.query<
      CollisionCheck,
      {
        org_id: string
        serial: string
        config_id: string
        data: CollisionCheckSchemaPayload
      }
    >({
      query: ({ org_id, serial, data, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/availability/collisions`,
        method: 'POST',
        data,
      }),
    }),

    coversFlow: build.query<
      CoversFlow,
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          date: string
        }
      }
    >({
      query: ({ org_id, serial, data, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/availability/covers-flow?date=${data.date}`,
        method: 'GET',
        data,
      }),
    }),

    fetchDailyNotes: build.query<
      DailyNote[],
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          date: string
        }
      }
    >({
      query: ({ org_id, serial, data, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/daily-notes?date=${data.date}`,
        method: 'GET',
        data,
      }),
      providesTags: (result, error) => [{ type: 'daily-notes', id: 'LIST' }],
    }),
    fetchDailyNotesCount: build.query<
      {
        count: number
      },
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          date: string
        }
      }
    >({
      query: ({ org_id, serial, data, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/daily-notes/count?date=${data.date}`,
        method: 'GET',
        data,
      }),
      providesTags: (result, error) => [
        { type: 'daily-notes-count', id: 'LIST' },
      ],
    }),
    createDailyNote: build.mutation<
      DailyNote,
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          date: string
          note: string
        }
      }
    >({
      query: ({ org_id, serial, data, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/daily-notes`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        { type: 'daily-notes', id: 'LIST' },
        { type: 'daily-notes-count', id: 'LIST' },
      ],
    }),
    deleteDailyNote: build.mutation<
      {},
      {
        org_id: string
        serial: string
        config_id: string
        data: {
          note_id: string
        }
      }
    >({
      query: ({ org_id, serial, data, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/daily-notes/${data.note_id}`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: [
        { type: 'daily-notes', id: 'LIST' },
        { type: 'daily-notes-count', id: 'LIST' },
      ],
    }),

    fetchOauthConfig: build.query<
      OauthUserConfig,
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/oauth-config`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'oauth-config', id: 'LIST' }],
    }),

    updateOauthConfig: build.mutation<
      OauthUserConfig,
      {
        org_id: string
        serial: string
        config_id: string
        data: UpdateOauthUserConfigPayload
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/oauth-config`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [{ type: 'oauth-config', id: 'LIST' }],
    }),

    fetchBookingsToComplete: build.query<
      Booking[],
      {
        org_id: string
        serial: string
        selected_date: string
      }
    >({
      query: ({ org_id, serial, selected_date }) => ({
        url: `/${org_id}/${serial}/bookings/manual/actions/complete?date=${selected_date}`,
        method: 'GET',
      }),
    }),

    processBookingsToComplete: build.mutation<
      Booking[],
      {
        org_id: string
        serial: string
        data: {
          date: string
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual/actions/complete`,
        method: 'PUT',
        data,
      }),

      invalidatesTags: () => [{ type: 'bookings', id: 'List' }],
    }),

    createManualBooking: build.mutation<
      Booking,
      {
        org_id: string
        serial: string
        data: CreateManualBookingPayload
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual`,
        method: 'POST',
        data: {
          ...data,
        },
      }),
      invalidatesTags: [
        { type: 'daily-stats' },
        { type: 'cover-counts', id: 'LIST' },
        { type: 'daily-stats-new', id: 'LIST' },
      ],

      async onQueryStarted(
        { org_id, serial, data },
        { dispatch, queryFulfilled }
      ) {
        try {
          const newBooking = await queryFulfilled
          console.log('onQueryStarted', { newBooking })

          const bookingDate = format(new Date(data.start_time), 'yyyy-MM-dd')

          // Update getBookingsByDate cache
          dispatch(
            zionApi.util.updateQueryData(
              'getBookingsByDate',
              {
                org_id,
                serial,
                date: bookingDate,
              },
              (draft: any) => {
                draft.push({
                  startDate: new Date(newBooking.data.start_time),
                  endDate: new Date(newBooking.data.end_time),
                  ...newBooking.data,
                })
              }
            )
          )

          // Update getBookingsByDateV2 cache (used by floorplan-fast)
          dispatch(
            zionApi.util.updateQueryData(
              'getBookingsByDateV2',
              {
                org_id,
                serial,
                date: bookingDate,
              },
              (draft: any) => {
                draft.push({
                  startDate: new Date(newBooking.data.start_time),
                  endDate: new Date(newBooking.data.end_time),
                  ...newBooking.data,
                })
              }
            )
          )
        } catch (error) {
          // Handle error
        }
      },
    }),

    // ── Waitlist (Feature A) ────────────────────────────────────────────
    // See zion/WAITLIST_ARD.md §6. List is pollable (D14 polling backup);
    // sockets handled by useWaitlistSubscriptions.
    getWaitlist: build.query<
      WaitlistEntry[],
      { org_id: string; serial: string; date: string; status?: string }
    >({
      query: ({ org_id, serial, date, status }) => ({
        url: `/${org_id}/${serial}/waitlist?date=${date}${
          status ? `&status=${status}` : ''
        }`,
        method: 'GET',
      }),
      providesTags: [{ type: 'waitlist', id: 'LIST' }],
    }),
    getWaitlistEntry: build.query<
      WaitlistEntry & { thread?: unknown },
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'waitlist', id }],
    }),
    getWaitlistSuggestion: build.query<
      WaitlistEtaSuggestion,
      {
        org_id: string
        serial: string
        party_size: number
        booking_type_id?: string
        date?: string
      }
    >({
      query: ({ org_id, serial, party_size, booking_type_id, date }) => ({
        url: `/${org_id}/${serial}/waitlist/suggestion?party_size=${party_size}${
          booking_type_id ? `&booking_type_id=${booking_type_id}` : ''
        }${date ? `&date=${date}` : ''}`,
        method: 'GET',
      }),
    }),
    getWaitlistReport: build.query<
      WaitlistReport,
      { org_id: string; serial: string; from: string; to: string }
    >({
      query: ({ org_id, serial, from, to }) => ({
        url: `/${org_id}/${serial}/waitlist/report?from=${from}&to=${to}`,
        method: 'GET',
      }),
    }),
    getWaitlistSeatOptions: build.query<
      WaitlistSeatOptions,
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/seat-options`,
        method: 'GET',
      }),
    }),
    // Live chat (staff side). Messages arrive via socket.io; these are the REST
    // load / send / end-chat write paths.
    getWaitlistChat: build.query<
      ChatThread | null,
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/chat`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { id }) => [
        { type: 'waitlist', id: `chat-${id}` },
      ],
    }),
    sendWaitlistChatMessage: build.mutation<
      ChatMessage,
      { org_id: string; serial: string; id: string; body: string }
    >({
      query: ({ org_id, serial, id, body }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/chat/messages`,
        method: 'POST',
        data: { body },
      }),
    }),
    endWaitlistChat: build.mutation<
      ChatThread,
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/chat/end`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'waitlist', id: `chat-${id}` },
      ],
    }),
    reopenWaitlistChat: build.mutation<
      ChatThread,
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/chat/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'waitlist', id: `chat-${id}` },
      ],
    }),
    addWaitlistEntry: build.mutation<
      WaitlistEntry,
      {
        org_id: string
        serial: string
        data: {
          party_size: number
          mobile?: string | null
          first_name?: string | null
          last_name?: string | null
          email?: string | null
          eta_minutes?: number | null
          notes?: string | null
          tags?: string[]
          source?: string
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/waitlist`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'waitlist', id: 'LIST' }],
    }),
    updateWaitlistEntry: build.mutation<
      WaitlistEntry,
      {
        org_id: string
        serial: string
        id: string
        data: Partial<{
          party_size: number
          first_name: string | null
          last_name: string | null
          email: string | null
          eta_minutes: number | null
          notes: string | null
          tags: string[]
        }>
      }
    >({
      query: ({ org_id, serial, id, data }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'waitlist', id },
        { type: 'waitlist', id: 'LIST' },
      ],
    }),
    notifyWaitlistEntry: build.mutation<
      WaitlistEntry & { sms_sent?: boolean },
      {
        org_id: string
        serial: string
        id: string
        data?: { custom_body?: string | null; hold_table_ids?: string[] }
      }
    >({
      query: ({ org_id, serial, id, data }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/notify`,
        method: 'POST',
        data: data ?? {},
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'waitlist', id },
        { type: 'waitlist', id: 'LIST' },
      ],
    }),
    seatWaitlistEntry: build.mutation<
      Booking,
      {
        org_id: string
        serial: string
        id: string
        data: {
          booking_tables: string[]
          start_time?: string
          duration?: number
          booking_type_id?: string | null
          status?: string
        }
      }
    >({
      query: ({ org_id, serial, id, data }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/seat`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        { type: 'waitlist', id: 'LIST' },
        { type: 'daily-stats' },
        { type: 'cover-counts', id: 'LIST' },
        { type: 'daily-stats-new', id: 'LIST' },
      ],
    }),
    removeWaitlistEntry: build.mutation<
      WaitlistEntry,
      {
        org_id: string
        serial: string
        id: string
        data: { removed_reason: string; message?: string | null }
      }
    >({
      query: ({ org_id, serial, id, data }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/remove`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'waitlist', id },
        { type: 'waitlist', id: 'LIST' },
      ],
    }),
    restoreWaitlistEntry: build.mutation<
      WaitlistEntry,
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'waitlist', id },
        { type: 'waitlist', id: 'LIST' },
      ],
    }),
    noShowWaitlistEntry: build.mutation<
      WaitlistEntry,
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/waitlist/${id}/no-show`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'waitlist', id },
        { type: 'waitlist', id: 'LIST' },
      ],
    }),
    reorderWaitlist: build.mutation<
      void,
      {
        org_id: string
        serial: string
        entries: { id: string; position: number }[]
      }
    >({
      query: ({ org_id, serial, entries }) => ({
        url: `/${org_id}/${serial}/waitlist/reorder`,
        method: 'PUT',
        data: { entries },
      }),
      invalidatesTags: [{ type: 'waitlist', id: 'LIST' }],
    }),
    getNextArrivedBooking: build.query<
      {
        arrivedBooking: Booking | null
        nextArrivedBooking: string | null | undefined
      },
      {
        org_id: string
        serial: string
        booking_id: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/next-arrived`,
        method: 'GET',
      }),
    }),
    updateManualBooking: build.mutation<
      BookingAndDuration,
      {
        org_id: string
        serial: string
        booking_id: string
        // zion's UpdateManualBookingSchema is CreateManualBookingSchema.partial():
        // omitted keys keep their stored value, so callers send only what changed.
        data: Partial<CreateManualBookingPayload> & {
          attempt_cancel_charge?: boolean
        }
      }
    >({
      query: ({ org_id, serial, data, booking_id }) => {
        return {
          url: `/${org_id}/${serial}/bookings/manual/${booking_id}`,
          method: 'PUT',
          data: {
            ...data,
          },
        }
      },

      invalidatesTags: (_1, _2, { booking_id }) => [
        { type: 'daily-stats' },
        { type: 'activity', id: booking_id },
        { type: 'bookings', id: 'List' },
        { type: 'cover-counts', id: 'LIST' },
        { type: 'daily-stats-new', id: 'LIST' },
      ],

      onQueryStarted: async (
        { org_id, serial, booking_id, data },
        { dispatch, queryFulfilled, getState }
      ) => {
        // Social bookings (ARD A5 4.2, A5-5): a night's save names activities, not the
        // envelope, so there is nothing to patch here; the invalidated refetch draws it.
        const nightSave = data as { segments?: unknown; removed_segment_ids?: unknown }
        if (!data.start_time || nightSave.segments !== undefined || nightSave.removed_segment_ids !== undefined) return

        let end_time = add(new Date(data.start_time), {
          seconds: differenceInSeconds(
            new Date(data.end_time),
            new Date(data.start_time)
          ),
        }).toISOString()

        if (data.duration) {
          end_time = add(new Date(data.start_time), {
            seconds: data.duration,
          }).toISOString()
        }

        const updated_data = { ...data, end_time }
        const patches: ReturnType<typeof dispatch>[] = []

        const patchResult = dispatch(
          zionApi.util.updateQueryData(
            'getBookingsByDate',
            {
              org_id,
              serial,
              date: format(new Date(data.start_time), 'yyyy-MM-dd'),
            },
            (draft: any) => {
              const index = draft.findIndex(
                (booking: any) => booking?.id === booking_id
              )
              if (index !== -1) {
                draft[index] = {
                  ...draft[index],
                  ...updated_data,
                  startDate: new Date(updated_data.start_time),
                  endDate: new Date(updated_data.end_time),
                }
              }
            }
          )
        )
        patches.push(patchResult)

        // Also update V2 query for fast day planner
        const patchResultV2 = dispatch(
          zionApi.util.updateQueryData(
            'getBookingsByDateV2',
            {
              org_id,
              serial,
              date: format(new Date(data.start_time), 'yyyy-MM-dd'),
            },
            (draft: any) => {
              const index = draft.findIndex(
                (booking: any) => booking?.id === booking_id
              )
              if (index !== -1) {
                draft[index] = {
                  ...draft[index],
                  ...updated_data,
                  startDate: new Date(updated_data.start_time),
                  endDate: new Date(updated_data.end_time),
                  booking_tables:
                    data.booking_tables || draft[index].booking_tables,
                }
              }
            }
          )
        )
        patches.push(patchResultV2)

        // Also update any cached getBookingsFastView queries that might contain this booking
        const searchQueries = zionApi.util.selectCachedArgsForQuery(
          getState(),
          'getBookingsFastView'
        )

        for (const queryArgs of searchQueries) {
          const searchPatch = dispatch(
            zionApi.util.updateQueryData(
              'getBookingsFastView',
              queryArgs,
              (draft: any) => {
                if (draft.data) {
                  const bookingIndex = draft.data.findIndex(
                    (booking: any) => booking?.id === booking_id
                  )
                  if (bookingIndex !== -1) {
                    draft.data[bookingIndex] = {
                      ...draft.data[bookingIndex],
                      ...updated_data,
                      start_time: updated_data.start_time,
                      end_time: updated_data.end_time,
                      booking_tables:
                        data.booking_tables ||
                        draft.data[bookingIndex].booking_tables,
                      // Also update tables field to match booking_tables for search modal display
                      tables:
                        data.booking_tables ||
                        draft.data[bookingIndex].booking_tables ||
                        draft.data[bookingIndex].tables,
                      // Preserve any transformed fields that might exist
                      startDate: new Date(updated_data.start_time),
                      endDate: new Date(updated_data.end_time),
                    }
                  }
                }
              }
            )
          )
          patches.push(searchPatch)
        }

        try {
          await queryFulfilled
        } catch {
          // Revert all optimistic updates on error
          patches.forEach((patch: any) => patch.undo())
        }
      },
    }),

    updateBookingStatusFastView: build.mutation<
      {
        message: string
        data: Booking
        previous_status: string
        updated_status: string
      },
      {
        org_id: string
        serial: string
        booking_id: string
        status: 'active' | 'seated' | 'complete'
        date: string // Add date parameter for cache updates
      }
    >({
      query: ({ org_id, serial, booking_id, status }) => ({
        url: `/${org_id}/${serial}/fast-view/bookings/${booking_id}/status`,
        method: 'PUT',
        data: { status },
      }),
      // Removed invalidatesTags to prevent cache invalidation that overwrites optimistic updates
      // The onQueryStarted optimistic updates handle the UI state changes
      async onQueryStarted(
        { org_id, serial, booking_id, status, date },
        { dispatch, queryFulfilled }
      ) {
        // Store patch results for potential rollback
        const patchResults: any[] = []

        // Update all the fast-view booking caches optimistically
        try {
          // Update active bookings cache
          const activePatch = dispatch(
            zionApi.util.updateQueryData(
              'getActiveBookings',
              { org_id, serial, date },
              (draft: any) => {
                if ('data' in draft && Array.isArray(draft.data)) {
                  const bookingIndex = draft.data.findIndex(
                    (booking: any) => booking?.id === booking_id
                  )
                  if (bookingIndex !== -1) {
                    if (status === 'active') {
                      // Update status if staying in active
                      draft.data[bookingIndex].status = status
                    } else {
                      // Remove from active if moving to seated/complete
                      draft.data.splice(bookingIndex, 1)
                    }
                  } else if (status === 'active') {
                    // This booking might be moving TO active from another status
                    // We'll add a placeholder that will be corrected by the server response
                    // The invalidatesTags will ensure fresh data
                  }
                }
              }
            )
          )
          patchResults.push(activePatch)

          // Update seated bookings cache
          const seatedPatch = dispatch(
            zionApi.util.updateQueryData(
              'getSeatedBookings',
              { org_id, serial, date },
              (draft: any) => {
                if ('data' in draft && Array.isArray(draft.data)) {
                  const bookingIndex = draft.data.findIndex(
                    (booking: any) => booking?.id === booking_id
                  )
                  if (bookingIndex !== -1) {
                    if (status === 'seated') {
                      // Update status if staying in seated
                      draft.data[bookingIndex].status = status
                    } else {
                      // Remove from seated if moving to active/complete
                      draft.data.splice(bookingIndex, 1)
                    }
                  }
                }
              }
            )
          )
          patchResults.push(seatedPatch)

          // Update complete bookings cache
          const completePatch = dispatch(
            zionApi.util.updateQueryData(
              'getCompleteBookings',
              { org_id, serial, date },
              (draft: any) => {
                if ('data' in draft && Array.isArray(draft.data)) {
                  const bookingIndex = draft.data.findIndex(
                    (booking: any) => booking?.id === booking_id
                  )
                  if (bookingIndex !== -1) {
                    if (status === 'complete') {
                      // Update status if staying in complete
                      draft.data[bookingIndex].status = status
                    } else {
                      // Remove from complete if moving to active/seated
                      draft.data.splice(bookingIndex, 1)
                    }
                  }
                }
              }
            )
          )
          patchResults.push(completePatch)

          // Update search results cache if it exists
          const searchPatch = dispatch(
            zionApi.util.updateQueryData(
              'getBookingsFastView',
              { org_id, serial, date, search: '' },
              (draft: any) => {
                if ('data' in draft && Array.isArray(draft.data)) {
                  const bookingIndex = draft.data.findIndex(
                    (booking: any) => booking?.id === booking_id
                  )
                  if (bookingIndex !== -1) {
                    draft.data[bookingIndex].status = status
                  }
                }
              }
            )
          )
          patchResults.push(searchPatch)

          await queryFulfilled
        } catch {
          // Undo all optimistic updates on error
          patchResults.forEach((patchResult: any) => {
            if (patchResult?.undo) {
              patchResult.undo()
            }
          })
        }
      },
    }),

    fetchManualPayments: build.query<
      ManualPayment[],
      {
        org_id: string
        serial: string
        booking_id: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/manual-payments`,
        method: 'GET',
      }),
      providesTags: (res, error, { booking_id }) => {
        return [{ id: booking_id, type: 'manual-payments' }]
      },
    }),
    createManualPaymentIntent: build.query<
      stripe.PaymentIntentResult['paymentIntent'],
      {
        org_id: string
        serial: string
        booking_id: string
        data: {
          email: string
        }
      }
    >({
      query: ({ org_id, serial, data, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/manual-payments/payment-intent`,
        method: 'POST',
        data,
      }),
    }),
    createManualPaymentCharge: build.mutation<
      any,
      {
        org_id: string
        serial: string
        booking_id: string
        data: {
          email: string
          payment_method_id: string
          amount: number | undefined
          internal_note: string | undefined
          email_note_to_customer: string | undefined
          currency: string | undefined
          charge_card_now: boolean
          payment_intent_id: string
          email_confirmation: boolean
        }
      }
    >({
      query: ({ org_id, serial, data, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/manual-payments/charge`,
        method: 'POST',
        data,
      }),
    }),
    createManualPayment: build.mutation<
      ManualPayment,
      {
        org_id: string
        serial: string
        booking_id: string
        data: CreateManualPaymentPayload
      }
    >({
      query: ({ org_id, serial, data, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/manual-payments`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (k, t, { booking_id }) => [
        {
          type: 'manual-payments',
          id: booking_id,
        },
      ],
    }),
    // Taking a card over the phone. These are the guest payment page's own two
    // calls, so a card entered by staff lands exactly where a card entered by the
    // guest would. Never retried: a retry is another attempt on the guest's card.
    createGuestCardSetupIntent: build.mutation<
      {
        success: boolean
        data?: { client_secret: string; status: string }
        message?: string
      },
      {
        org_id: string
        serial: string
        data: {
          email: string
          payment_method_id: string
          first_name: string
          last_name: string
          phone: string
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/public/${org_id}/${serial}/v2/stripe/setup-intent`,
        method: 'POST',
        data,
      }),
      extraOptions: { maxRetries: 0 },
    }),
    // A deposit taken over the phone is first held for its real amount, so the
    // guest's bank asks them to approve that figure rather than the £0.00 of a
    // card save. Settling it (below, with payment_intent_id) captures the hold.
    authoriseManualDepositWithCard: build.mutation<
      {
        success: boolean
        data?: {
          payment_intent_id: string
          client_secret: string
          status: string
          amount: number
        }
        error?: string
      },
      {
        org_id: string
        serial: string
        booking_id: string
        payment_id: string
        data: { payment_method_id: string }
      }
    >({
      query: ({ org_id, serial, booking_id, payment_id, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/manual-payments/${payment_id}/authorise`,
        method: 'POST',
        data,
      }),
      extraOptions: { maxRetries: 0 },
    }),
    processManualPaymentWithCard: build.mutation<
      { success: boolean; message?: string },
      {
        org_id: string
        serial: string
        booking_id: string
        payment_id: string
        data: {
          payment_method_id: string
          type: ManualPayment['type']
          amount: number
          /** A deposit held by authoriseManualDepositWithCard, to capture. */
          payment_intent_id?: string
        }
      }
    >({
      // The dashboard route first: the same settle step as the guest's
      // payment page, behind Auth, so the booking's log names who took the
      // card. A 404 means a zion that doesn't have it yet (nothing was
      // charged), so fall back to the page's public route, which settles the
      // payment identically. Drop the fallback once zion has shipped it.
      async queryFn(
        { org_id, serial, booking_id, payment_id, data },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const body = { payment_id, ...data }
        const staff = await baseQuery({
          url: `/${org_id}/${serial}/bookings/manual/${booking_id}/manual-payments/${payment_id}/process`,
          method: 'POST',
          data: body,
        })
        const staffError: unknown = staff.error
        const notDeployed =
          typeof staffError === 'object' &&
          staffError !== null &&
          'status' in staffError &&
          staffError.status === 404
        const result = notDeployed
          ? await baseQuery({
              url: `/public/${org_id}/${serial}/v2/bookings/${booking_id}/payments/${payment_id}/process`,
              method: 'POST',
              data: body,
            })
          : staff
        return result as
          | { data: { success: boolean; message?: string } }
          | { error: unknown }
      },
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (k, t, { booking_id }) => [
        { type: 'manual-payments', id: booking_id },
        { type: 'bookings', id: booking_id },
      ],
    }),
    deleteManualPayment: build.mutation<
      {},
      {
        org_id: string
        serial: string
        booking_id: string
        manual_payment_id: string
      }
    >({
      query: ({ org_id, serial, booking_id, manual_payment_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/manual-payments/${manual_payment_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (k, t, { booking_id }) => [
        {
          type: 'manual-payments',
          id: booking_id,
        },
      ],
    }),
    resendConfirmationManualPayment: build.mutation<
      {},
      {
        org_id: string
        serial: string
        booking_id: string
        manual_payment_id: string
      }
    >({
      query: ({ org_id, serial, booking_id, manual_payment_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/manual-payments/${manual_payment_id}/confirmation-email`,
        method: 'POST',
        data: {},
      }),
    }),

    // Booking invoicing (Square) endpoints — identical shape to the
    // enquiry-scoped invoicing endpoints above, mounted under
    // `bookings/manual` alongside manual payments.
    getBookingInvoices: build.query<
      { invoices: Invoice[] },
      { org_id: string; serial: string; booking_id: string }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/invoices`,
        method: 'GET',
      }),
      providesTags: (res, error, { booking_id }) => [
        { type: 'booking-invoices', id: booking_id },
      ],
    }),

    createBookingInvoice: build.mutation<
      { message: string; invoice: Invoice },
      {
        org_id: string
        serial: string
        booking_id: string
        data: CreateInvoicePayload
      }
    >({
      query: ({ org_id, serial, booking_id, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/invoices`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (res, error, { booking_id }) => [
        { type: 'booking-invoices', id: booking_id },
      ],
    }),

    publishBookingInvoice: build.mutation<
      { message: string; invoice: Invoice },
      { org_id: string; serial: string; booking_id: string; invoice_id: string }
    >({
      query: ({ org_id, serial, booking_id, invoice_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/invoices/${invoice_id}/publish`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: (res, error, { booking_id }) => [
        { type: 'booking-invoices', id: booking_id },
      ],
    }),

    cancelBookingInvoice: build.mutation<
      { message: string; invoice: Invoice },
      { org_id: string; serial: string; booking_id: string; invoice_id: string }
    >({
      query: ({ org_id, serial, booking_id, invoice_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/invoices/${invoice_id}/cancel`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: (res, error, { booking_id }) => [
        { type: 'booking-invoices', id: booking_id },
      ],
    }),

    getSquareInvoicingCapability: build.query<
      SquareInvoicingCapability,
      { org_id: string; serial: string; config_id: string }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/square-invoicing-capability`,
        method: 'GET',
      }),
      providesTags: (res, error, { config_id }) => [
        { type: 'square-invoicing-capability', id: config_id },
      ],
    }),

    getCancellationChargeCheck: build.query<
      {
        can_charge: boolean
        amount: number
        reason: string
        already_charged: boolean
      },
      {
        org_id: string
        serial: string
        booking_id: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/cancellation-charge-check`,
        method: 'GET',
      }),
    }),

    getCalculateCharge: build.query<
      {
        has_deposit_charge: boolean
        deposit_charge_amount: number
        deposit_charge_type: string
        has_cancel_charge: boolean
        cancel_charge_amount: number
        /** Social bookings (A2 4.4). A priced type prefills from pricePreview instead. */
        payment_mode?: BookingPaymentMode
        price_basis?: PriceBasis | null
        /** Pence; null for a priced type. */
        full_payment_total?: number | null
      },
      {
        org_id: string
        serial: string
        date: string
        party_size: number
        booking_type_id?: string
        event_id?: string
      }
    >({
      query: ({
        org_id,
        serial,
        date,
        party_size,
        booking_type_id,
        event_id,
      }) => {
        const params = new URLSearchParams({
          date,
          party_size: String(party_size),
        })
        if (booking_type_id) params.append('booking_type_id', booking_type_id)
        if (event_id) params.append('event_id', event_id)
        return {
          url: `/${org_id}/${serial}/bookings/manual/calculate-charge?${params.toString()}`,
          method: 'GET',
        }
      },
    }),

    updateBookingStatus: build.mutation<
      Booking,
      {
        org_id: string
        serial: string
        booking_id: string
        status:
          | 'provisionally_held_by_enquiry'
          | 'active'
          | 'table_ready'
          | 'arrived'
          | 'partially_seated'
          | 'seated'
          | 'starter'
          | 'main'
          | 'appetizer'
          | 'entree'
          | 'dessert'
          | 'cleared'
          | 'check_dropped'
          | 'paid'
          | 'cancelled'
          | 'no_show'
          | 'complete'
        send_notification?: boolean
        apply_cancellation_charge?: boolean
        // Staff-chosen charge amount (pence), for when fewer guests showed
        // up than the party size the charge was agreed against. Omit to
        // charge the full agreed amount; the server clamps this to it
        // regardless.
        cancellation_charge_amount?: number
        neo_order_action?: 'detach' | 'reassign'
        reassign_to_booking_id?: string
        date?: string // For optimistic update of getBookingsByDateV2
      }
    >({
      query: ({
        org_id,
        serial,
        booking_id,
        status,
        send_notification,
        apply_cancellation_charge,
        cancellation_charge_amount,
        neo_order_action,
        reassign_to_booking_id,
      }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/status`,
        method: 'PUT',
        data: {
          status,
          send_notification,
          apply_cancellation_charge,
          cancellation_charge_amount,
          neo_order_action,
          reassign_to_booking_id,
        },
      }),
      // Optimistic update for getBookingsByDateV2 and search results
      async onQueryStarted(
        { org_id, serial, booking_id, status, date },
        { dispatch, queryFulfilled, getState }
      ) {
        const patches: ReturnType<typeof dispatch>[] = []

        // Update getBookingsByDateV2 if we have a date
        if (date) {
          patches.push(
            dispatch(
              zionApi.util.updateQueryData(
                'getBookingsByDateV2',
                { org_id, serial, date },
                (draft: any) => {
                  const bookingIndex = draft.findIndex(
                    (booking: any) => booking?.id === booking_id
                  )

                  if (bookingIndex !== -1) {
                    // Update the status while preserving all other fields including transformed fields
                    draft[bookingIndex] = {
                      ...draft[bookingIndex],
                      status,
                      // Ensure transformed fields are preserved if they exist
                      startDate:
                        draft[bookingIndex].startDate ||
                        parseISO(draft[bookingIndex].start_time),
                      endDate:
                        draft[bookingIndex].endDate ||
                        parseISO(draft[bookingIndex].end_time),
                    }
                  }
                }
              )
            )
          )
        }

        // Also update any cached search results that might contain this booking
        const searchQueries = zionApi.util.selectCachedArgsForQuery(
          getState(),
          'getBookingsFastView'
        )

        for (const queryArgs of searchQueries) {
          const searchPatch = dispatch(
            zionApi.util.updateQueryData(
              'getBookingsFastView',
              queryArgs,
              (draft: any) => {
                if (draft.data) {
                  const bookingIndex = draft.data.findIndex(
                    (booking: any) => booking?.id === booking_id
                  )
                  if (bookingIndex !== -1) {
                    draft.data[bookingIndex] = {
                      ...draft.data[bookingIndex],
                      status,
                    }
                  }
                }
              }
            )
          )
          patches.push(searchPatch)
        }

        try {
          await queryFulfilled
        } catch (error) {
          // Revert all optimistic updates on error
          patches.forEach((patch: any) => patch.undo())
        }
      },
      // Invalidate relevant caches
      invalidatesTags: (result, error, { booking_id }) => [
        { type: 'bookings', id: booking_id },
        { type: 'bookings', id: 'List' },
        { type: 'activity', id: booking_id },
      ],
    }),
    reassignOrder: build.mutation<
      { source: Booking; destination: Booking },
      {
        org_id: string
        serial: string
        source_booking_id: string
        destination_booking_id: string
        date?: string
      }
    >({
      query: ({
        org_id,
        serial,
        source_booking_id,
        destination_booking_id,
      }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${source_booking_id}/reassign-order`,
        method: 'POST',
        data: { destination_booking_id },
      }),
      async onQueryStarted(
        { org_id, serial, source_booking_id, destination_booking_id, date },
        { dispatch, queryFulfilled }
      ) {
        const patches: ReturnType<typeof dispatch>[] = []

        if (date) {
          patches.push(
            dispatch(
              zionApi.util.updateQueryData(
                'getBookingsByDateV2' as any,
                { org_id, serial, date } as any,
                (draft: any) => {
                  const source = draft?.find(
                    (b: any) => b.id === source_booking_id
                  )
                  const dest = draft?.find(
                    (b: any) => b.id === destination_booking_id
                  )
                  if (source) {
                    const orderId = source.latest_neo_order_id
                    source.has_neo_orders = false
                    source.latest_neo_order_id = null
                    source.ignore_order_tables = false
                    if (dest) {
                      dest.has_neo_orders = true
                      dest.latest_neo_order_id = orderId
                    }
                  }
                }
              )
            )
          )
        }

        try {
          await queryFulfilled
        } catch (error) {
          patches.forEach((patch: any) => patch.undo())
        }
      },
      invalidatesTags: (result, error, { source_booking_id }) => [
        { type: 'bookings', id: source_booking_id },
        { type: 'bookings', id: 'List' },
        { type: 'daily-stats' },
        { type: 'cover-counts', id: 'LIST' },
      ],
    }),
    getUserBookingsCount: build.query<
      { previous: number },
      { org_id: string; serial: string; email: string }
    >({
      query: ({ org_id, serial, email }) => ({
        url: `/${org_id}/${serial}/bookings/user/${email}/count`,
        method: 'GET',
      }),
    }),
    getBookingsByDate: build.query<
      Booking[],
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/bookings?date=${date}`,
        method: 'GET',
      }),
      transformResponse: (items: Booking[]) => {
        if (!items) return []

        const map = new Map<string, Booking>()
        for (const item of items) {
          map.set(item?.id!, item)
        }
        return Array.from(map.values())
          .map((item: any) => ({
            ...item,
            startDate: parseISO(item?.start_time),
            endDate: parseISO(item?.end_time),
          }))
          .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
      },
      providesTags: () => [{ type: 'bookings', id: 'List' }],
    }),
    getBookingsByDateV2: build.query<
      Booking[],
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/bookings/v2?date=${date}`,
        method: 'GET',
      }),
      transformResponse: (items: Booking[]) => {
        if (!items) return []

        const map = new Map<string, Booking>()
        for (const item of items) {
          map.set(item?.id!, item)
        }

        // Force a new array reference by using [...array]
        const result = [...Array.from(map.values())]
          .map((item: any) => ({
            ...item,
            startDate: parseISO(item?.start_time),
            endDate: parseISO(item?.end_time),
          }))
          .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())

        return result
      },
      providesTags: () => [{ type: 'bookings', id: 'List' }],
    }),
    getDailyStats: build.query<
      DailyStatsNew,
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/bookings/daily-stats?date=${date}`,
        method: 'GET',
      }),
      providesTags: (response: any) => [
        { type: 'daily-stats-new', id: `LIST` },
      ],
    }),
    getDailyAiReport: build.query<
      DailyAiReport | null,
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/bookings/daily-ai-report?date=${date}`,
        method: 'GET',
      }),
      providesTags: (response: any) => [
        { type: 'daily-ai-report', id: `LIST` },
      ],
    }),
    generateDailyAiReport: build.mutation<
      DailyAiReport,
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/bookings/daily-ai-report?date=${date}`,
        method: 'PUT',
        data: {
          date,
        },
      }),
      invalidatesTags: (result, error) => [
        { type: 'daily-ai-report', id: 'LIST' },
      ],
    }),
    getAISettings: build.query<
      AISettings,
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/ai-settings`,
        method: 'GET',
      }),
      providesTags: (result, error, { config_id }) => [
        { type: 'ai-settings', id: config_id },
      ],
    }),
    updateAISettings: build.mutation<
      AISettings,
      {
        org_id: string
        serial: string
        config_id: string
        data: Partial<AISettings>
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/ai-settings`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { config_id }) => [
        { type: 'ai-settings', id: config_id },
      ],
    }),
    getBookingExtraDetails: build.query<
      Booking & {
        booking_deposits?: {
          id: string
          paid_amount: number
          // Refund counters. Zion returns these because the /extra handler uses
          // a Prisma `include`, which yields every scalar on the relation. They
          // are declared here so that stays a contract rather than a happy
          // accident: narrowing that `include` to a `select` without listing
          // them would leave the refund dialog reading 0 and showing a fully
          // refunded deposit as still refundable.
          refunded_amount?: number
          refund_pending_amount?: number
          transactions?: {
            id: string
            amount: number
            stripe_payment_method?: {
              id: string
              type: string
              card?: {
                brand: string
                last4: string
              }
            }
          }[]
        }[]
        // Full payment (FULL_PAYMENT_ARD.md D23). Same include contract as
        // booking_deposits above, so the refund counters are real.
        full_payments?: {
          id: string
          total_amount: number
          immediate_amount: number
          deferred_amount: number
          paid_amount: number
          refunded_amount?: number
          refund_pending_amount?: number
          status: string
          due_at: string | null
          transactions?: { id: string; amount: number }[]
        }[]
        cancellation_charges?: {
          id: string
          amount?: number
          paid_amount?: number
          charged_amount?: number
          // See the note on booking_deposits above.
          refunded_amount?: number
          refund_pending_amount?: number
          cancellation_transactions?: {
            id: string
            amount: number
            stripe_payment_method?: {
              id: string
              type: string
              card?: {
                brand: string
                last4: string
              }
            }
          }[]
        }[]
        user_invitations?: {
          status: string
          user?: {
            last_name: string
            first_name: string
            email: string
            phone?: string
            core_user_profile_id?: string
          }
        }[]
        user_profile_bookings?: {
          user_profile?: {
            id: string
            email: string
            first_name: string
            last_name: string
            last_interacted_at?: string
            core_user_profile_id?: string
            stripe_customers?: {
              cards?: {
                id: string
                brand: string
                last4: string
              }[]
            }[]
          }
        }[]
        internal_booking_messages?: {
          content: string
          created_at: string
        }[]
        issued_automation?: {
          automation_configuration?: {
            automationSource?: {
              name: string
            }
          }
        }
        neo_orders?: { booking_id: string; neo_order_id: string }[]
        orders?: {
          id: string
          type: string
          order_status: string
          order_value_face_value: number
          accepting_changes: boolean
          created_at: string
          menus: { id: string; name: string }[]
          order_items: {
            id: string
            quantity: number
            price: number
            special_instructions?: string
            menu_item: {
              id: string
              name: string
              price: number
              img?: string
            }
            modifiers?: { id: string; name: string; price: number }[]
            participant?: {
              id: string
              user?: {
                id: string
                first_name: string
                last_name: string
                email?: string
              }
            }
          }[]
          participants?: {
            id: string
            creator: boolean
            participant: boolean
            user?: {
              id: string
              first_name: string
              last_name: string
              email?: string
            }
          }[]
          transactions?: {
            id: string
            total_amount: number
            status: string
            type: string
            created_at: string
            user_profile?: {
              id: string
              first_name: string
              last_name: string
              email?: string
            }
          }[]
        }[]
        storekit_orders?: {
          id: string
          storekit_order_id: string
          order_code: string | null
          status: string
          order_type: string
          total: number
          tip: number
          table_name: string | null
          table_covers: number | null
          customer_email: string | null
          customer_first_name: string | null
          customer_last_name: string | null
          storekit_created_at: string | null
          payload?: {
            id: string
            tip: number
            asap: boolean
            code: string
            items: {
              plu: string | null
              name: string
              note: string
              posId: string | null
              price: number
              taxRate: number | null
              quantity: number
              modifiers: {
                name: string
                price: number
              }[]
            }[]
            notes: string
            table: {
              id: string
              name: string
              covers: number | null
              area: {
                id: string
                name: string
              }
            }
            total: number
            customer: {
              email: string
              phone: string
              firstName: string
              marketingConsent: boolean
            }
            createdAt: string
            orderType: string
            deliveryFee: number
            discountTotal: number
          }
        }[]
      },
      {
        org_id: string
        serial: string
        booking_id: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/extra`,
        method: 'GET',
        headers: {
          'Accept-Encoding': 'gzip',
        },
      }),
      providesTags: (result, error, { booking_id }) => [
        { type: 'bookings', id: booking_id },
      ],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),
    getDurationFromPartySize: build.query<
      { duration: number },
      {
        org_id: string
        serial: string
        config_id: string
        party_size: number
        booking_type_id?: string | undefined
      }
    >({
      query: ({ org_id, serial, config_id, party_size, booking_type_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/party-size-duration/${party_size}?${booking_type_id ? `booking_type_id=${booking_type_id}` : ''}`,
        method: 'GET',
      }),
    }),
    getBookingActivityLog: build.query<
      ActivityLogs,
      {
        org_id: string
        serial: string
        booking_id: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/activity-log`,
        method: 'GET',
      }),
      providesTags: (_, _1, { booking_id }) => [
        { type: 'activity', id: booking_id },
      ],
    }),
    getBooking: build.query<
      Booking & { startDate: Date; endDate: Date },
      {
        org_id: string
        serial: string
        booking_id: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}`,
        method: 'GET',
      }),
      transformResponse: (
        item: Booking & { startDate: Date; endDate: Date }
      ) => {
        const res = {
          ...item,
          allergens: item?.allergens?.length ? item?.allergens : [],
          // Handle booking_tables - safely check if it exists and handle different formats
          booking_tables: item?.booking_tables
            ? Array.isArray(item?.booking_tables)
              ? typeof item?.booking_tables[0] === 'string'
                ? item?.booking_tables
                : item?.booking_tables.map(
                    (bt: any) => bt.table_id || bt.id || bt
                  )
              : []
            : [],
          duration:
            item?.end_time && item?.start_time
              ? differenceInSeconds(
                  new Date(item?.end_time),
                  new Date(item?.start_time)
                )
              : 0,
        }
        //@ts-ignore
        return res
      },
      providesTags: (result, error, { booking_id }) => [
        { type: 'bookings', id: booking_id },
      ],
    }),
    getBookingsDailyStats: build.query<
      { online: number; internal: number; pre_date: number; walk_in: number },
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/bookings/stats/daily?date=${date}`,
        method: 'GET',
      }),
      providesTags: (result, error, { org_id, serial, date }) => [
        { type: 'daily-stats', org_id, serial, date },
      ],
    }),

    swapBookings: build.mutation<
      Booking[],
      {
        org_id: string
        serial: string
        data: SwapManual
        updated_source_tables: string[]
        updated_target_tables: string[]
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual/swap`,
        method: 'PUT',
        data: {
          ...data,
        },
      }),
      onQueryStarted: async (
        { org_id, serial, data, updated_source_tables, updated_target_tables },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          zionApi.util.updateQueryData(
            'getBookingsByDate',
            {
              org_id,
              serial,
              date: format(new Date(data.new_start), 'yyyy-MM-dd'),
            },
            (draft: any) => {
              const index = draft.findIndex(
                (booking: any) => booking?.id === data.booking_source_id
              )
              if (index !== -1) {
                draft[index] = {
                  ...draft[index],
                  start_time: data.new_start,
                  end_time: data.new_end,
                  booking_tables: updated_source_tables,
                }
              }

              const index_2 = draft.findIndex(
                (booking: any) => booking?.id === data.booking_target_id
              )
              if (index !== -1) {
                draft[index_2] = {
                  ...draft[index_2],
                  booking_tables: updated_target_tables,
                }
              }
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    getBookingNotes: build.query<
      Internal_Booking_Note[],
      {
        org_id: string
        serial: string
        booking_id: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/notes`,
        method: 'GET',
      }),

      providesTags: (result: any) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'notes',
                id: (id as string | undefined) || undefined,
              })),
              { type: 'notes', id: 'LIST' },
            ]
          : [{ type: 'notes', id: 'LIST' }],
    }),
    createBookingNotes: build.mutation<
      Internal_Booking_Note,
      {
        org_id: string
        serial: string
        booking_id: string
        data: {
          content: string
        }
      }
    >({
      query: ({ org_id, serial, booking_id, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/notes`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, _1, { booking_id }) => [
        { type: 'notes', id: 'LIST' },
        { type: 'activity', id: booking_id },
        { type: 'bookings', id: booking_id },
        { type: 'bookings', id: 'List' },
      ],
    }),
    updateBookingNotes: build.mutation<
      Internal_Booking_Note,
      {
        org_id: string
        serial: string
        booking_id: string
        note_id: string
        data: {
          content: string
        }
      }
    >({
      query: ({ org_id, serial, booking_id, note_id, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/notes/${note_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { note_id, booking_id }) => [
        { type: 'notes', id: note_id },
        { type: 'activity', id: booking_id },
        { type: 'bookings', id: booking_id },
        { type: 'bookings', id: 'List' },
      ],
    }),
    getCloseOutsTimeAndTables: build.query<
      CloseOut[],
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/bookings/manual/close-outs?date=${date}`,
        method: 'GET',
      }),
      providesTags: (result: any) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'close-outs',
                id: (id as string | undefined) || undefined,
              })),
              { type: 'close-outs', id: 'LIST' },
            ]
          : [{ type: 'close-outs', id: 'LIST' }],
    }),
    createCloseOut: build.mutation<
      CloseOut,
      {
        org_id: string
        serial: string
        data: {
          id: string
          start_time: string
          end_time: string
          tables: string[]
          dates?: string[]
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual/close-outs`,
        method: 'POST',
        data,
      }),

      onQueryStarted: async (
        { org_id, serial, data },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          zionApi.util.updateQueryData(
            'getCloseOutsTimeAndTables',
            {
              org_id,
              serial,
              date: format(new Date(data.start_time), 'yyyy-MM-dd'),
            },
            (draft: any) => {
              return [...draft, data as any]
            }
          )
        )
        try {
          const { data: created_close_out } = await queryFulfilled
          dispatch(
            zionApi.util.updateQueryData(
              'getCloseOutsTimeAndTables',
              {
                org_id,
                serial,
                date: format(new Date(data.start_time), 'yyyy-MM-dd'),
              },
              (draft: any) => {
                return draft.map((close_out: any) => {
                  if (created_close_out?.id === close_out.id) {
                    return created_close_out
                  }
                  return close_out
                })
              }
            )
          )
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: (result, error) => [{ type: 'close-outs', id: 'LIST' }],
    }),
    updateCloseOut: build.mutation<
      CloseOut,
      {
        org_id: string
        serial: string
        data: {
          id: string
          start_time: string
          end_time: string
          tables: string[]
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/bookings/manual/close-outs/${data.id}`,
        method: 'PUT',
        data,
      }),
      onQueryStarted: async (
        { org_id, serial, data },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          zionApi.util.updateQueryData(
            'getCloseOutsTimeAndTables',
            {
              org_id,
              serial,
              date: format(new Date(data.start_time), 'yyyy-MM-dd'),
            },
            (draft: any) => {
              if (draft) {
                return Array.from(
                  new Set(
                    [...draft, data as CloseOut].map(
                      (closeOut: any) => closeOut.id
                    )
                  )
                ).map(
                  (id: any) =>
                    [...draft, data as CloseOut].find(
                      (closeOut: any) => closeOut?.id === id
                    )!
                )
              }
            }
          )
        )
        try {
          const { data: created_close_out } = await queryFulfilled
          dispatch(
            zionApi.util.updateQueryData(
              'getCloseOutsTimeAndTables',
              {
                org_id,
                serial,
                date: format(new Date(data.start_time), 'yyyy-MM-dd'),
              },
              (draft: any) => {
                return draft.map((close_out: any) => {
                  if (created_close_out?.id === close_out.id) {
                    return created_close_out
                  }
                  return close_out
                })
              }
            )
          )
        } catch {
          patchResult.undo()
        }
      },
    }),
    removeCloseOut: build.mutation<
      {},
      {
        org_id: string
        serial: string
        id: string
        date: string
      }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/close-outs/${id}`,
        method: 'delete',
      }),
      onQueryStarted: async (
        { org_id, serial, id, date },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          zionApi.util.updateQueryData(
            'getCloseOutsTimeAndTables',
            {
              org_id,
              serial,
              date: format(new Date(date), 'yyyy-MM-dd'),
            },
            (draft: any) => {
              return draft.filter((close_out: any) => close_out.id !== id)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    removeCloseOutBulk: build.mutation<
      {},
      {
        org_id: string
        serial: string
        activity_log_id: string
      }
    >({
      query: ({ org_id, serial, activity_log_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/close-outs/bulk/${activity_log_id}`,
        method: 'delete',
      }),
      invalidatesTags: [{ type: 'close-outs', id: 'LIST' }],
    }),
    /**
     * Apply a Schedule Mode save in one round-trip.
     *
     * Replaces dp3's old N-parallel-mutation fan-out on save —
     * `Promise.all([useRemoveCloseOutMutation × N])` then
     * `Promise.all([useCreateCloseOutMutation × M])`. Both passes are
     * collapsed into a single POST that the server commits inside one
     * Prisma transaction.
     *
     * Optimistic patch: the dp3 page already holds the working state
     * in `useScheduleEdits`, so the cache patch here just folds the
     * declared shape into the day's `getCloseOutsTimeAndTables`
     * result — adds the new entries, drops the deleted ids. On
     * settle we still refetch via the `'close-outs'` tag in case the
     * server normalised anything (e.g. expanded all-tables blocks).
     */
    bulkApplyCloseOuts: build.mutation<
      { created: number; deleted: number },
      {
        org_id: string
        serial: string
        date: string
        create: {
          id: string
          start_time: string
          end_time: string
          tables: string[]
        }[]
        delete: { id: string }[]
      }
    >({
      query: ({ org_id, serial, date, create, delete: deletes }) => ({
        url: `/${org_id}/${serial}/bookings/manual/close-outs/batch`,
        method: 'POST',
        // `date` MUST be in the body — the server uses it to
        // invalidate the read-side `close_outs:{org}:{serial}:{date}`
        // Redis cache after the bulk apply commits. Without it,
        // delete-only batches leave the cache stale and the refetch
        // resurrects the blocks the user just removed.
        data: { date, create, delete: deletes },
      }),
      onQueryStarted: async (
        { org_id, serial, date, create, delete: deletes },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          zionApi.util.updateQueryData(
            'getCloseOutsTimeAndTables',
            { org_id, serial, date },
            (draft: any) => {
              if (!Array.isArray(draft)) return
              const deletedIds = new Set(deletes.map((d) => d.id))
              const filtered = draft.filter((co: any) => !deletedIds.has(co.id))
              for (const c of create) {
                filtered.push({
                  id: c.id,
                  start_time: c.start_time,
                  end_time: c.end_time,
                  tables: c.tables,
                  // Activity log + oauth_user shapes are populated
                  // by the refetch that follows tag invalidation.
                })
              }
              return filtered
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: [{ type: 'close-outs', id: 'LIST' }],
    }),
    sendInvite: build.mutation<
      {},
      {
        org_id: string
        serial: string
        booking_id: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/manual/${booking_id}/resend`,
        method: 'POST',
      }),
    }),

    getOpenClose: build.query<
      { open: string; close: string; reasons?: string | null | undefined },
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/times/open-close?date=${date}`,
        method: 'GET',
      }),
    }),
    getReports: build.query<
      ReportingResponse,
      {
        org_id: string
        serial: string
        time_frame: TimeFrame
        date: string
        data_type: 'bookings' | 'covers'
      }
    >({
      query: ({ org_id, serial, date, data_type, time_frame }) => ({
        url: `/${org_id}/${serial}/reporting?date=${date}&time_frame=${time_frame}&data_type=${data_type}`,
        method: 'GET',
      }),
    }),
    getCustomerBookings: build.query<
      ServiceCursorPaginatedResponseType<Booking>,
      {
        org_id: string
        limit: number
        cursor: string
        profile_id: string | number
      }
    >({
      query: ({ org_id, profile_id, cursor, limit }) => ({
        url: buildUrl(`/${org_id}/user-profiles/${profile_id}/bookings`, {
          cursor,
          limit,
        }),
        method: 'GET',
      }),
    }),
    getCustomerBookingsCount: build.query<
      unknown,
      {
        org_id: string
        profile_id: string | number
      }
    >({
      query: ({ org_id, profile_id }) => ({
        url: `/${org_id}/user-profiles/${profile_id}/bookings-count`,
        method: 'GET',
      }),
    }),
    createReplies: build.query<
      ZionEvePromptReply,
      {
        org_id: string
        serial: string
        data: {
          first_name: string
          message: string
          context: 'booking_message_reply'
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/eve/prompts`,
        method: 'POST',
        data,
      }),
    }),
    modifyUserProfileSerialNote: build.mutation<
      {
        id: string
        note: string
        org_id: string
        serial: string
        user_profile_id: string
      },
      {
        org_id: string
        serial: string
        user_profile_id: string
        data: {
          note: string
        }
      }
    >({
      query: ({ org_id, serial, data, user_profile_id }) => ({
        url: `/${org_id}/${serial}/user-profiles/${user_profile_id}/note`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { user_profile_id }) => [
        { type: 'note', user_profile_id },
      ],
    }),
    getUserProfileSerialNote: build.query<
      {
        id: string
        note: string
        org_id: string
        serial: string
        user_profile_id: string
      },
      {
        org_id: string
        serial: string
        user_profile_id: string
      }
    >({
      query: ({ org_id, serial, user_profile_id }) => ({
        url: `/${org_id}/${serial}/user-profiles/${user_profile_id}/note`,
        method: 'GET',
      }),
      providesTags: (result, error, { user_profile_id }) => [
        { type: 'note', user_profile_id },
      ],
    }),
    getUserTags: build.query<
      {
        user_profile_id: string
        org_id: string
        tags: {
          id: string
          tag_name: string
          source: string
          metadata?: any
          created_at: string
        }[]
      },
      {
        org_id: string
        serial: string
        user_profile_id: string
        email?: string
      }
    >({
      query: ({ org_id, serial, user_profile_id, email }) => ({
        url: `/${org_id}/${serial}/user-profiles/${user_profile_id}/tags${email ? `?email=${encodeURIComponent(email)}` : ''}`,
        method: 'GET',
      }),
      providesTags: (result, error, { user_profile_id }) => [
        { type: 'vip' as any, id: `tags-${user_profile_id}` },
      ],
    }),
    getVipStatus: build.query<
      {
        user_profile_id: string
        org_id: string
        vip: boolean
        vip_tag_id: string | null
        // Square Loyalty guest-level cache (docs/square-loyalty/ARD.md ARD-016
        // in the neo repo) - a stable account reference, refreshed async at
        // booking time. Never the guest's live points/tier, only the pointer.
        square_loyalty_account_id: string | null
        square_loyalty_program_id: string | null
        square_loyalty_match_status:
          | 'PENDING'
          | 'MATCHED'
          | 'NOT_MATCHED'
          | 'AMBIGUOUS'
        square_loyalty_checked_at: string | null
      },
      {
        org_id: string
        serial: string
        user_profile_id: string
      }
    >({
      query: ({ org_id, serial, user_profile_id }) => ({
        url: `/${org_id}/${serial}/user-profiles/${user_profile_id}/vip`,
        method: 'GET',
      }),
      providesTags: (result, error, { user_profile_id }) => [
        { type: 'vip' as any, user_profile_id },
      ],
    }),
    setVipStatus: build.mutation<
      {
        user_profile_id: string
        org_id: string
        vip: boolean
        vip_tag_id: string | null
      },
      {
        org_id: string
        serial: string
        user_profile_id: string
        data: { vip: boolean }
      }
    >({
      query: ({ org_id, serial, user_profile_id, data }) => ({
        url: `/${org_id}/${serial}/user-profiles/${user_profile_id}/vip`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { user_profile_id }) => [
        { type: 'vip' as any, user_profile_id },
      ],
    }),
    getBookingInvites: build.query<
      ({
        user: {
          email: string
          first_name: string | null | undefined
          last_name: string | null | undefined
          phone: string | null | undefined
        }
      } & {
        id: string
        org_reg_id: string | null | undefined
        booking_id: string
        user_id: string
        status: InvitationStatus
        created_at: Date
        updated_at: Date | null
        deleted_at: Date | null
        relation: string
      })[],
      {
        org_id: string
        booking_id: string
        serial: string
      }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/public/${org_id}/${serial}/booking/${booking_id}/invitations`,
        method: 'GET',
      }),
    }),
    getBookingsFromRange: build.query<
      any,
      {
        org_id: string
        serial: string
        start: string
        end: string
        cursor: string
        limit: number
        serial_filter: string | null | undefined
      }
    >({
      query: ({
        org_id,
        serial,
        start,
        end,
        cursor,
        limit,
        serial_filter,
      }) => ({
        url: `/${org_id}/${serial}/reporting/bookings?start=${start}&end=${end}${cursor ? `&cursor=${cursor}` : ``}&limit=${limit}&serial=${serial_filter}`,
        method: 'GET',
      }),
    }),
    getBookingsCountsFromRange: build.query<
      Stat[],
      {
        org_id: string
        serial: string
        start: string
        end: string
        serial_filter: string | null | undefined
      }
    >({
      query: ({ org_id, serial, start, end, serial_filter }) => ({
        url: `/${org_id}/${serial}/reporting/bookings/counts?start=${start}&end=${end}&serial=${serial_filter}`,
        method: 'GET',
      }),
    }),
    getCoversCountsFromRange: build.query<
      {
        name: string | null | undefined
        bookings_total: number
        covers: number
        covers_active: number
        emoji: string
      }[],
      {
        org_id: string
        serial: string
        start: string
        end: string
      }
    >({
      query: ({ org_id, serial, start, end }) => ({
        url: `/${org_id}/${serial}/reporting/bookings/cover_counts?start=${start}&end=${end}`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'cover-counts', id: 'LIST' }],
    }),
    /**
     * What the phone agent booked, and what those guests spent.
     *
     * `range` is the window asked for, `all_time` is everything since the
     * agent started there. Calls are the operator's, not zion's, so this
     * carries no conversion figure: the page holds both halves.
     */
    getVoiceReport: build.query<
      {
        from: string
        to: string
        range: VoiceFigures
        all_time: VoiceFigures
        first_booking_at: string | null
        /** 'square' is measured, 'estimate' is covers x a per-head figure */
        spend_source: 'square' | 'estimate'
        estimate_per_head_pence: number
        spend_available: boolean
        currency: string | null
      },
      {
        org_id: string
        serial: string
        start: string
        end: string
        /** the dashboard knows the integration first-hand, so it says */
        has_square?: boolean
      }
    >({
      query: ({ org_id, serial, start, end, has_square }) => ({
        url: `/${org_id}/${serial}/reporting/voice?start=${start}&end=${end}${
          has_square === undefined ? '' : `&has_square=${has_square}`
        }`,
        method: 'GET',
      }),
    }),

    /**
     * The bookings behind the figures, a page at a time.
     *
     * `total` is every booking in the window, not just this page, so the table
     * can say "1-25 of 143" instead of making someone click until it stops.
     */
    getVoiceBookings: build.query<
      {
        bookings: VoiceBookingRow[]
        total: number
        limit: number
        offset: number
      },
      {
        org_id: string
        serial: string
        start: string
        end: string
        has_square?: boolean
        limit?: number
        offset?: number
      }
    >({
      query: ({ org_id, serial, start, end, has_square, limit, offset }) => ({
        url: `/${org_id}/${serial}/reporting/voice/bookings?start=${start}&end=${end}${
          has_square === undefined ? '' : `&has_square=${has_square}`
        }${limit === undefined ? '' : `&limit=${limit}`}${
          offset ? `&offset=${offset}` : ''
        }`,
        method: 'GET',
      }),
    }),
    getBDAScore: build.query<
      BDAScorePayload,
      {
        org_id: string
        serial: string
        start: string
        end: string
      }
    >({
      query: ({ org_id, serial, start, end }) => ({
        url: `/${org_id}/${serial}/retention?start=${start}&end=${end}`,
        method: 'GET',
      }),
    }),
    getAutomations: build.query<
      AutomationConfig[],
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/automations`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'automations', id: 'LIST' }],
    }),
    getAutomation: build.query<
      AutomationConfig,
      {
        org_id: string
        serial: string
        config_id: string
        automation_source_id: string
      }
    >({
      query: ({ org_id, serial, config_id, automation_source_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/automations/${automation_source_id}`,
        method: 'GET',
      }),
    }),
    getAutomationStats: build.query<
      BookingAutomationStat[],
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/automations/stats`,
        method: 'GET',
      }),
    }),
    modifyAutomation: build.mutation<
      AutomationConfig,
      {
        org_id: string
        serial: string
        config_id: string
        automation_source_id: string
        data: Partial<AutomationConfig>
      }
    >({
      query: ({ org_id, serial, config_id, automation_source_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/automations/${automation_source_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error) => [{ type: 'automations', id: 'LIST' }],
    }),
    sendAutomationPreview: build.mutation<
      { message: string },
      {
        org_id: string
        serial: string
        config_id: string
        automation_source_id: string
        data: any
      }
    >({
      query: ({ org_id, serial, config_id, automation_source_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/automations/${automation_source_id}/send-preview`,
        method: 'POST',
        data,
      }),
    }),
    getBookingTypeForTable: build.query<
      BookingType | null,
      {
        org_id: string
        serial: string
        table_id: string
        start_time: string
      }
    >({
      query: ({ org_id, serial, table_id, start_time }) => ({
        url: `/${org_id}/${serial}/tables/${table_id}/booking_type?start_time=${start_time}`,
        method: 'GET',
      }),
    }),

    staffleadrBoard: build.query<
      UserRanking[],
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/tables/oauth_users/leaderboard?date=${date}`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'leader_board', id: 'LIST' }],
    }),

    upsertStaffTables: build.mutation<
      TableAssignment,
      {
        org_id: string
        serial: string
        date: string
        data: {
          tables: string[]
          user: OrganisationUsersAccess
          config_id: string
        }
      }
    >({
      query: ({ org_id, serial, data, date }) => ({
        url: `/${org_id}/${serial}/tables/oauth_user?date=${date}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: () => [{ type: 'staff-assign-tables', id: 'LIST' }],
    }),

    fetchAlltStaffsTables: build.query<
      TableAssignment[],
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/tables/oauth_users?date=${date}`,
        method: 'GET',
      }),
      providesTags: (result, error) => [
        { type: 'staff-assign-tables', id: 'LIST' },
      ],
    }),

    getFloorPlans: build.query<
      FloorPlan[],
      {
        org_id: string
        serial: string
      }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/floorplans`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'floorplans', id: 'LIST' }],
    }),
    getFloorPlan: build.query<
      FloorPlan,
      {
        org_id: string
        serial: string
        floor_plan_id: string
      }
    >({
      query: ({ org_id, serial, floor_plan_id }) => ({
        url: `/${org_id}/${serial}/floorplans/${floor_plan_id}`,
        method: 'GET',
      }),
      providesTags: (result, error) => [{ type: 'floorplans', id: result?.id }],
    }),
    createFloorPlan: build.mutation<
      FloorPlan,
      {
        org_id: string
        serial: string
        floor_plan: {
          name: string
          config_id: string
          tables: {
            table_id: string
            x: string
            y: string
            is_round: string
            rotation: string
          }[]
        }
      }
    >({
      query: ({ org_id, serial, floor_plan }) => ({
        url: `/${org_id}/${serial}/floorplans`,
        method: 'POST',
        data: floor_plan,
      }),

      invalidatesTags: () => [{ type: 'floorplans', id: 'LIST' }],
    }),
    updateFloorPlan: build.mutation<
      FloorPlan,
      {
        org_id: string
        serial: string
        data: {
          id: string
          name: string
          active: boolean
          tables?: {
            table_id: string
            x: number
            y: number
            is_round: boolean
            rotation: number
            layout_kind?: 'desktop' | 'mobile'
          }[]
          structural_elements: FloorPlan['structural_elements']
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/floorplans/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: () => [{ type: 'floorplans', id: 'LIST' }],
    }),
    removeFloorPlan: build.mutation<
      {},
      {
        org_id: string
        serial: string
        floor_plan_id: string
      }
    >({
      query: ({ org_id, serial, floor_plan_id }) => ({
        url: `/${org_id}/${serial}/floorplans/${floor_plan_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => [{ type: 'floorplans', id: 'LIST' }],
    }),
    updatefloorPlanPriorities: build.mutation<
      FloorPlan[],
      {
        org_id: string
        serial: string
        ids: string[]
      }
    >({
      query: ({ org_id, serial, ids }) => ({
        url: `/${org_id}/${serial}/floorplans/priorities`,
        method: 'PUT',
        data: { ids },
      }),

      invalidatesTags: (result, error) => [{ type: 'floorplans', id: 'LIST' }],
    }),
    getBookingDayStatsEnquiries: build.query<
      BookingDayStats,
      {
        org_id: string
        serial: string
        date: string
      }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/enquiries/booking-day-stats`,
        method: 'GET',
        params: {
          date,
        },
      }),
      providesTags: (result, error, { org_id, serial, date }) => [
        { type: 'BookingDayStats', id: `${org_id}-${serial}-${date}` },
      ],
    }),

    assignOwnerToEnquiry: build.mutation<
      Enquiry,
      {
        org_id: string
        serial: string
        id: string
        oauth_id: string
      }
    >({
      query: ({ org_id, serial, id, oauth_id }) => ({
        url: `/${org_id}/${serial}/enquiries/${id}/owner`,
        method: 'PUT',
        data: { oauth_id },
      }),
      invalidatesTags: (result, error) => [
        { type: 'enquiries', id: 'LIST' },
        { type: 'enquiries', id: result?.id },
        { type: 'enquiries', id: `STATUS:${result?.status}` },
      ],
    }),

    removeOwnerToEnquiry: build.mutation<
      Enquiry,
      {
        org_id: string
        serial: string
        id: string
        oauth_id: string
      }
    >({
      query: ({ org_id, serial, id, oauth_id }) => ({
        url: `/${org_id}/${serial}/enquiries/${id}/owner/${oauth_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => [
        { type: 'enquiries', id: 'LIST' },
        { type: 'enquiries', id: result?.id },
        { type: 'enquiries', id: `STATUS:${result?.status}` },
      ],
    }),

    assignSubscriberToEnquiry: build.mutation<
      Enquiry,
      {
        org_id: string
        serial: string
        id: string
        oauth_id: string
      }
    >({
      query: ({ org_id, serial, id, oauth_id }) => ({
        url: `/${org_id}/${serial}/enquiries/${id}/subscriber`,
        method: 'PUT',
        data: { oauth_id },
      }),
      invalidatesTags: (result, error) => [
        { type: 'enquiries', id: 'LIST' },
        { type: 'enquiries', id: result?.id },
        { type: 'enquiries', id: `STATUS:${result?.status}` },
      ],
    }),
    removeSubscriberToEnquiry: build.mutation<
      Enquiry,
      {
        org_id: string
        serial: string
        id: string
        oauth_id: string
      }
    >({
      query: ({ org_id, serial, id, oauth_id }) => ({
        url: `/${org_id}/${serial}/enquiries/${id}/subscriber/${oauth_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => [
        { type: 'enquiries', id: 'LIST' },
        { type: 'enquiries', id: result?.id },
        { type: 'enquiries', id: `STATUS:${result?.status}` },
      ],
    }),

    // Enquiry Manual Payments endpoints
    getEnquiryManualPayments: build.query<
      {
        manual_payments: ManualPayment[]
        deposits: any[]
        cancellation_charges: any[]
      },
      {
        org_id: string
        id: string
      }
    >({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/enquiries/${id}/manual-payments`,
        method: 'GET',
      }),
      providesTags: (res, error, { id }) => [
        { type: 'enquiry-manual-payments', id },
      ],
    }),

    createEnquiryManualPayment: build.mutation<
      {
        message: string
        manual_payment: ManualPayment
      },
      {
        org_id: string
        id: string
        data: {
          manual_payment_type: 'DEPOSIT' | 'CANCEL_CHARGE' | 'FULL_PAYMENT'
          manual_payment_amount: number
          manual_payment_message?: string
          manual_payment_date?: string
          manual_payment_date_active?: boolean
          manual_payment_date_fail_auto_cancel?: boolean
          reminder_offsets_seconds?: number[] | null
          send_email?: boolean
        }
      }
    >({
      query: ({ org_id, id, data }) => ({
        url: `/${org_id}/enquiries/${id}/manual-payments`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (res, error, { id }) => [
        { type: 'enquiry-manual-payments', id },
        { type: 'enquiries', id },
      ],
    }),

    deleteEnquiryManualPayment: build.mutation<
      {
        message: string
      },
      {
        org_id: string
        id: string
        payment_id: string
      }
    >({
      query: ({ org_id, id, payment_id }) => ({
        url: `/${org_id}/enquiries/${id}/manual-payments/${payment_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, error, { id }) => [
        { type: 'enquiry-manual-payments', id },
        { type: 'enquiries', id },
      ],
    }),

    sendEnquiryPaymentConfirmation: build.mutation<
      {
        message: string
      },
      {
        org_id: string
        id: string
        payment_id: string
      }
    >({
      query: ({ org_id, id, payment_id }) => ({
        // `confirmation-email`, matching the booking-side route and what zion has
        // registered since Oct 2025. `send-confirmation` never existed server-side.
        url: `/${org_id}/enquiries/${id}/manual-payments/${payment_id}/confirmation-email`,
        method: 'POST',
        data: {},
      }),
    }),

    createEnquiryDeposit: build.mutation<
      {
        message: string
        deposit: any
      },
      {
        org_id: string
        id: string
        data: {
          amount: number
          deadline?: string
          user_profile_ids?: string[]
          split_evenly?: boolean
        }
      }
    >({
      query: ({ org_id, id, data }) => ({
        url: `/${org_id}/enquiries/${id}/manual-payments/deposits`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (res, error, { id }) => [
        { type: 'enquiry-manual-payments', id },
        { type: 'enquiries', id },
      ],
    }),

    createEnquiryCancellationCharge: build.mutation<
      {
        message: string
        charge: any
      },
      {
        org_id: string
        id: string
        data: {
          amount: number
          user_profile_ids?: string[]
          split_evenly?: boolean
        }
      }
    >({
      query: ({ org_id, id, data }) => ({
        url: `/${org_id}/enquiries/${id}/manual-payments/cancellation-charges`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (res, error, { id }) => [
        { type: 'enquiry-manual-payments', id },
        { type: 'enquiries', id },
      ],
    }),

    // Enquiry invoicing (Square) endpoints — a separate, parallel flow to
    // manual payments/deposits/cancellation charges above (see zion's
    // `Invoice` schema doc comment). Identical shape on the booking side
    // below, sharing zion's `CreateInvoiceSchema` across both route trees.
    getEnquiryInvoices: build.query<
      { invoices: Invoice[] },
      { org_id: string; id: string }
    >({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/enquiries/${id}/invoices`,
        method: 'GET',
      }),
      providesTags: (res, error, { id }) => [{ type: 'enquiry-invoices', id }],
    }),

    createEnquiryInvoice: build.mutation<
      { message: string; invoice: Invoice },
      { org_id: string; id: string; data: CreateInvoicePayload }
    >({
      query: ({ org_id, id, data }) => ({
        url: `/${org_id}/enquiries/${id}/invoices`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (res, error, { id }) => [
        { type: 'enquiry-invoices', id },
        { type: 'enquiries', id },
      ],
    }),

    publishEnquiryInvoice: build.mutation<
      { message: string; invoice: Invoice },
      { org_id: string; id: string; invoice_id: string }
    >({
      query: ({ org_id, id, invoice_id }) => ({
        url: `/${org_id}/enquiries/${id}/invoices/${invoice_id}/publish`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: (res, error, { id }) => [
        { type: 'enquiry-invoices', id },
        { type: 'enquiries', id },
      ],
    }),

    cancelEnquiryInvoice: build.mutation<
      { message: string; invoice: Invoice },
      { org_id: string; id: string; invoice_id: string }
    >({
      query: ({ org_id, id, invoice_id }) => ({
        url: `/${org_id}/enquiries/${id}/invoices/${invoice_id}/cancel`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: (res, error, { id }) => [
        { type: 'enquiry-invoices', id },
        { type: 'enquiries', id },
      ],
    }),

    getTimeSlotCapacities: build.query<
      TimeSlotCapacity,
      {
        org_id: string
        serial: string
        config_id: string
      }
    >({
      query: ({ org_id, serial, config_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/time-slot-capacities`,
        method: 'GET',
      }),
      providesTags: [{ type: 'time-slot-capacities', id: 'LIST' }],
    }),

    updateTimeSlotCapacities: build.mutation<
      TimeSlotCapacity,
      {
        org_id: string
        serial: string
        config_id: string
        data: TimeSlotCapacityPayload
      }
    >({
      query: ({ org_id, serial, config_id, data }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/time-slot-capacities`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [{ type: 'time-slot-capacities', id: 'LIST' }],
    }),

    // Daily Insights endpoint
    fetchDayInsights: build.query<
      any, // We'll use any for now since DayInsightsData is in a different location
      {
        org_id: string
        serial: string
        date: string // Format: YYYY-MM-DD
        include_cancelled?: boolean
      }
    >({
      query: ({ org_id, serial, date, include_cancelled = false }) => ({
        url: `/${org_id}/${serial}/fast-view/bookings/daily-insights`,
        method: 'GET',
        params: {
          date,
          include_cancelled: include_cancelled.toString(),
        },
      }),
      providesTags: ['daily-insights'],
    }),

    getOrgBookingTypes: build.query<
      {
        id: string
        config_id: string
        name: string
        active: boolean
      }[],
      {
        org_id: string
      }
    >({
      query: ({ org_id }) => ({
        url: `/${org_id}/booking-types`,
        method: 'GET',
      }),
      providesTags: [{ type: 'booking-types', id: 'LIST' }],
    }),

    // Reports endpoints
    getReports: build.query<
      ReportsResponse,
      {
        org_id: string
        page?: string
        limit?: string
        status?: string
        venue?: string
        sort?: string
        order?: string
      }
    >({
      query: ({ org_id, ...params }) => ({
        url: `/${org_id}/reports`,
        method: 'GET',
        params,
      }),
      providesTags: [{ type: 'reporting', id: 'LIST' }],
    }),

    getReport: build.query<
      { report: Report },
      {
        org_id: string
        id: string
      }
    >({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/reports/${id}`,
        method: 'GET',
      }),
      providesTags: (result) => [{ type: 'reporting', id: result?.report?.id }],
    }),

    createReport: build.mutation<
      { report: Report },
      {
        org_id: string
        data: CreateReportRequest
      }
    >({
      query: ({ org_id, data }) => ({
        url: `/${org_id}/reports`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'reporting', id: 'LIST' }],
    }),

    deleteReport: build.mutation<
      { message: string },
      {
        org_id: string
        id: string
      }
    >({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/reports/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'reporting', id: 'LIST' },
        { type: 'reporting', id: 'DELETED' },
      ],
    }),

    regenerateReport: build.mutation<
      { message: string; report_id: string },
      {
        org_id: string
        id: string
      }
    >({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/reports/${id}/regenerate`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'reporting', id: arg.id },
        { type: 'reporting', id: 'LIST' },
      ],
    }),

    getReportVenues: build.query<
      VenuesResponse,
      {
        org_id: string
      }
    >({
      query: ({ org_id }) => ({
        url: `/${org_id}/reports/venues`,
        method: 'GET',
      }),
      providesTags: [{ type: 'reporting', id: 'VENUES' }],
    }),

    exportReport: build.mutation<
      { message: string; report_id: string },
      {
        org_id: string
        id: string
        format?: 'pdf' | 'csv' | 'excel'
        include_ai_report?: boolean
        include_charts?: boolean
      }
    >({
      query: ({ org_id, id, ...data }) => ({
        url: `/${org_id}/reports/${id}/export`,
        method: 'POST',
        data: {
          format: data.format ?? 'pdf',
          include_ai_report: data.include_ai_report ?? true,
          include_charts: data.include_charts ?? true,
        },
      }),
    }),

    compareReports: build.mutation<
      { comparison_id: string; message: string },
      {
        org_id: string
        report_from_id: string
        report_to_id: string
      }
    >({
      query: ({ org_id, report_from_id, report_to_id }) => ({
        url: `/${org_id}/reports/compare`,
        method: 'POST',
        data: {
          report_from_id,
          report_to_id,
        },
      }),
      invalidatesTags: [{ type: 'reporting', id: 'COMPARISONS' }],
    }),

    getReportComparisons: build.query<
      ComparisonsResponse,
      {
        org_id: string
        page?: string
        limit?: string
      }
    >({
      query: ({ org_id, ...params }) => ({
        url: `/${org_id}/reports/comparisons`,
        method: 'GET',
        params,
      }),
      providesTags: [{ type: 'reporting', id: 'COMPARISONS' }],
    }),

    // Events endpoints
    getEvents: build.query<
      EventsListResponse,
      {
        org_id: string
        serial: string
        config_id: string
        page?: number
        limit?: number
        search?: string
        /**
         * Narrow to one kind of event. Ticketed and table-booked events are
         * separate sections in Connect, and filtering server-side is what keeps
         * `pagination.total` honest — a client-side filter leaves `total`
         * counting both kinds, so a page of 25 can show 3 rows while claiming 40.
         * Omit for both kinds.
         */
        ticket_mode?: boolean
      }
    >({
      query: ({
        org_id,
        serial,
        config_id,
        page = 1,
        limit = 10,
        search = '',
        ticket_mode,
      }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/events`,
        method: 'GET',
        params: {
          page,
          limit,
          search,
          ...(ticket_mode === undefined
            ? {}
            : { ticket_mode: String(ticket_mode) }),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: 'events' as const,
                id,
              })),
              { type: 'events', id: 'LIST' },
            ]
          : [{ type: 'events', id: 'LIST' }],
    }),

    getEventById: build.query<
      BookingEvent,
      { org_id: string; serial: string; config_id: string; event_id: string }
    >({
      query: ({ org_id, serial, config_id, event_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/events/${event_id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { event_id }) => [
        { type: 'events', id: event_id },
      ],
    }),

    createEvent: build.mutation<
      BookingEvent,
      {
        org_id: string
        serial: string
        config_id: string
        data: CreateEventPayload
      }
    >({
      query: ({ org_id, serial, config_id, data }) => {
        console.log('🚀 CREATE EVENT API CALL:')
        console.log(
          '  📍 URL:',
          `/${org_id}/${serial}/config/${config_id}/events`
        )
        console.log('  📦 Payload:', JSON.stringify(data, null, 2))
        console.log('  🗓️ Date Info:')
        console.log('    - Range mode:', data.range)
        console.log('    - Start date:', data.start_date)
        console.log('    - End date:', data.end_date)
        console.log('    - Individual dates:', data.dates)

        return {
          url: `/${org_id}/${serial}/config/${config_id}/events`,
          method: 'POST',
          data,
        }
      },
      transformResponse: (envelope: {
        message?: string
        data: BookingEvent
      }) => {
        // zion wraps every events response in `{ message, data }`. This used to hand the
        // envelope straight back while declaring it a BookingEvent, so every field read off
        // the result was undefined. Harmless for years because no caller read one, until the
        // ticketed form needed the new id to POST its tiers and sent `events/undefined/tiers`.
        const response = envelope.data
        console.log('🎉🎊✨ CREATE EVENT RESPONSE SUCCESS! ✨🎊🎉')
        console.log('  🆔 Event ID:', response.id)
        console.log('  📛 Event Name:', response.name)
        console.log('  ✅ Active:', response.active ? '✓ YES 🟢' : '✗ NO 🔴')
        console.log('  👁️ Hidden:', response.hidden ? '✓ YES 🙈' : '✗ NO 👀')
        console.log(
          '  📅 Date Mode:',
          response.range ? '📆 RANGE MODE' : '📌 INDIVIDUAL DATES'
        )
        console.log('  🗓️ Start Date:', response.start_date || '❌ NOT SET')
        console.log('  🗓️ End Date:', response.end_date || '❌ NOT SET')
        console.log(
          '  📍 Occurrences:',
          response.occurrences?.length || 0,
          'dates'
        )
        console.log(
          '  🎫 Ticket Mode:',
          response.ticket_mode ? '🎟️ ENABLED' : '🚫 DISABLED'
        )
        console.log(
          '  🍽️ Tables Override:',
          response.tables_combinations_override ? '✅ CUSTOM' : '❌ DEFAULT'
        )
        console.log(
          '  💰 Avg Spend:',
          response.avg_spend_per_cover
            ? `$${response.avg_spend_per_cover / 100}`
            : 'N/A'
        )
        console.log('  ⏰ Created:', response.created_at)
        console.log('  🔄 Updated:', response.updated_at)
        console.log('  📊 Full Response:', response)
        console.log('🌟💫🎯 Event Created Successfully! 🎯💫🌟')
        return response
      },
      invalidatesTags: [{ type: 'events', id: 'LIST' }],
    }),

    updateEvent: build.mutation<
      BookingEvent,
      {
        org_id: string
        serial: string
        config_id: string
        event_id: string
        data: UpdateEventPayload
      }
    >({
      query: ({ org_id, serial, config_id, event_id, data }) => {
        console.log('🔄 UPDATE EVENT API CALL:')
        console.log(
          '  📍 URL:',
          `/${org_id}/${serial}/config/${config_id}/events/${event_id}`
        )
        console.log('  🆔 Event ID:', event_id)
        console.log('  📦 Payload:', JSON.stringify(data, null, 2))
        console.log('  🗓️ Date Info:')
        console.log('    - Range mode:', data.range)
        console.log('    - Start date:', data.start_date)
        console.log('    - End date:', data.end_date)
        console.log('    - Individual dates:', data.dates)

        return {
          url: `/${org_id}/${serial}/config/${config_id}/events/${event_id}`,
          method: 'PUT',
          data,
        }
      },
      transformResponse: (envelope: {
        message?: string
        data: BookingEvent
      }) => {
        // Same `{ message, data }` envelope as create, unwrapped for the same reason.
        const response = envelope.data
        console.log('🎯🔥💥 UPDATE EVENT RESPONSE SUCCESS! 💥🔥🎯')
        console.log('  🆔 Event ID:', response.id)
        console.log('  📛 Event Name:', response.name)
        console.log('  ✅ Active:', response.active ? '✓ YES 🟢' : '✗ NO 🔴')
        console.log('  👁️ Hidden:', response.hidden ? '✓ YES 🙈' : '✗ NO 👀')
        console.log(
          '  📅 Date Mode:',
          response.range ? '📆 RANGE MODE' : '📌 INDIVIDUAL DATES'
        )
        console.log('  🗓️ Start Date:', response.start_date || '❌ NOT SET')
        console.log('  🗓️ End Date:', response.end_date || '❌ NOT SET')
        console.log(
          '  📍 Occurrences:',
          response.occurrences?.length || 0,
          'dates'
        )
        if (response.occurrences?.length) {
          console.log(
            '  📅 Occurrence Dates:',
            response.occurrences.map((o) => o.date).join(', ')
          )
        }
        console.log(
          '  🎫 Ticket Mode:',
          response.ticket_mode ? '🎟️ ENABLED' : '🚫 DISABLED'
        )
        console.log(
          '  🍽️ Tables Override:',
          response.tables_combinations_override ? '✅ CUSTOM' : '❌ DEFAULT'
        )
        console.log(
          '  💰 Avg Spend:',
          response.avg_spend_per_cover
            ? `$${response.avg_spend_per_cover / 100}`
            : 'N/A'
        )
        console.log(
          '  🏢 Business Hours:',
          response.business_hours ? '⏰ SET' : '❌ NOT SET'
        )
        console.log('  ⏰ Created:', response.created_at)
        console.log('  🔄 Updated:', response.updated_at)
        console.log('  📊 Full Response:', response)
        console.log('🚀🌈🎊 Event Updated Successfully! 🎊🌈🚀')
        return response
      },
      invalidatesTags: (result, error, { event_id }) => [
        { type: 'events', id: event_id },
        { type: 'events', id: 'LIST' },
      ],
    }),

    deleteEvent: build.mutation<
      { message: string; data: BookingEvent },
      {
        org_id: string
        serial: string
        config_id: string
        event_id: string
      }
    >({
      query: ({ org_id, serial, config_id, event_id }) => ({
        url: `/${org_id}/${serial}/config/${config_id}/events/${event_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'events', id: 'LIST' }],
    }),

    // ── Ticketed events (TICKETED_EVENTS_API.md) ────────────────────────────
    //
    // These sit on `/:org_id/:serial/tickets`, NOT under `/config/:config_id`
    // like the events endpoints above — a ticket order belongs to the venue, not
    // to a booking config. Every response is wrapped in `{ success, data }`, so
    // each endpoint unwraps it in `transformResponse` and callers see the
    // payload directly. Error bodies are NOT unwrapped: read `error.data.code`.

    getTicketTiers: build.query<
      TicketTier[],
      { org_id: string; serial: string; event_id: string }
    >({
      query: ({ org_id, serial, event_id }) => ({
        url: `/${org_id}/${serial}/tickets/events/${event_id}/tiers`,
        method: 'GET',
      }),
      transformResponse: (response: { data: TicketTier[] }) => response.data,
      providesTags: (result, error, { event_id }) => [
        { type: 'ticket-tiers', id: event_id },
      ],
    }),

    createTicketTier: build.mutation<
      CreateTierResult,
      {
        org_id: string
        serial: string
        event_id: string
        data: TicketTierPayload
      }
    >({
      query: ({ org_id, serial, event_id, data }) => ({
        url: `/${org_id}/${serial}/tickets/events/${event_id}/tiers`,
        method: 'POST',
        data,
      }),
      // `warning` sits alongside `data` rather than inside it, and it is the one
      // thing that must not be dropped: it means the tier will sell nothing.
      transformResponse: (response: {
        data: { tier: TicketTier; buckets_created: number }
        warning?: string
      }) => ({ ...response.data, warning: response.warning }),
      invalidatesTags: (result, error, { event_id }) => [
        { type: 'ticket-tiers', id: event_id },
      ],
    }),

    updateTicketTier: build.mutation<
      TicketTier,
      {
        org_id: string
        serial: string
        event_id: string
        tier_id: string
        // A full replace, not a patch: zion's schema is `.strict()` and requires
        // name, price and capacity every time, and rejects unknown keys.
        data: TicketTierPayload
      }
    >({
      query: ({ org_id, serial, event_id, tier_id, data }) => ({
        url: `/${org_id}/${serial}/tickets/events/${event_id}/tiers/${tier_id}`,
        method: 'PUT',
        data,
      }),
      transformResponse: (response: { data: TicketTier }) => response.data,
      invalidatesTags: (result, error, { event_id }) => [
        { type: 'ticket-tiers', id: event_id },
      ],
    }),

    deleteTicketTier: build.mutation<
      DeleteTierResult,
      { org_id: string; serial: string; event_id: string; tier_id: string }
    >({
      query: ({ org_id, serial, event_id, tier_id }) => ({
        url: `/${org_id}/${serial}/tickets/events/${event_id}/tiers/${tier_id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: {
        data: { deactivated: boolean; tickets_sold: number }
        message?: string
      }) => ({ ...response.data, message: response.message }),
      invalidatesTags: (result, error, { event_id }) => [
        { type: 'ticket-tiers', id: event_id },
      ],
    }),

    getTicketOrders: build.query<
      TicketOrdersResponse,
      {
        org_id: string
        serial: string
        event_id?: string
        occurrence_id?: string
        status?: TicketOrderStatus
        search?: string
        limit?: number
        cursor?: string
      }
    >({
      query: ({ org_id, serial, ...params }) => ({
        url: `/${org_id}/${serial}/tickets/orders`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: TicketOrdersResponse) => ({
        data: response.data,
        next_cursor: response.next_cursor,
      }),
      providesTags: [{ type: 'ticket-orders', id: 'LIST' }],
    }),

    getTicketOrder: build.query<
      TicketOrderDetail,
      { org_id: string; serial: string; order_id: string }
    >({
      query: ({ org_id, serial, order_id }) => ({
        url: `/${org_id}/${serial}/tickets/orders/${order_id}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: TicketOrderDetail }) =>
        response.data,
      providesTags: (result, error, { order_id }) => [
        { type: 'ticket-orders', id: order_id },
      ],
    }),

    /**
     * The cancellation quote. Writes nothing, so it can be re-read freely as
     * staff tick tickets on and off — which is why the cancel dialog always
     * fetches it before showing a number.
     */
    getTicketCancelQuote: build.query<
      CancellationQuote,
      {
        org_id: string
        serial: string
        order_id: string
        /** Omit to quote the whole order. */
        ticket_ids?: string[]
      }
    >({
      query: ({ org_id, serial, order_id, ticket_ids }) => ({
        url: `/${org_id}/${serial}/tickets/orders/${order_id}/cancel`,
        method: 'GET',
        params: ticket_ids?.length
          ? { ticket_ids: ticket_ids.join(',') }
          : undefined,
      }),
      transformResponse: (response: { data: CancellationQuote }) =>
        response.data,
      // Deliberately untagged: the quote is a live read that the dialog refetches
      // itself, and tagging it would have every cancellation refetch stale quotes
      // for orders nobody is looking at.
    }),

    cancelTicketOrder: build.mutation<
      CancelTicketsResult,
      {
        org_id: string
        serial: string
        order_id: string
        /** Only used to invalidate the tier figures the release moves. */
        event_id?: string
        data: CancelTicketsPayload
      }
    >({
      query: ({ org_id, serial, order_id, data }) => ({
        url: `/${org_id}/${serial}/tickets/orders/${order_id}/cancel`,
        method: 'POST',
        data,
      }),
      // The slice's base query retries twice by default. A refund must never be
      // replayed on our behalf: `refund_failed` comes back as a 400 with the
      // tickets already cancelled, and a retry would attempt the refund again.
      // `client_request_id` is the server-side guard; this is the client one.
      extraOptions: { maxRetries: 0 },
      transformResponse: (response: { data: CancelTicketsResult }) =>
        response.data,
      invalidatesTags: (result, error, { order_id, event_id }) => [
        { type: 'ticket-orders', id: order_id },
        { type: 'ticket-orders', id: 'LIST' },
        // Cancelling releases inventory, so the tier figures move too.
        ...(event_id
          ? [{ type: 'ticket-tiers' as const, id: event_id }]
          : [{ type: 'ticket-tiers' as const, id: 'LIST' }]),
        { type: 'ticket-door', id: 'LIST' },
      ],
    }),

    resendTicketHolderEmail: build.mutation<
      { success: boolean },
      { org_id: string; serial: string; order_id: string; holder_id: string }
    >({
      query: ({ org_id, serial, order_id, holder_id }) => ({
        url: `/${org_id}/${serial}/tickets/orders/${order_id}/holders/${holder_id}/resend`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { order_id }) => [
        { type: 'ticket-orders', id: order_id },
      ],
    }),

    /**
     * Admit a holder at the door (D14, D23, D25).
     *
     * Answers 200 even when nothing was admitted, because "already admitted at
     * 19:42" is information the door needs rather than an error. Callers branch
     * on `ok` in the body, never on the HTTP status — which is also why this is
     * typed to resolve rather than reject on a refusal.
     */
    scanTicket: build.mutation<
      ScanResult,
      { org_id: string; serial: string; code: string; count?: number }
    >({
      query: ({ org_id, serial, code, count }) => ({
        url: `/${org_id}/${serial}/tickets/scan`,
        method: 'POST',
        data: { code, ...(count === undefined ? {} : { count }) },
      }),
      // Admission is not idempotent: a retry of a request that actually
      // succeeded server-side would admit a second ticket.
      extraOptions: { maxRetries: 0 },
      transformResponse: (response: { data: ScanResult }) => response.data,
      invalidatesTags: [{ type: 'ticket-door', id: 'LIST' }],
    }),

    getTicketDoorList: build.query<
      DoorList,
      {
        org_id: string
        serial: string
        event_id: string
        occurrence_id?: string
      }
    >({
      query: ({ org_id, serial, event_id, occurrence_id }) => ({
        url: `/${org_id}/${serial}/tickets/door-list`,
        method: 'GET',
        params: { event_id, ...(occurrence_id ? { occurrence_id } : {}) },
      }),
      transformResponse: (response: { data: DoorList }) => response.data,
      providesTags: [{ type: 'ticket-door', id: 'LIST' }],
    }),

    getEnquiryOrders: build.query<
      EnquiryOrdersResponse,
      {
        org_id: string
        enquiry_id: string
      }
    >({
      query: ({ org_id, enquiry_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders`,
        method: 'GET',
      }),
      providesTags: (result, error, { enquiry_id }) => [
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
      ],
    }),

    createEnquiryOrder: build.mutation<
      EnquiryOrderResponse,
      {
        org_id: string
        enquiry_id: string
        data: CreateEnquiryOrderRequest
      }
    >({
      query: ({ org_id, enquiry_id, data }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { enquiry_id }) => [
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
        { type: 'enquiries', id: enquiry_id },
      ],
    }),

    getEnquiryOrder: build.query<
      EnquiryOrderResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { order_id }) => [
        { type: 'enquiry-orders', id: order_id },
      ],
    }),

    updateEnquiryOrder: build.mutation<
      EnquiryOrderResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
        data: UpdateEnquiryOrderRequest
      }
    >({
      query: ({ org_id, enquiry_id, order_id, data }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
        { type: 'enquiries', id: enquiry_id },
      ],
    }),

    deleteEnquiryOrder: build.mutation<
      { message: string },
      {
        org_id: string
        enquiry_id: string
        order_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
        { type: 'enquiries', id: enquiry_id },
      ],
    }),

    cancelEnquiryOrder: build.mutation<
      { message: string },
      { org_id: string; enquiry_id: string; order_id: string }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
        { type: 'enquiries', id: enquiry_id },
      ],
    }),

    reactivateEnquiryOrder: build.mutation<
      { message: string },
      { org_id: string; enquiry_id: string; order_id: string }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
        { type: 'enquiries', id: enquiry_id },
      ],
    }),

    completeEnquiryOrder: build.mutation<
      { message: string },
      { org_id: string; enquiry_id: string; order_id: string }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
        { type: 'enquiries', id: enquiry_id },
      ],
    }),

    reopenEnquiryOrder: build.mutation<
      { message: string },
      { org_id: string; enquiry_id: string; order_id: string }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
        { type: 'enquiries', id: enquiry_id },
      ],
    }),

    submitEnquiryOrder: build.mutation<
      EnquiryOrderResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
        { type: 'enquiries', id: enquiry_id },
      ],
    }),

    getEnquiryOrderParticipants: build.query<
      EnquiryOrderParticipantsResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/participants`,
        method: 'GET',
      }),
      providesTags: (result, error, { order_id }) => [
        { type: 'enquiry-orders', id: `PARTICIPANTS-${order_id}` },
      ],
    }),

    createEnquiryOrderParticipant: build.mutation<
      EnquiryOrderParticipantResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
        data: CreateEnquiryOrderParticipantRequest
      }
    >({
      query: ({ org_id, enquiry_id, order_id, data }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/participants`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `PARTICIPANTS-${order_id}` },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
      ],
    }),

    updateEnquiryOrderParticipant: build.mutation<
      EnquiryOrderParticipantResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
        participant_id: string
        data: UpdateEnquiryOrderParticipantRequest
      }
    >({
      query: ({ org_id, enquiry_id, order_id, participant_id, data }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/participants/${participant_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `PARTICIPANTS-${order_id}` },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
      ],
    }),

    deleteEnquiryOrderParticipant: build.mutation<
      { message: string },
      {
        org_id: string
        enquiry_id: string
        order_id: string
        participant_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id, participant_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/participants/${participant_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `PARTICIPANTS-${order_id}` },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
      ],
    }),

    getEnquiryOrderItems: build.query<
      EnquiryOrderItemsResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/items`,
        method: 'GET',
      }),
      providesTags: (result, error, { order_id }) => [
        { type: 'enquiry-orders', id: `ITEMS-${order_id}` },
      ],
    }),

    createEnquiryOrderItem: build.mutation<
      EnquiryOrderItemResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
        data: CreateEnquiryOrderItemRequest
      }
    >({
      query: ({ org_id, enquiry_id, order_id, data }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/items`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `ITEMS-${order_id}` },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
      ],
    }),

    updateEnquiryOrderItem: build.mutation<
      EnquiryOrderItemResponse,
      {
        org_id: string
        enquiry_id: string
        order_id: string
        item_id: string
        data: UpdateEnquiryOrderItemRequest
      }
    >({
      query: ({ org_id, enquiry_id, order_id, item_id, data }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/items/${item_id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `ITEMS-${order_id}` },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
      ],
    }),

    deleteEnquiryOrderItem: build.mutation<
      { message: string },
      {
        org_id: string
        enquiry_id: string
        order_id: string
        item_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id, item_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/items/${item_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { enquiry_id, order_id }) => [
        { type: 'enquiry-orders', id: order_id },
        { type: 'enquiry-orders', id: `ITEMS-${order_id}` },
        { type: 'enquiry-orders', id: `LIST-${enquiry_id}` },
      ],
    }),

    sendEnquiryPreorderEmail: build.mutation<
      { message: string },
      {
        org_id: string
        enquiry_id: string
        order_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/send-preorder-email`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { enquiry_id }) => [
        { type: 'enquiry-activities' as const, id: enquiry_id },
      ],
    }),

    sendParticipantPreorderEmail: build.mutation<
      { message: string },
      {
        org_id: string
        enquiry_id: string
        order_id: string
        participant_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id, participant_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/participants/${participant_id}/send-email`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { enquiry_id }) => [
        { type: 'enquiry-activities' as const, id: enquiry_id },
      ],
    }),

    sendAllPreorderEmails: build.mutation<
      { message: string; sent_count: number },
      {
        org_id: string
        enquiry_id: string
        order_id: string
      }
    >({
      query: ({ org_id, enquiry_id, order_id }) => ({
        url: `/${org_id}/enquiries/${enquiry_id}/orders/${order_id}/send-all-emails`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { enquiry_id }) => [
        { type: 'enquiry-activities' as const, id: enquiry_id },
      ],
    }),

    // ===== PRE-ORDERS DASHBOARD =====

    // ===== PLACE CARDS =====

    getPlacecardOrders: build.query<
      PlacecardOrdersResponse,
      PlacecardOrderFilters
    >({
      query: (filters) => ({
        url: `/${filters.org_id}/placecards/pre-orders?${placecardSearchParams(
          filters
        ).toString()}`,
        method: 'GET',
      }),
      providesTags: ['booking-orders'],
    }),

    getPlacecardPresets: build.query<
      { data: PlacecardPreset[] },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/placecards/presets`,
        method: 'GET',
        params: { serial },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: 'placecard-presets' as const,
                id,
              })),
              { type: 'placecard-presets', id: 'LIST' },
            ]
          : [{ type: 'placecard-presets', id: 'LIST' }],
    }),

    createPlacecardPreset: build.mutation<
      { data: PlacecardPreset },
      { org_id: string; serial: string; data: PlacecardPresetPayload }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/placecards/presets`,
        method: 'POST',
        params: { serial },
        data,
      }),
      invalidatesTags: [{ type: 'placecard-presets', id: 'LIST' }],
    }),

    updatePlacecardPreset: build.mutation<
      { data: PlacecardPreset },
      {
        org_id: string
        serial: string
        id: string
        /** The server rejects an empty body: send a name, a config, or both. */
        data: Partial<PlacecardPresetPayload>
      }
    >({
      query: ({ org_id, serial, id, data }) => ({
        url: `/${org_id}/placecards/presets/${id}`,
        method: 'PATCH',
        params: { serial },
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'placecard-presets', id },
        { type: 'placecard-presets', id: 'LIST' },
      ],
    }),

    deletePlacecardPreset: build.mutation<
      { data: { id: string; deleted_at: string } },
      { org_id: string; serial: string; id: string }
    >({
      query: ({ org_id, serial, id }) => ({
        url: `/${org_id}/placecards/presets/${id}`,
        method: 'DELETE',
        params: { serial },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'placecard-presets', id },
        { type: 'placecard-presets', id: 'LIST' },
      ],
    }),

    getHowAreWeDoing: build.query<
      HowAreWeDoingResponse,
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/how-are-we-doing`,
        method: 'GET',
      }),
    }),

    getPreOrderEligibility: build.query<
      {
        eligible: boolean
        menus: {
          id: string
          name: string
          pre_order_cutoff: number | null
          cutoff_time: string | null
          header_img: string | null
          pay_ahead: boolean
          individual_payment: boolean
          // Optional eligibility flags. Populated once the backend
          // extension lands; until then the form treats every menu
          // as fully eligible (no reasons rendered).
          min_party_size?: number
          max_party_size?: number
          within_party_size?: boolean
          within_service_hours?: boolean
          cutoff_passed?: boolean
          is_preferred?: boolean
        }[]
      },
      {
        org_id: string
        serial: string
        booking_type_id?: string
        start_time: string
        party_size: number
        event_id?: string
        override?: boolean
      }
    >({
      query: ({ org_id, serial, ...params }) => {
        const sp = new URLSearchParams()
        sp.set('start_time', params.start_time)
        sp.set('party_size', String(params.party_size))
        if (params.booking_type_id)
          sp.set('booking_type_id', params.booking_type_id)
        if (params.event_id) sp.set('event_id', params.event_id)
        if (params.override) sp.set('override', 'true')
        return {
          url: `/${org_id}/${serial}/bookings/pre-order-eligibility?${sp.toString()}`,
          method: 'GET',
        }
      },
    }),

    searchUserProfileWithPreferences: build.query<
      {
        id: string
        first_name: string
        last_name: string
        allergens: string[]
        dietary_requirements: string[]
        special_occassions: string[]
        number_of_dogs: number
        vip: boolean
        previous_bookings_count: number
      } | null,
      {
        org_id: string
        serial: string
        email?: string
        phone?: string
      }
    >({
      query: ({ org_id, serial, email, phone }) => {
        const sp = new URLSearchParams()
        if (email) sp.set('email', email)
        if (phone) sp.set('phone', phone)
        return {
          url: `/${org_id}/${serial}/user-profiles/search?${sp.toString()}`,
          method: 'GET',
        }
      },
    }),

    getBookingEligibleMenus: build.query<
      {
        eligible: boolean
        reason?: string
        menus: {
          id: string
          name: string
          active: boolean
          pre_order: boolean
          pre_order_cutoff: number | null
          cutoff_time: string | null
          cutoff_passed: boolean
          min_party_size: number | null
          max_party_size: number | null
          within_party_size: boolean
          within_service_hours: boolean
          is_preferred: boolean
          header_img: string | null
          pay_ahead: boolean
          individual_payment: boolean
        }[]
        existing_orders: {
          id: string
          menu_id: string | null
          menu_name: string | null
          order_status: string
          item_count: number
        }[]
      },
      { org_id: string; serial: string; booking_id: string }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/eligible-menus`,
        method: 'GET',
      }),
    }),

    // ===== BOOKING ORDER MUTATIONS =====

    getBookingOrder: build.query<
      { order: any },
      { org_id: string; serial: string; booking_id: string; order_id: string }
    >({
      query: ({ org_id, serial, booking_id, order_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { order_id }) => [
        { type: 'booking-orders' as const, id: order_id },
      ],
    }),

    getBookingOrders: build.query<
      { orders: any[]; count: number },
      { org_id: string; serial: string; booking_id: string }
    >({
      query: ({ org_id, serial, booking_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders`,
        method: 'GET',
      }),
      providesTags: (result, error, { booking_id }) => [
        { type: 'booking-orders' as const, id: `LIST-${booking_id}` },
      ],
    }),

    createBookingOrder: build.mutation<
      { order: any },
      {
        org_id: string
        serial: string
        booking_id: string
        data: { menu_id: string }
      }
    >({
      query: ({ org_id, serial, booking_id, data }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { booking_id }) => [
        { type: 'booking-orders' as const, id: `LIST-${booking_id}` },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    cancelBookingOrder: build.mutation<
      { message: string },
      { org_id: string; serial: string; booking_id: string; order_id: string }
    >({
      query: ({ org_id, serial, booking_id, order_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}`,
        method: 'DELETE',
      }),
      // The order itself too: a cancelled pre-order is still listed and
      // fetchable, so an open editor should flip to Cancelled / Reactivate.
      invalidatesTags: (result, error, { booking_id, order_id }) => [
        { type: 'booking-orders' as const, id: `LIST-${booking_id}` },
        { type: 'booking-orders' as const, id: order_id },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    reopenBookingOrder: build.mutation<
      { message: string },
      { org_id: string; serial: string; booking_id: string; order_id: string }
    >({
      query: ({ org_id, serial, booking_id, order_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { booking_id, order_id }) => [
        { type: 'booking-orders' as const, id: `LIST-${booking_id}` },
        { type: 'booking-orders' as const, id: order_id },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    completeBookingOrder: build.mutation<
      { message: string },
      { org_id: string; serial: string; booking_id: string; order_id: string }
    >({
      query: ({ org_id, serial, booking_id, order_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { booking_id, order_id }) => [
        { type: 'booking-orders' as const, id: `LIST-${booking_id}` },
        { type: 'booking-orders' as const, id: order_id },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    reactivateBookingOrder: build.mutation<
      { message: string },
      { org_id: string; serial: string; booking_id: string; order_id: string }
    >({
      query: ({ org_id, serial, booking_id, order_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { booking_id, order_id }) => [
        { type: 'booking-orders' as const, id: `LIST-${booking_id}` },
        { type: 'booking-orders' as const, id: order_id },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    createBookingOrderItem: build.mutation<
      { items: any[] },
      {
        org_id: string
        serial: string
        booking_id: string
        order_id: string
        data: {
          items: {
            menu_item_id: string
            participant_id: string
            special_instructions?: string | null
            modifiers?: {
              modifier_id: string
              quantity: number
            }[]
          }[]
        }
      }
    >({
      query: ({ org_id, serial, booking_id, order_id, data }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/items`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { order_id, booking_id }) => [
        { type: 'booking-orders' as const, id: order_id },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    deleteBookingOrderItem: build.mutation<
      { message: string },
      {
        org_id: string
        serial: string
        booking_id: string
        order_id: string
        item_id: string
      }
    >({
      query: ({ org_id, serial, booking_id, order_id, item_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/items/${item_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { order_id, booking_id }) => [
        { type: 'booking-orders' as const, id: order_id },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    createBookingOrderParticipant: build.mutation<
      any,
      {
        org_id: string
        serial: string
        booking_id: string
        order_id: string
        data: {
          email: string
          first_name: string
          last_name: string
          phone?: string | null
          /** Add them AS the lead, demoting the current one. */
          make_lead?: boolean
        }
      }
    >({
      query: ({ org_id, serial, booking_id, order_id, data }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/participants`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { order_id, booking_id }) => [
        { type: 'booking-orders' as const, id: order_id },
        { type: 'booking-orders' as const, id: `LIST-${booking_id}` },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    deleteBookingOrderParticipant: build.mutation<
      { message: string },
      {
        org_id: string
        serial: string
        booking_id: string
        order_id: string
        participant_id: string
      }
    >({
      query: ({ org_id, serial, booking_id, order_id, participant_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/participants/${participant_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { order_id, booking_id }) => [
        { type: 'booking-orders' as const, id: order_id },
        { type: 'booking-orders' as const, id: `LIST-${booking_id}` },
        { type: 'bookings' as const, id: booking_id },
      ],
    }),

    sendBookingParticipantPreorderEmail: build.mutation<
      { message: string },
      {
        org_id: string
        serial: string
        booking_id: string
        order_id: string
        participant_id: string
      }
    >({
      query: ({ org_id, serial, booking_id, order_id, participant_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/participants/${participant_id}/send-email`,
        method: 'POST',
      }),
    }),

    sendAllBookingPreorderEmails: build.mutation<
      { message: string; sent_count: number },
      { org_id: string; serial: string; booking_id: string; order_id: string }
    >({
      query: ({ org_id, serial, booking_id, order_id }) => ({
        url: `/${org_id}/${serial}/bookings/${booking_id}/orders/${order_id}/send-all-emails`,
        method: 'POST',
      }),
    }),

    getNeoReporting: build.query<
      NeoReportingResponse,
      {
        org_id: string
        from: string
        to: string
        serial?: string
      }
    >({
      query: ({ org_id, from, to, serial }) => ({
        url: buildUrl(`/${org_id}/neo-reporting`, { from, to, serial }),
        method: 'GET',
      }),
      providesTags: (result, error, { org_id, from, to, serial }) => [
        {
          type: 'neo_reporting' as const,
          id: `${org_id}-${from}-${to}-${serial ?? 'all'}`,
        },
      ],
    }),

    listNeoOrders: build.query<
      NeoVenueOrderEntry[],
      { org_id: string; serial: string; date: string }
    >({
      query: ({ org_id, serial, date }) => ({
        url: `/${org_id}/${serial}/neo/orders`,
        method: 'GET',
        params: { date },
      }),
      providesTags: (result, error, { org_id, serial, date }) => [
        {
          type: 'neo_reporting' as const,
          id: `orders-${org_id}-${serial}-${date}`,
        },
      ],
    }),

    getQueueStats: build.query<
      {
        total_in_queue: number
        total_admitted: number
        last_release_at: string | null
      },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/queue/stats`,
        method: 'GET',
      }),
      providesTags: (result, error, { org_id, serial }) => [
        { type: 'queue-stats' as const, id: `${org_id}-${serial}` },
      ],
    }),

    // Onboarding progress
    getOnboardingProgress: build.query<
      {
        id: string
        config_id: string
        org_id: string
        serial: string
        current_step: string
        [key: `${string}_completed_at`]: string | null
        wizard_completed_at: string | null
        wizard_dismissed_at: string | null
        created_at: string
        updated_at: string
      },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/onboarding/progress`,
        method: 'GET',
      }),
      providesTags: (result, error, { org_id, serial }) => [
        { type: 'onboarding-progress' as const, id: `${org_id}-${serial}` },
      ],
    }),

    updateOnboardingProgress: build.mutation<
      {
        id: string
        config_id: string
        org_id: string
        serial: string
        current_step: string
        wizard_completed_at: string | null
        wizard_dismissed_at: string | null
      },
      {
        org_id: string
        serial: string
        data: {
          current_step?: string
          step_completed?: string
          step_uncompleted?: string
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/onboarding/progress`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { org_id, serial }) => [
        { type: 'onboarding-progress' as const, id: `${org_id}-${serial}` },
      ],
    }),

    completeOnboardingWizard: build.mutation<
      {
        progress: { wizard_completed_at: string }
        activate_booking_page: boolean
      },
      {
        org_id: string
        serial: string
        data: { activate_booking_page?: boolean }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/onboarding/complete`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { org_id, serial }) => [
        { type: 'onboarding-progress' as const, id: `${org_id}-${serial}` },
      ],
    }),

    dismissOnboardingWizard: build.mutation<
      { wizard_dismissed_at: string },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/onboarding/dismiss`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { org_id, serial }) => [
        { type: 'onboarding-progress' as const, id: `${org_id}-${serial}` },
      ],
    }),

    validateOnboardingSteps: build.query<
      {
        steps: Record<string, { valid: boolean; errors: string[] }>
        can_go_live: boolean
        required_incomplete: string[]
      },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/onboarding/validate`,
        method: 'POST',
      }),
      providesTags: (result, error, { org_id, serial }) => [
        {
          type: 'onboarding-progress' as const,
          id: `validate-${org_id}-${serial}`,
        },
      ],
    }),

    getUserOnboardingProgress: build.query<
      {
        id: string
        oauth_user_id: string
        config_id: string
        org_id: string
        serial: string
        confirmed_sections: string[] | null
        wizard_started_at: string | null
        wizard_completed_at: string | null
        current_step: string | null
      },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/onboarding/user-progress`,
        method: 'GET',
      }),
      providesTags: (result, error, { org_id, serial }) => [
        {
          type: 'user-onboarding-progress' as const,
          id: `${org_id}-${serial}`,
        },
      ],
    }),

    updateUserOnboardingProgress: build.mutation<
      {
        id: string
        oauth_user_id: string
        config_id: string
        org_id: string
        serial: string
        confirmed_sections: string[] | null
        wizard_started_at: string | null
        wizard_completed_at: string | null
        current_step: string | null
      },
      {
        org_id: string
        serial: string
        data: {
          confirmed_sections?: string[]
          current_step?: string
          wizard_completed?: boolean
        }
      }
    >({
      query: ({ org_id, serial, data }) => ({
        url: `/${org_id}/${serial}/onboarding/user-progress`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { org_id, serial }) => [
        {
          type: 'user-onboarding-progress' as const,
          id: `${org_id}-${serial}`,
        },
        {
          type: 'user-onboarding-progress' as const,
          id: `all-${org_id}-${serial}`,
        },
      ],
    }),

    getAllUserOnboardingProgress: build.query<
      {
        id: string
        oauth_user_id: string
        config_id: string
        org_id: string
        serial: string
        confirmed_sections: string[] | null
        wizard_started_at: string | null
        wizard_completed_at: string | null
        current_step: string | null
        oauth_user: {
          oauth_user_name: string | null
          oauth_email: string | null
        }
      }[],
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/onboarding/user-progress/all`,
        method: 'GET',
      }),
      providesTags: (result, error, { org_id, serial }) => [
        {
          type: 'user-onboarding-progress' as const,
          id: `all-${org_id}-${serial}`,
        },
      ],
    }),

    resetOnboardingWizard: build.mutation<
      {
        id: string
        config_id: string
        org_id: string
        serial: string
        current_step: string
        wizard_completed_at: null
        wizard_dismissed_at: null
      },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/${org_id}/${serial}/onboarding/reset`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { org_id, serial }) => [
        { type: 'onboarding-progress' as const, id: `${org_id}-${serial}` },
      ],
    }),

    /*
     * Wallet card styles. Org-scoped, so no serial in the URL: a group styles its
     * cards once and applies them across sites, the same way brandkits behave.
     */
    getWalletBrandings: build.query<
      WalletBrandingListResponse,
      { org_id: string }
    >({
      query: ({ org_id }) => ({
        url: `/${org_id}/wallet-branding`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: 'wallet-branding' as const,
                id,
              })),
              { type: 'wallet-branding', id: 'LIST' },
            ]
          : [{ type: 'wallet-branding', id: 'LIST' }],
    }),

    getWalletBranding: build.query<
      WalletBrandingResponse,
      { org_id: string; id: string }
    >({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/wallet-branding/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: 'wallet-branding', id },
      ],
    }),

    createWalletBranding: build.mutation<
      WalletBrandingResponse,
      { org_id: string; data: CreateWalletBrandingPayload }
    >({
      query: ({ org_id, data }) => ({
        url: `/${org_id}/wallet-branding`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'wallet-branding', id: 'LIST' }],
    }),

    updateWalletBranding: build.mutation<
      WalletBrandingResponse,
      { org_id: string; id: string; data: UpdateWalletBrandingPayload }
    >({
      query: ({ org_id, id, data }) => ({
        url: `/${org_id}/wallet-branding/${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'wallet-branding', id },
        { type: 'wallet-branding', id: 'LIST' },
      ],
    }),

    /*
     * 204, no body. The delete is soft and it detaches every config, booking type,
     * event and special date pointing at the style, so a cached config still carrying
     * the now-detached `wallet_branding_id` is stale. Pass the serial from a
     * venue-scoped page to have its booking config refetched too.
     */
    deleteWalletBranding: build.mutation<
      void,
      { org_id: string; id: string; serial?: string }
    >({
      query: ({ org_id, id }) => ({
        url: `/${org_id}/wallet-branding/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id, serial }) => [
        { type: 'wallet-branding', id },
        { type: 'wallet-branding', id: 'LIST' },
        ...(serial ? [{ type: 'booking-config' as const, id: serial }] : []),
      ],
    }),

    /*
     * Wallet pass adoption for one venue. Read-only reporting over rows nothing in
     * Connect writes: a pass is issued by the guest's booking confirmation and marked
     * added by Apple's device registration or Google's save callback, so there is no
     * mutation here to invalidate against and deliberately no tag. Refetching is the
     * caller's business, through the date range it asks for.
     *
     * `from` / `to` are validated server-side with `z.string().datetime()`, so they
     * must be full ISO instants, not `yyyy-MM-dd`. Omitting both takes the server's
     * last-30-days window.
     */
    getWalletStats: build.query<
      WalletPassStatsResponse,
      { org_id: string; serial: string; from?: string; to?: string }
    >({
      query: ({ org_id, serial, from, to }) => ({
        url: `/${org_id}/${serial}/wallet/stats`,
        method: 'GET',
        params: {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        },
      }),
    }),

    /*
     * The bookings at one venue that have a pass, and what each wallet did with it.
     *
     * A row is a pass issued on at least one platform; whether it reached a device is
     * `added`, a server-side filter. It has to be server-side: filtering a page of 25
     * in the browser would silently under-fill every page. Omitting it returns both,
     * which is how the venue sees the gap between tapping the button and keeping the
     * pass. The row itself carries `added_at` per platform and no `downloaded`
     * boolean, so the caller decides what to call it.
     */
    getWalletPasses: build.query<
      WalletPassListResponse,
      {
        org_id: string
        serial: string
        page?: number
        limit?: number
        provider?: WalletPassProvider
        /** On a device now: added and not since deleted. Omit for both. */
        added?: boolean
        from?: string
        to?: string
      }
    >({
      query: ({
        org_id,
        serial,
        page = 1,
        limit = 25,
        provider,
        added,
        from,
        to,
      }) => ({
        url: `/${org_id}/${serial}/wallet/passes`,
        method: 'GET',
        params: {
          page,
          limit,
          ...(provider ? { provider } : {}),
          // Sent as the string the server's `z.enum(['true','false'])` expects rather
          // than trusting a serialiser to render a boolean the same way.
          ...(added === undefined ? {} : { added: added ? 'true' : 'false' }),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        },
      }),
    }),
  }),
})

// Export booking upgrades hooks
export const {
  useGetBookingUpgradesQuery,
  useGetBookingUpgradeQuery,
  useCreateBookingUpgradeMutation,
  useUpdateBookingUpgradeMutation,
  useDeleteBookingUpgradeMutation,
  useGetSquareCatalogItemsQuery,
  useGetDepositsQuery,
  // Social bookings: prices
  usePricePreviewMutation,
  useSetPriceOverrideMutation,
  useClearPriceOverrideMutation,
  // Refunds
  useCreateRefundMutation,
  useGetRefundsQuery,
  useGetChargesQuery,
  useGetBookingReportsQuery,
  useGetBookingsOverviewQuery,
  useListBookingAreasQuery,
  useCreateBookingAreaMutation,
  useUpdateBookingAreaMutation,
  useDeleteBookingAreaMutation,
  useReorderBookingAreasMutation,
  useListBookingTablesQuery,
  useCreateBookingTableMutation,
  useUpdateBookingTableMutation,
  useDeleteBookingTableMutation,
  useReorderBookingTablesMutation,
  useGetCampaignBookingReportQuery,
  useGetCampaignBookingsSummaryQuery,
  useStartNeoSyncMutation,
  usePushSquareDepositMutation,
  useGetNeoSyncStatusQuery,
  useGetModifiersQuery,
  useGetModifierCategoriesQuery,
  useFetchMenuItemQuery,
  useDeleteMenuMutation,
  useDeleteMenuItemMutation,
  useDeleteMenuCategoryMutation,
  // ordering.stampede.ai table ordering
  useGetMenuTablesQuery,
  useCreateMenuTableMutation,
  useUpdateMenuTableMutation,
  useDeleteMenuTableMutation,
  useGetNeoReportingQuery,
  useGetEnquiryOrdersQuery,
  useCreateEnquiryOrderMutation,
  useGetEnquiryOrderQuery,
  useUpdateEnquiryOrderMutation,
  useDeleteEnquiryOrderMutation,
  useCancelEnquiryOrderMutation,
  useReactivateEnquiryOrderMutation,
  useCompleteEnquiryOrderMutation,
  useReopenEnquiryOrderMutation,
  useSubmitEnquiryOrderMutation,
  useGetEnquiryOrderParticipantsQuery,
  useCreateEnquiryOrderParticipantMutation,
  useUpdateEnquiryOrderParticipantMutation,
  useDeleteEnquiryOrderParticipantMutation,
  useGetEnquiryOrderItemsQuery,
  useCreateEnquiryOrderItemMutation,
  useUpdateEnquiryOrderItemMutation,
  useDeleteEnquiryOrderItemMutation,
  useSendEnquiryPreorderEmailMutation,
  useSendParticipantPreorderEmailMutation,
  useSendAllPreorderEmailsMutation,
  useGetPreOrderEligibilityQuery,
  useSearchUserProfileWithPreferencesQuery,
  useGetBookingEligibleMenusQuery,
  useGetBookingOrderQuery,
  useGetBookingOrdersQuery,
  useCreateBookingOrderMutation,
  useCancelBookingOrderMutation,
  useReactivateBookingOrderMutation,
  useCompleteBookingOrderMutation,
  useReopenBookingOrderMutation,
  useCreateBookingOrderItemMutation,
  useDeleteBookingOrderItemMutation,
  useCreateBookingOrderParticipantMutation,
  useDeleteBookingOrderParticipantMutation,
  useSendBookingParticipantPreorderEmailMutation,
  useSendAllBookingPreorderEmailsMutation,
  useGetQueueStatsQuery,
  useGetOnboardingProgressQuery,
  useUpdateOnboardingProgressMutation,
  useCompleteOnboardingWizardMutation,
  useDismissOnboardingWizardMutation,
  useValidateOnboardingStepsQuery,
  useResetOnboardingWizardMutation,
  useGetUserOnboardingProgressQuery,
  useUpdateUserOnboardingProgressMutation,
  useGetAllUserOnboardingProgressQuery,
  // Waitlist (Feature A)
  useGetWaitlistQuery,
  useGetWaitlistEntryQuery,
  useGetWaitlistSuggestionQuery,
  useGetWaitlistReportQuery,
  useGetWaitlistSeatOptionsQuery,
  useGetWaitlistChatQuery,
  useSendWaitlistChatMessageMutation,
  useEndWaitlistChatMutation,
  useReopenWaitlistChatMutation,
  useAddWaitlistEntryMutation,
  useUpdateWaitlistEntryMutation,
  useNotifyWaitlistEntryMutation,
  useSeatWaitlistEntryMutation,
  useRemoveWaitlistEntryMutation,
  useRestoreWaitlistEntryMutation,
  useNoShowWaitlistEntryMutation,
  useReorderWaitlistMutation,
  // Place cards
  useGetPlacecardOrdersQuery,
  useGetPlacecardPresetsQuery,
  useCreatePlacecardPresetMutation,
  useUpdatePlacecardPresetMutation,
  useDeletePlacecardPresetMutation,
  // Wallet card styles
  useGetWalletBrandingsQuery,
  useGetWalletBrandingQuery,
  useCreateWalletBrandingMutation,
  useUpdateWalletBrandingMutation,
  useDeleteWalletBrandingMutation,
  // Wallet pass adoption
  useGetWalletStatsQuery,
  useGetWalletPassesQuery,
  useStartOptimisationReplayMutation,
  useGetOptimisationReplayQuery,
  // Voice AI reporting
  useGetVoiceReportQuery,
  useGetVoiceBookingsQuery,
} = zionApi

export default zionApi
